// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ============================================
   LANDING PAGE — Apothecary RPG
   Dark editorial + pharmaceutical document motifs
   ============================================ */

/* ---- Layout ---- */
.landing {
  min-height: 100vh;
  background: var(--bg-base);
  color: var(--text-primary);
  font-family: var(--font-display);
  position: relative;
  overflow-x: hidden;

  /* ---- The three axes ----
     Debono's colour is semantic, never decorative. These three are the whole
     vocabulary of the page background, and each one names a WAY OF KNOWING:

       understanding — many things held at once, and how they interact
       knowledge     — plain fact recall, memorised and retrieved
       intuition     — what can be known without having been learned

     Values are the logo-derived pastels (mint / sky / blush) kept as raw RGB
     triples so the same hue can be spent at any alpha. Declared HERE, on the
     page root, because a custom property has to sit on an ancestor of
     everything that reads it — the clouds, the cards and the tags all do.

     TO ADD AN AXIS: define --axis-<name> here, then put data-axis="<name>" on
     the thing. A cloud for it appears, its cards colour themselves, and the
     scroll lighting picks it up — there is no list of axes in the JS to update
     and no gradient to add. An axis with no token declared falls back to the
     neutral below rather than painting nothing, so a typo degrades quietly
     instead of leaving an invisible layer nobody can explain. */
  --axis-understanding: 138 230 180;
  --axis-knowledge: 150 194 246;
  --axis-intuition: 242 150 150;
  --axis-fallback: 148 148 156;
}

/* Grain + scan overlay — gives the page film/print character */
.landing::before {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    radial-gradient(circle at 1px 1px, rgba(255,255,255,0.025) 1px, transparent 0);
  background-size: 3px 3px;
  opacity: 0.5;
  mix-blend-mode: overlay;
}

/* ---- The background: dark ground, three pastel clouds ----

   REPLACES the full-bleed pastel wash that used to live here. That version laid
   a five-stop linear gradient over the base at alpha 0.52-0.56, which is enough
   to bury a #0a0a0a ground completely — the page read as lavender, not as a
   dark-only product, and the pastels were spent evenly on every section whether
   or not they meant anything there.

   Now the ground is the near-black base and the pastels survive only as three
   soft CLOUDS, one per axis. Each cloud is a large, very soft radial that never
   exceeds ~0.13 alpha at its core, so it tints the dark rather than replacing
   it. Their weights are driven by what you are actually reading: a section
   about understanding lights the green cloud and lets the other two fall back.
   Colour therefore states which way of knowing is on screen. */
.landing-bg {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  background-color: var(--bg-base);
}

/* One cloud, painted once per axis the page uses. Landing.jsx renders one of
   these for each distinct data-axis value it finds and hands it two things: the
   colour (--axis) and where it sits (--cx / --cy). Nothing about the SET of
   clouds is written down here, so adding a mechanic on a new axis needs a
   colour token and no changes to this rule.

   Size is in vmax so the shape stays soft and off-frame at any window — an
   ellipse wider than the viewport has no visible edge, which is what keeps
   this reading as atmosphere rather than as a coloured blob. */
.landing-cloud {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(80vmax 62vmax at var(--cx, 50%) var(--cy, 50%),
    rgba(var(--axis) / calc(0.13 * var(--w, 0.62))) 0%,
    rgba(var(--axis) / calc(0.05 * var(--w, 0.62))) 39%,
    rgba(var(--axis) / 0) 69%);
  background-repeat: no-repeat;
  /* Weight is crossfaded rather than snapped, so scrolling past a boundary is
     a shift in the light and not a repaint. */
  transition: background-image 900ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* ---- Edge ----
   Two hairline passes over the clouds. The vignette pulls the corners back to
   true black so the page is darkest where it meets the browser chrome and the
   content sits in the lit middle; the scanline keeps a fine mechanical texture
   in the flat areas, which is what stops a soft gradient reading as mush. */
.landing-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.014) 0px,
      rgba(255, 255, 255, 0.014) 1px,
      transparent 1px,
      transparent 3px),
    radial-gradient(120% 88% at 50% 42%,
      transparent 42%,
      rgba(6, 6, 8, 0.55) 78%,
      rgba(6, 6, 8, 0.86) 100%);
}

@media (prefers-reduced-motion: reduce) {
  .landing-bg { transition: none; }
}

/* ---- Scroll animation ---- */
.landing-animate {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}

.landing-animate.landing-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ============================================
   HERO
   ============================================ */

.landing-hero {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  text-align: center;

  /* ---- Fit the first screen ----------------------------------------------
     The hero used to be a fixed-height column: every quantity in it answered
     to the viewport's WIDTH (vw clamps) or to nothing at all (the mark was a
     flat 396px), and none of them to its HEIGHT. So the column stood ~825px
     tall whatever screen it was on, and the program strip — the last thing a
     visitor needs to see before deciding anything — fell below the fold on
     every common laptop: measured 56px under at 1366x768, 67px at 1440x765,
     116px at 1536x730, 228px at 1280x585. It only cleared the fold from about
     864px of viewport height up.

     ONE ratio now governs the whole vertical rhythm. --hero-u is a LENGTH
     equal to (fit x 1px), which is the only shape CSS allows here: calc()
     cannot divide a length by a length to make a number, but it can multiply a
     length by one. So every flexible quantity below is written
     calc(<its authored px> * var(--hero-u)) and is EXACTLY its authored value
     whenever the fit is 1 — the change is a provable no-op on any screen that
     already worked.

     The fit is SOLVED, not tuned. The strip's bottom sits at N + F*fit, where

       N = what must not shrink -- the wordmark, the tagline, the sub, the
           divider, the strip itself, and a 48px floor under the button row
       F = what yields -- the mark (331px of it, margins included) and every
           vertical gap: 511px in total

     Requiring the strip to land at least 16px clear of the fold gives
     fit = (viewport - N - 16) / F, capped at 1 so a tall screen gets the
     design exactly as authored, and floored so an extreme window degrades to
     scrolling rather than to nothing readable.

     🔴 EVERY TERM OF N IS A FUNCTION OF WIDTH, and it has to be. The first cut
     of this expressed only the wordmark that way and lumped the rest into one
     247px constant calibrated near 1366. That was measured across 17 widths and
     it does not hold: the sub alone swings 92 -> 110px, because its font-size is
     a vw clamp and its measure is a DIFFERENT vw clamp, so the line count is a
     step function of the ratio between them -- and not even a monotonic one
     (3.54 line-boxes at a 440px measure, 3.36 at 480, back to 3.54 at 504-564,
     then 2.36 at 576+). The consequence was not academic: the 16px clearance
     collapsed to 3.5px between 1680 and 1880 wide, and the strip went fully
     UNDER the fold at wide-and-short windows (1680x581, 1728x584, 1880x560,
     2048x569, 2304x571, 3440x571).

     So each term below converts its own AUTHORED source into a height, and
     tracks width exactly as that source does. Where a term cannot be derived it
     is the measured worst case over the whole width range, which is safe in one
     direction only: over-reserving costs a little mark size, under-reserving
     costs the requirement. Verified conservative at every width sampled, with
     the tightest margin +0.9px at 1680-1880 (where it used to be -12.6px).

     Every term is in REM, not px, and that matters: all of it measures TEXT,
     and text grows with the reader's default font size. A px reserve would
     under-count the very thing it is reserving for on a browser set to 20px,
     which is a fold failure and not a cosmetic one.

     (The flexible side stays in px by construction -- --hero-u is a length, so
     the multipliers below it have to be plain numbers. That direction is safe:
     the gaps come out tighter than authored under a large root font rather than
     looser, so the strip only ever moves UP.) */
  --hero-vh: 100vh;

  /* .landing-hero-script's own width clamp x 95.4/550.1, the SVG's aspect. */
  --hero-n-wordmark: clamp(3.87rem, 6.625vw, 8.65rem);
  /* 1.5 line-boxes of .landing-hero-tagline's font clamp (measured 18 -> 22.8px
     at a 16px root). */
  --hero-n-tagline: clamp(1.125rem, 1.425vw, 1.425rem);
  /* 3.54 line-boxes (the worst of the step function above) x 1.55 line-height
     x .landing-hero-sub's font clamp = 92.2 -> 109.7px at a 16px root.
     ⚠️ The 3.54 is a MEASUREMENT of the current copy, not a derivation --
     lengthen that sentence and this must be re-measured. */
  --hero-n-sub: clamp(5.7625rem, 6.859vw, 6.85625rem);
  /* The genuinely width-invariant remainder, each one measured: divider 18 +
     program strip 46 + button-row floor 48 + 16 clear of the fold = 128px. */
  --hero-n-rest: 8rem;

  --hero-fixed: calc(var(--hero-n-wordmark) + var(--hero-n-tagline)
                   + var(--hero-n-sub) + var(--hero-n-rest));
  --hero-flex: 511;
  /* Floor 0.3, not 0.45: at the floor the fit stops answering the viewport, so
     the floor IS the height below which the requirement fails. 0.45 put that
     boundary at ~600px of viewport height on a wide window, which is why the
     wide-and-short failures above existed. 0.3 moves it to ~552px at 3440 wide.
     Nothing textual shrinks on the way down -- only the mark and the gaps -- so
     a low floor degrades gracefully rather than illegibly. */
  --hero-u: clamp(0.3px, calc((var(--hero-vh) - var(--hero-fixed)) / var(--hero-flex)), 1px);

  /* min() rather than a bare calc so the authored vw ramp still governs a tall
     screen: the scaled term can only ever be the smaller of the two. */
  padding: min(clamp(1.75rem, 4vw, 3.5rem), calc(56 * var(--hero-u)))
           var(--space-6)
           calc(48 * var(--hero-u));
  min-height: min(clamp(640px, 80vh, 880px), var(--hero-vh));
}

/* svh, not dvh: the question is what fits WITHOUT scrolling, and the small
   viewport is the one you get on load. dvh would also re-fit the hero live
   while a mobile URL bar retracts, which is a resize nobody asked for.
   Guarded rather than declared twice, because a custom property holding an
   unsupported unit does NOT fall back to an earlier declaration — it parses
   fine and then poisons every property that reads it. */
@supports (height: 100svh) {
  .landing-hero { --hero-vh: 100svh; }
}

/* The button row is flex-wrap: wrap with three items in the logged-out state,
   and N counts it as ONE row. Once .landing-hero-inner is narrower than the
   row's ~517px it becomes two, and the unbudgeted second row plus its 12px gap
   put the strip back under the fold — measured +24.4px at 500x800, +19.4 at
   480x800, +20.3 at 480x700, +46.5 at 390x664.

   So N gains a row here. The breakpoint is 600px rather than the ~567px where
   the wrap actually begins, because the row's width depends on its own text and
   font: erring wide over-reserves 60px on a handful of narrow windows, erring
   narrow loses the requirement outright. */
@media (max-width: 600px) {
  .landing-hero { --hero-n-rest: 188px; }  /* 128 + a second 48px row + its 12px gap */
}

.landing-hero-inner {
  max-width: clamp(620px, 44vw, 980px);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

/* Decorative RX cross watermark behind hero */
.landing-hero-watermark {
  position: absolute;
  top: 48%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: clamp(520px, 56vw, 880px);
  height: clamp(520px, 56vw, 880px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.05;
  color: var(--accent-primary);
}

.landing-hero-inner > *:not(.landing-hero-watermark) {
  position: relative;
  z-index: 1;
}

.landing-hero-watermark svg {
  width: 100%;
  height: 100%;
  display: block;
  animation: landing-rotate 120s linear infinite;
}

@keyframes landing-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Hero corner stamps — pharmaceutical document feel */
.landing-hero-stamp {
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-subtle);
  line-height: 1;
}

.landing-hero-stamp-left { left: 0; }
.landing-hero-stamp-right { right: 0; }

.landing-hero-stamp-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34,197,94,0.6);
  animation: landing-pulse 2.4s ease-in-out infinite;
}

@keyframes landing-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.85); }
}

.landing-hero-stamp-bar {
  width: 28px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--text-disabled));
}

.landing-hero-stamp-right .landing-hero-stamp-bar {
  background: linear-gradient(270deg, transparent, var(--text-disabled));
}

/* ---- Program strip (inline in hero, above title) ---- */
.landing-program-strip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  /* Below the fit target, so this gap does not decide whether the strip is
     visible — it scales anyway, because a shrunken column with full-size
     trailing gaps reads as a mistake. */
  margin-bottom: calc(32 * var(--hero-u));  /* --space-8 at fit 1 */
  padding: 0.35rem;
  background: rgba(255,255,255,0.025);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-full);
  backdrop-filter: blur(12px);
}

/* Rendered as a <button> — the strip is a real picker (it reframes the demo
   headline below), so the element resets browser chrome back to the pill. */
.landing-program-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.95rem;
  border-radius: var(--radius-full);
  font-family: var(--font-display);
  font-size: 0.72rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  line-height: 1;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.landing-program-tag-active {
  background: #c265f5; /* vivid orchid — first stop of the button sequence */
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 0 22px rgba(194,101,245,0.5), inset 0 1px 0 rgba(255,255,255,0.3);
}

.landing-program-tag-active::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ffffff;
  margin-right: 0.1rem;
}

.landing-program-tag-idle {
  background: transparent;
  border-color: transparent;
  color: var(--text-subtle);
  opacity: 0.75;
}

.landing-program-tag-idle:hover,
.landing-program-tag-idle:focus-visible {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  opacity: 1;
}

/* On the picked pill the badge sits on orchid, so it needs its own contrast —
   the disabled grey it uses when idle disappears into the fill. */
.landing-program-tag-active .landing-program-tag-soon {
  color: rgba(255, 255, 255, 0.92);
  border-color: rgba(255, 255, 255, 0.45);
  opacity: 1;
}

.landing-program-tag-soon {
  font-size: 0.55rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-disabled);
  opacity: 0.8;
  padding: 0.15rem 0.35rem;
  border: 1px solid var(--border-subtle);
  border-radius: 3px;
}

/* ============================================
   HERO INTRO SEQUENCE
   One timeline, two movements. The logo is drawn stroke by stroke and the colour
   floods in behind the nib; then the script is written, the prescription symbol
   first and the wordmark after it, left to right in a single pass.

     0.00s  silhouette, with colour sweeping in behind the nib
     0.58s  hole
     0.90s  the three limbs, staggered
     1.35s  colour settles
     1.65s  the script is written
     2.65s  done
   ============================================ */
.landing-hero-logo {
  display: block;
  /* The single largest item in the hero, and the one that used to ignore the
     viewport completely: 347px of a ~825px column. Height stays \`auto\`, so the
     SVG's own 396x347 intrinsic ratio carries it — scaling the width is enough,
     and the stroke-draw above scales with the viewBox for free. */
  width: calc(396 * var(--hero-u));
  height: auto;
  margin: calc(-36 * var(--hero-u)) auto calc(20 * var(--hero-u));
  isolation: isolate;
}

/* The mark draws itself. \`pathLength="1"\` normalises every path so one keyframe
   serves all five and the timings below stay readable as seconds. */
.landing-hero-logo .dbl-line,
.landing-hero-logo .dbl-sweep {
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: landing-logo-draw var(--dbl-dur) var(--dbl-ease) var(--dbl-delay) forwards;
}

/* A nib gets up to speed and settles; it does not start and stop instantly.
   Long strokes carry the acceleration, short ones only need the landing. */
.landing-hero-logo .dbl-line,
.landing-hero-logo .dbl-sweep { --dbl-ease: cubic-bezier(0.37, 0.01, 0.25, 1); }
.landing-hero-logo .dbl-seg-3,
.landing-hero-logo .dbl-seg-4,
.landing-hero-logo .dbl-seg-5 { --dbl-ease: cubic-bezier(0.25, 0.6, 0.3, 1); }

.landing-hero-logo .dbl-seg-1 { --dbl-delay: 0s;    --dbl-dur: 0.95s; }
.landing-hero-logo .dbl-seg-2 { --dbl-delay: 0.62s; --dbl-dur: 0.40s; }
/* Staggered by less than half their own length so the three limbs overlap heavily
   and read as one continuous passage rather than three separate events. */
.landing-hero-logo .dbl-seg-3 { --dbl-delay: 0.86s; --dbl-dur: 0.42s; }
.landing-hero-logo .dbl-seg-4 { --dbl-delay: 1.00s; --dbl-dur: 0.42s; }
.landing-hero-logo .dbl-seg-5 { --dbl-delay: 1.14s; --dbl-dur: 0.42s; }

/* Starts after the nib and runs longer, so colour trails the outline instead of
   racing ahead of it as it did when both started together. */
.landing-hero-logo .dbl-sweep { --dbl-delay: 0.12s; --dbl-dur: 1.25s; }

@keyframes landing-logo-draw {
  to { stroke-dashoffset: 0; }
}

/* Starts while the sweep still has a third to go. The sweep's tail is the corner it
   began on, which is also the corner the nib drew first, so without this overlap the
   outline sits finished for a beat with its top-left still uncoloured. Eased both
   ends so it arrives as a settle, not a fade-up. */
.landing-hero-logo .dbl-flood {
  opacity: 0;
  animation: landing-logo-flood 0.65s ease-in-out 0.95s forwards;
}

@keyframes landing-logo-flood {
  to { opacity: 1; }
}

/* ---- Hero text ---- */
.landing-hero-name {
  margin: 0;
  text-align: center;
  position: relative;
  display: block;
  /* The wordmark is now an SVG so the reveal can be stroke-driven. The glow stays
     here on the h1, outside the SVG's own mask, so nothing shears it. */
  filter: drop-shadow(0 0 70px rgba(191, 64, 255, 0.30));
}

/* 550.1 / 103.68 = 5.306, so this reproduces the box the live text used to make
   from font-size: clamp(4.2rem, 7.2vw, 9.4rem).
   ⚠️ --hero-n-wordmark in .landing-hero mirrors the width clamp below, scaled by
   95.4/550.1 to get this mark's height. Change one and re-derive the other. */
.landing-hero-script {
  display: block;
  width: clamp(22.29rem, 38.2vw, 49.88rem);
  height: auto;
  margin: 0 auto;
  overflow: visible;

  /* ---- DEBONO is centred on the page; the ℞ hangs off to its left ----
     \`margin: 0 auto\` centres the SVG BOX, and the box holds the WHOLE lockup —
     so what was centred was ℞ + DEBONO together, which left DEBONO itself right
     of the page axis and off the centreline of the logo above it.

     The offset is readable straight off the markup, which is why the correction
     is written as it is rather than as a tuned number: the wordmark's <text>
     starts at x=75.2 in a 550.1-wide viewBox that is fitted to the lockup's
     advance, so DEBONO's centre sits at (75.2 + 550.1)/2 = 312.65 where the
     box's is 275.05 — 37.6 units right, i.e. 37.6/550.1 = 6.835% of the mark's
     width, at every size.

     Confirmed live, not assumed: with the rule removed in-run the wordmark
     measured +35.62px off axis at 1366, +50.07 at 1920, +33.36 at 1280, +37.54
     at 1440 and +54.48 at 2560 — which is 6.8253% of the rendered width every
     time (spread 0.004%), agreeing with the figure above to 0.05px.

     Corrected with a TRANSLATE on the <svg>, NOT by moving the <text> inside
     it. That is not a preference: the wordmark is revealed by a stroke-driven
     mask whose pen routes (SCRIPT_STROKES in Landing.jsx) are authored in this
     same coordinate space, so moving the text would leave the mask tracing
     glyphs that are no longer there. Translating the root carries text, mask and
     nibs together and the reveal cannot come apart.

     Percent, because translateX(%) resolves against the element's OWN box, so
     one number holds at every width where a px offset would only be right at
     the size it was measured on. A transform does not reflow, so the hero's fit
     budget above is untouched (asserted: the program strip's deficit is
     identical with the rule on and off, at all five sizes).

     The ℞ keeps its distance from the D — that gap lives inside the SVG and
     rides along, so it stays proportional to the mark rather than fixed. */
  transform: translateX(-6.835%);
}

.landing-hero-script-word {
  font-family: var(--font-display);
  font-size: 103.68px;
  font-weight: var(--font-weight-black);
  letter-spacing: 2.0736px;
  font-feature-settings: 'ss01' 1, 'salt' 1;
}

.landing-hero-script-rx {
  font-family: var(--font-serif);
  font-size: 64.2816px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 2.0736px;
}


/* The script is written by drawing the pen route through each glyph. Same primitive
   as the logo: pathLength normalises every route to 1, so one keyframe serves all.

   --dbs-s is a speed multiplier on BOTH the offsets and the durations, so the whole
   passage stretches without the rhythm between glyphs changing. 1 is real time; raise
   it to watch the mechanism.

   The gap between the R and the D is a real gap between two draws, not a hold inside
   one sweep, which is why the pause reads. */
.landing-hero-script {
  --dbs-t0: 1.65s;
  --dbs-s: 1;
}

.landing-hero-script .dbs-ink,
.landing-hero-script .dbs-nib {
  animation-duration: calc(var(--dbs-dur) * var(--dbs-s));
  animation-delay: calc(var(--dbs-t0) + var(--dbs-off) * var(--dbs-s));
  animation-timing-function: cubic-bezier(0.4, 0.05, 0.3, 1);
  animation-fill-mode: forwards;
}

.landing-hero-script .dbs-ink {
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation-name: landing-script-draw;
}


@keyframes landing-script-draw {
  to { stroke-dashoffset: 0; }
}

/* Nib. Sampled positions along each pen route, replayed as plain transforms.
   This started as CSS offset-path, which is exact but is a recent Chromium
   feature; where it is unsupported every nib silently parks at the origin,
   off-canvas, and reads as "there is no nib". Sampling costs a few keyframes and
   works everywhere.
   Sized to the mask stroke (26) so the ink's leading edge and the nib's leading
   edge are the same edge: no ink appears that the nib has not reached. The nib
   fades out where a route jumps between strokes, which is the pen lifting. */
.landing-hero-script .dbs-nib {
  fill: #ffffff;
  opacity: 0;
  transform-box: view-box;
  filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.95))
          drop-shadow(0 0 16px rgba(191, 64, 255, 0.95));
}

.landing-hero-script .dbs-st-1 { --dbs-off: 0s; --dbs-dur: 0.0683s; }
.landing-hero-script .dbs-st-2 { --dbs-off: 0.0832s; --dbs-dur: 0.0881s; }
.landing-hero-script .dbs-st-3 { --dbs-off: 0.1863s; --dbs-dur: 0.0347s; }
.landing-hero-script .dbs-st-4 { --dbs-off: 0.2359s; --dbs-dur: 0.0441s; }
.landing-hero-script .dbs-st-5 { --dbs-off: 0.48s; --dbs-dur: 0.066s; }
.landing-hero-script .dbs-st-6 { --dbs-off: 0.5812s; --dbs-dur: 0.1188s; }
.landing-hero-script .dbs-st-7 { --dbs-off: 0.68s; --dbs-dur: 0.1158s; }
.landing-hero-script .dbs-st-8 { --dbs-off: 0.8246s; --dbs-dur: 0.0354s; }
.landing-hero-script .dbs-st-9 { --dbs-off: 0.84s; --dbs-dur: 0.0474s; }
.landing-hero-script .dbs-st-10 { --dbs-off: 0.9034s; --dbs-dur: 0.0585s; }
.landing-hero-script .dbs-st-11 { --dbs-off: 0.9779s; --dbs-dur: 0.0621s; }
.landing-hero-script .dbs-st-12 { --dbs-off: 1.02s; --dbs-dur: 0.22s; }
.landing-hero-script .dbs-st-13 { --dbs-off: 1.22s; --dbs-dur: 0.2s; }
.landing-hero-script .dbs-st-14 { --dbs-off: 1.4s; --dbs-dur: 0.22s; }

@keyframes landing-script-nib-1 {
  0% { transform: translate(8.5px, 6.2px) rotate(90deg); opacity: 0; }
  5% { transform: translate(8.5px, 8.64px) rotate(90deg); opacity: 1; }
  10% { transform: translate(8.5px, 11.08px) rotate(90deg); opacity: 1; }
  15% { transform: translate(8.5px, 13.52px) rotate(90deg); opacity: 1; }
  20% { transform: translate(8.5px, 15.96px) rotate(90deg); opacity: 1; }
  25% { transform: translate(8.5px, 18.4px) rotate(90deg); opacity: 1; }
  30% { transform: translate(8.5px, 20.84px) rotate(90deg); opacity: 1; }
  35% { transform: translate(8.5px, 23.28px) rotate(90deg); opacity: 1; }
  40% { transform: translate(8.5px, 25.72px) rotate(90deg); opacity: 1; }
  45% { transform: translate(8.5px, 28.16px) rotate(90deg); opacity: 1; }
  50% { transform: translate(8.5px, 30.6px) rotate(90deg); opacity: 1; }
  55% { transform: translate(8.5px, 33.04px) rotate(90deg); opacity: 1; }
  60% { transform: translate(8.5px, 35.48px) rotate(90deg); opacity: 1; }
  65% { transform: translate(8.5px, 37.92px) rotate(90deg); opacity: 1; }
  70% { transform: translate(8.5px, 40.36px) rotate(90deg); opacity: 1; }
  75% { transform: translate(8.5px, 42.8px) rotate(90deg); opacity: 1; }
  80% { transform: translate(8.5px, 45.24px) rotate(90deg); opacity: 1; }
  85% { transform: translate(8.5px, 47.68px) rotate(90deg); opacity: 1; }
  90% { transform: translate(8.5px, 50.12px) rotate(90deg); opacity: 1; }
  95% { transform: translate(8.5px, 52.56px) rotate(90deg); opacity: 1; }
  100% { transform: translate(8.5px, 55px) rotate(90deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-1 { animation-name: landing-script-nib-1; }

@keyframes landing-script-nib-2 {
  0% { transform: translate(8.5px, 9.8px) rotate(1.7deg); opacity: 0; }
  5% { transform: translate(11.65px, 9.89px) rotate(1.7deg); opacity: 1; }
  10% { transform: translate(14.8px, 9.99px) rotate(1.7deg); opacity: 1; }
  15% { transform: translate(17.95px, 10.08px) rotate(1.7deg); opacity: 1; }
  20% { transform: translate(21.09px, 10.18px) rotate(3.2deg); opacity: 1; }
  25% { transform: translate(23.98px, 11.31px) rotate(27deg); opacity: 1; }
  30% { transform: translate(26.78px, 12.74px) rotate(27deg); opacity: 1; }
  35% { transform: translate(29.2px, 14.03px) rotate(50.8deg); opacity: 1; }
  40% { transform: translate(31.19px, 16.47px) rotate(61.8deg); opacity: 1; }
  45% { transform: translate(31.87px, 19.53px) rotate(78.9deg); opacity: 1; }
  50% { transform: translate(32.2px, 22.64px) rotate(90deg); opacity: 1; }
  55% { transform: translate(32.2px, 25.79px) rotate(90deg); opacity: 1; }
  60% { transform: translate(32.02px, 28.92px) rotate(102.5deg); opacity: 1; }
  65% { transform: translate(30.37px, 30.85px) rotate(177.7deg); opacity: 1; }
  70% { transform: translate(27.23px, 30.98px) rotate(176deg); opacity: 1; }
  75% { transform: translate(24.14px, 31.55px) rotate(167.8deg); opacity: 1; }
  80% { transform: translate(21.06px, 32.22px) rotate(167.8deg); opacity: 1; }
  85% { transform: translate(17.95px, 32.59px) rotate(-179.5deg); opacity: 1; }
  90% { transform: translate(14.8px, 32.56px) rotate(-179.5deg); opacity: 1; }
  95% { transform: translate(11.65px, 32.53px) rotate(-179.5deg); opacity: 1; }
  100% { transform: translate(8.5px, 32.5px) rotate(-179.5deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-2 { animation-name: landing-script-nib-2; }

@keyframes landing-script-nib-3 {
  0% { transform: translate(34.8px, 34.5px) rotate(55.7deg); opacity: 0; }
  5% { transform: translate(35.5px, 35.53px) rotate(55.7deg); opacity: 1; }
  10% { transform: translate(36.2px, 36.55px) rotate(55.7deg); opacity: 1; }
  15% { transform: translate(36.9px, 37.57px) rotate(55.7deg); opacity: 1; }
  20% { transform: translate(37.6px, 38.6px) rotate(55.7deg); opacity: 1; }
  25% { transform: translate(38.3px, 39.63px) rotate(55.7deg); opacity: 1; }
  30% { transform: translate(39px, 40.65px) rotate(55.7deg); opacity: 1; }
  35% { transform: translate(39.7px, 41.67px) rotate(55.7deg); opacity: 1; }
  40% { transform: translate(40.4px, 42.7px) rotate(55.7deg); opacity: 1; }
  45% { transform: translate(41.1px, 43.72px) rotate(55.7deg); opacity: 1; }
  50% { transform: translate(41.8px, 44.75px) rotate(55.7deg); opacity: 1; }
  55% { transform: translate(42.5px, 45.78px) rotate(55.7deg); opacity: 1; }
  60% { transform: translate(43.2px, 46.8px) rotate(55.7deg); opacity: 1; }
  65% { transform: translate(43.9px, 47.83px) rotate(55.7deg); opacity: 1; }
  70% { transform: translate(44.6px, 48.85px) rotate(55.7deg); opacity: 1; }
  75% { transform: translate(45.3px, 49.88px) rotate(55.7deg); opacity: 1; }
  80% { transform: translate(46px, 50.9px) rotate(55.7deg); opacity: 1; }
  85% { transform: translate(46.7px, 51.92px) rotate(55.7deg); opacity: 1; }
  90% { transform: translate(47.4px, 52.95px) rotate(55.7deg); opacity: 1; }
  95% { transform: translate(48.1px, 53.98px) rotate(55.7deg); opacity: 1; }
  100% { transform: translate(48.8px, 55px) rotate(55.7deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-3 { animation-name: landing-script-nib-3; }

@keyframes landing-script-nib-4 {
  0% { transform: translate(47.8px, 30.5px) rotate(128.9deg); opacity: 0; }
  5% { transform: translate(46.81px, 31.72px) rotate(128.9deg); opacity: 1; }
  10% { transform: translate(45.82px, 32.95px) rotate(128.9deg); opacity: 1; }
  15% { transform: translate(44.83px, 34.17px) rotate(128.9deg); opacity: 1; }
  20% { transform: translate(43.84px, 35.4px) rotate(128.9deg); opacity: 1; }
  25% { transform: translate(42.85px, 36.63px) rotate(128.9deg); opacity: 1; }
  30% { transform: translate(41.86px, 37.85px) rotate(128.9deg); opacity: 1; }
  35% { transform: translate(40.87px, 39.07px) rotate(128.9deg); opacity: 1; }
  40% { transform: translate(39.88px, 40.3px) rotate(128.9deg); opacity: 1; }
  45% { transform: translate(38.89px, 41.52px) rotate(128.9deg); opacity: 1; }
  50% { transform: translate(37.9px, 42.75px) rotate(128.9deg); opacity: 1; }
  55% { transform: translate(36.91px, 43.97px) rotate(128.9deg); opacity: 1; }
  60% { transform: translate(35.92px, 45.2px) rotate(128.9deg); opacity: 1; }
  65% { transform: translate(34.93px, 46.42px) rotate(128.9deg); opacity: 1; }
  70% { transform: translate(33.94px, 47.65px) rotate(128.9deg); opacity: 1; }
  75% { transform: translate(32.95px, 48.88px) rotate(128.9deg); opacity: 1; }
  80% { transform: translate(31.96px, 50.1px) rotate(128.9deg); opacity: 1; }
  85% { transform: translate(30.97px, 51.33px) rotate(128.9deg); opacity: 1; }
  90% { transform: translate(29.98px, 52.55px) rotate(128.9deg); opacity: 1; }
  95% { transform: translate(28.99px, 53.78px) rotate(128.9deg); opacity: 1; }
  100% { transform: translate(28px, 55px) rotate(128.9deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-4 { animation-name: landing-script-nib-4; }

@keyframes landing-script-nib-5 {
  0% { transform: translate(91.7px, 11.9px) rotate(90deg); opacity: 0; }
  5% { transform: translate(91.7px, 15.58px) rotate(90deg); opacity: 1; }
  10% { transform: translate(91.7px, 19.27px) rotate(90deg); opacity: 1; }
  15% { transform: translate(91.7px, 22.96px) rotate(90deg); opacity: 1; }
  20% { transform: translate(91.7px, 26.64px) rotate(90deg); opacity: 1; }
  25% { transform: translate(91.7px, 30.33px) rotate(90deg); opacity: 1; }
  30% { transform: translate(91.7px, 34.01px) rotate(90deg); opacity: 1; }
  35% { transform: translate(91.7px, 37.69px) rotate(90deg); opacity: 1; }
  40% { transform: translate(91.7px, 41.38px) rotate(90deg); opacity: 1; }
  45% { transform: translate(91.7px, 45.06px) rotate(90deg); opacity: 1; }
  50% { transform: translate(91.7px, 48.75px) rotate(90deg); opacity: 1; }
  55% { transform: translate(91.7px, 52.44px) rotate(90deg); opacity: 1; }
  60% { transform: translate(91.7px, 56.12px) rotate(90deg); opacity: 1; }
  65% { transform: translate(91.7px, 59.81px) rotate(90deg); opacity: 1; }
  70% { transform: translate(91.7px, 63.49px) rotate(90deg); opacity: 1; }
  75% { transform: translate(91.7px, 67.18px) rotate(90deg); opacity: 1; }
  80% { transform: translate(91.7px, 70.86px) rotate(90deg); opacity: 1; }
  85% { transform: translate(91.7px, 74.54px) rotate(90deg); opacity: 1; }
  90% { transform: translate(91.7px, 78.23px) rotate(90deg); opacity: 1; }
  95% { transform: translate(91.7px, 81.92px) rotate(90deg); opacity: 1; }
  100% { transform: translate(91.7px, 85.6px) rotate(90deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-5 { animation-name: landing-script-nib-5; }

@keyframes landing-script-nib-6 {
  0% { transform: translate(91.7px, 20.6px) rotate(0.7deg); opacity: 0; }
  5% { transform: translate(98.33px, 20.68px) rotate(0.7deg); opacity: 1; }
  10% { transform: translate(104.96px, 20.76px) rotate(0.7deg); opacity: 1; }
  15% { transform: translate(111.59px, 20.84px) rotate(0.7deg); opacity: 1; }
  20% { transform: translate(118.2px, 21.16px) rotate(11.3deg); opacity: 1; }
  25% { transform: translate(124.57px, 22.88px) rotate(22.2deg); opacity: 1; }
  30% { transform: translate(130.34px, 26.05px) rotate(36.1deg); opacity: 1; }
  35% { transform: translate(135.32px, 30.35px) rotate(51.8deg); opacity: 1; }
  40% { transform: translate(138.8px, 35.93px) rotate(67.7deg); opacity: 1; }
  45% { transform: translate(140.97px, 42.15px) rotate(82.9deg); opacity: 1; }
  50% { transform: translate(141.79px, 48.73px) rotate(89.4deg); opacity: 1; }
  55% { transform: translate(140.98px, 55.31px) rotate(97.2deg); opacity: 1; }
  60% { transform: translate(138.81px, 61.54px) rotate(112.3deg); opacity: 1; }
  65% { transform: translate(135.34px, 67.12px) rotate(128.2deg); opacity: 1; }
  70% { transform: translate(130.37px, 71.42px) rotate(143.9deg); opacity: 1; }
  75% { transform: translate(124.59px, 74.55px) rotate(158.7deg); opacity: 1; }
  80% { transform: translate(118.2px, 76.24px) rotate(168.7deg); opacity: 1; }
  85% { transform: translate(111.59px, 76.56px) rotate(179.3deg); opacity: 1; }
  90% { transform: translate(104.96px, 76.64px) rotate(179.3deg); opacity: 1; }
  95% { transform: translate(98.33px, 76.72px) rotate(179.3deg); opacity: 1; }
  100% { transform: translate(91.7px, 76.8px) rotate(179.3deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-6 { animation-name: landing-script-nib-6; }

@keyframes landing-script-nib-7 {
  0% { transform: translate(212.6px, 20.5px) rotate(-177.7deg); opacity: 0; }
  5% { transform: translate(205.7px, 20.22px) rotate(-177.7deg); opacity: 1; }
  10% { transform: translate(198.8px, 19.94px) rotate(-177.7deg); opacity: 1; }
  15% { transform: translate(191.89px, 19.65px) rotate(-177.7deg); opacity: 1; }
  20% { transform: translate(184.99px, 19.37px) rotate(-177.7deg); opacity: 1; }
  25% { transform: translate(178.09px, 19.09px) rotate(-177.7deg); opacity: 1; }
  30% { transform: translate(173.4px, 21.12px) rotate(90deg); opacity: 1; }
  35% { transform: translate(173.4px, 28.02px) rotate(90deg); opacity: 1; }
  40% { transform: translate(173.4px, 34.93px) rotate(90deg); opacity: 1; }
  45% { transform: translate(173.4px, 41.84px) rotate(90deg); opacity: 1; }
  50% { transform: translate(173.4px, 48.75px) rotate(90deg); opacity: 1; }
  55% { transform: translate(173.4px, 55.66px) rotate(90deg); opacity: 1; }
  60% { transform: translate(173.4px, 62.56px) rotate(90deg); opacity: 1; }
  65% { transform: translate(173.4px, 69.47px) rotate(90deg); opacity: 1; }
  70% { transform: translate(173.4px, 76.38px) rotate(90deg); opacity: 1; }
  75% { transform: translate(178.08px, 78.42px) rotate(-2.2deg); opacity: 1; }
  80% { transform: translate(184.99px, 78.16px) rotate(-2.2deg); opacity: 1; }
  85% { transform: translate(191.89px, 77.89px) rotate(-2.2deg); opacity: 1; }
  90% { transform: translate(198.79px, 77.63px) rotate(-2.2deg); opacity: 1; }
  95% { transform: translate(205.7px, 77.36px) rotate(-2.2deg); opacity: 1; }
  100% { transform: translate(212.6px, 77.1px) rotate(-2.2deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-7 { animation-name: landing-script-nib-7; }

@keyframes landing-script-nib-8 {
  0% { transform: translate(173.4px, 48.1px) rotate(0deg); opacity: 0; }
  5% { transform: translate(175.51px, 48.1px) rotate(0deg); opacity: 1; }
  10% { transform: translate(177.62px, 48.1px) rotate(0deg); opacity: 1; }
  15% { transform: translate(179.73px, 48.1px) rotate(0deg); opacity: 1; }
  20% { transform: translate(181.84px, 48.1px) rotate(0deg); opacity: 1; }
  25% { transform: translate(183.95px, 48.1px) rotate(0deg); opacity: 1; }
  30% { transform: translate(186.06px, 48.1px) rotate(0deg); opacity: 1; }
  35% { transform: translate(188.17px, 48.1px) rotate(0deg); opacity: 1; }
  40% { transform: translate(190.28px, 48.1px) rotate(0deg); opacity: 1; }
  45% { transform: translate(192.39px, 48.1px) rotate(0deg); opacity: 1; }
  50% { transform: translate(194.5px, 48.1px) rotate(0deg); opacity: 1; }
  55% { transform: translate(196.61px, 48.1px) rotate(0deg); opacity: 1; }
  60% { transform: translate(198.72px, 48.1px) rotate(0deg); opacity: 1; }
  65% { transform: translate(200.83px, 48.1px) rotate(0deg); opacity: 1; }
  70% { transform: translate(202.94px, 48.1px) rotate(0deg); opacity: 1; }
  75% { transform: translate(205.05px, 48.1px) rotate(0deg); opacity: 1; }
  80% { transform: translate(207.16px, 48.1px) rotate(0deg); opacity: 1; }
  85% { transform: translate(209.27px, 48.1px) rotate(0deg); opacity: 1; }
  90% { transform: translate(211.38px, 48.1px) rotate(0deg); opacity: 1; }
  95% { transform: translate(213.49px, 48.1px) rotate(0deg); opacity: 1; }
  100% { transform: translate(215.6px, 48.1px) rotate(0deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-8 { animation-name: landing-script-nib-8; }

@keyframes landing-script-nib-9 {
  0% { transform: translate(241.5px, 85.6px) rotate(-90deg); opacity: 0; }
  5% { transform: translate(241.5px, 81.92px) rotate(-90deg); opacity: 1; }
  10% { transform: translate(241.5px, 78.23px) rotate(-90deg); opacity: 1; }
  15% { transform: translate(241.5px, 74.54px) rotate(-90deg); opacity: 1; }
  20% { transform: translate(241.5px, 70.86px) rotate(-90deg); opacity: 1; }
  25% { transform: translate(241.5px, 67.17px) rotate(-90deg); opacity: 1; }
  30% { transform: translate(241.5px, 63.49px) rotate(-90deg); opacity: 1; }
  35% { transform: translate(241.5px, 59.81px) rotate(-90deg); opacity: 1; }
  40% { transform: translate(241.5px, 56.12px) rotate(-90deg); opacity: 1; }
  45% { transform: translate(241.5px, 52.43px) rotate(-90deg); opacity: 1; }
  50% { transform: translate(241.5px, 48.75px) rotate(-90deg); opacity: 1; }
  55% { transform: translate(241.5px, 45.06px) rotate(-90deg); opacity: 1; }
  60% { transform: translate(241.5px, 41.38px) rotate(-90deg); opacity: 1; }
  65% { transform: translate(241.5px, 37.69px) rotate(-90deg); opacity: 1; }
  70% { transform: translate(241.5px, 34.01px) rotate(-90deg); opacity: 1; }
  75% { transform: translate(241.5px, 30.32px) rotate(-90deg); opacity: 1; }
  80% { transform: translate(241.5px, 26.64px) rotate(-90deg); opacity: 1; }
  85% { transform: translate(241.5px, 22.95px) rotate(-90deg); opacity: 1; }
  90% { transform: translate(241.5px, 19.27px) rotate(-90deg); opacity: 1; }
  95% { transform: translate(241.5px, 15.58px) rotate(-90deg); opacity: 1; }
  100% { transform: translate(241.5px, 11.9px) rotate(-90deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-9 { animation-name: landing-script-nib-9; }

@keyframes landing-script-nib-10 {
  0% { transform: translate(241.5px, 19.3px) rotate(0deg); opacity: 0; }
  5% { transform: translate(246.05px, 19.3px) rotate(0deg); opacity: 1; }
  10% { transform: translate(250.6px, 19.3px) rotate(0deg); opacity: 1; }
  15% { transform: translate(255.15px, 19.3px) rotate(0deg); opacity: 1; }
  20% { transform: translate(259.7px, 19.3px) rotate(0deg); opacity: 1; }
  25% { transform: translate(264.25px, 19.33px) rotate(5.9deg); opacity: 1; }
  30% { transform: translate(268.73px, 20.11px) rotate(10deg); opacity: 1; }
  35% { transform: translate(272.84px, 21.96px) rotate(34.4deg); opacity: 1; }
  40% { transform: translate(275.96px, 25.24px) rotate(58.2deg); opacity: 1; }
  45% { transform: translate(277.74px, 29.41px) rotate(77.4deg); opacity: 1; }
  50% { transform: translate(277.86px, 33.92px) rotate(97.6deg); opacity: 1; }
  55% { transform: translate(276.57px, 38.27px) rotate(109.7deg); opacity: 1; }
  60% { transform: translate(275.03px, 42.55px) rotate(110.3deg); opacity: 1; }
  65% { transform: translate(272.92px, 46.56px) rotate(133.8deg); opacity: 1; }
  70% { transform: translate(268.76px, 48.19px) rotate(167.5deg); opacity: 1; }
  75% { transform: translate(264.25px, 48.59px) rotate(-179.8deg); opacity: 1; }
  80% { transform: translate(259.7px, 48.57px) rotate(-179.8deg); opacity: 1; }
  85% { transform: translate(255.15px, 48.55px) rotate(-179.8deg); opacity: 1; }
  90% { transform: translate(250.6px, 48.54px) rotate(-179.8deg); opacity: 1; }
  95% { transform: translate(246.05px, 48.52px) rotate(-179.8deg); opacity: 1; }
  100% { transform: translate(241.5px, 48.5px) rotate(-179.8deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-10 { animation-name: landing-script-nib-10; }

@keyframes landing-script-nib-11 {
  0% { transform: translate(241.5px, 45.5px) rotate(0.8deg); opacity: 0; }
  5% { transform: translate(246.33px, 45.57px) rotate(0.8deg); opacity: 1; }
  10% { transform: translate(251.15px, 45.63px) rotate(0.8deg); opacity: 1; }
  15% { transform: translate(255.98px, 45.7px) rotate(0.8deg); opacity: 1; }
  20% { transform: translate(260.81px, 45.77px) rotate(0.8deg); opacity: 1; }
  25% { transform: translate(265.64px, 45.83px) rotate(0.8deg); opacity: 1; }
  30% { transform: translate(270.46px, 45.93px) rotate(15.3deg); opacity: 1; }
  35% { transform: translate(273.92px, 49.12px) rotate(53.9deg); opacity: 1; }
  40% { transform: translate(276.54px, 53.15px) rotate(63.1deg); opacity: 1; }
  45% { transform: translate(278.72px, 57.46px) rotate(63.1deg); opacity: 1; }
  50% { transform: translate(279.93px, 62.1px) rotate(78.9deg); opacity: 1; }
  55% { transform: translate(279.8px, 66.88px) rotate(104.6deg); opacity: 1; }
  60% { transform: translate(277.93px, 71.33px) rotate(122.1deg); opacity: 1; }
  65% { transform: translate(274.63px, 74.84px) rotate(143.1deg); opacity: 1; }
  70% { transform: translate(270.35px, 77.01px) rotate(166deg); opacity: 1; }
  75% { transform: translate(265.61px, 77.97px) rotate(168.5deg); opacity: 1; }
  80% { transform: translate(260.81px, 78.2px) rotate(180deg); opacity: 1; }
  85% { transform: translate(255.98px, 78.2px) rotate(180deg); opacity: 1; }
  90% { transform: translate(251.16px, 78.2px) rotate(180deg); opacity: 1; }
  95% { transform: translate(246.33px, 78.2px) rotate(180deg); opacity: 1; }
  100% { transform: translate(241.5px, 78.2px) rotate(180deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-11 { animation-name: landing-script-nib-11; }

@keyframes landing-script-nib-12 {
  0% { transform: translate(340.4px, 19.6px) rotate(11.2deg); opacity: 0; }
  5% { transform: translate(349.36px, 21.74px) rotate(21.8deg); opacity: 1; }
  10% { transform: translate(357.28px, 26.36px) rotate(41.6deg); opacity: 1; }
  15% { transform: translate(363.2px, 33.36px) rotate(64.8deg); opacity: 1; }
  20% { transform: translate(366.41px, 41.95px) rotate(79.8deg); opacity: 1; }
  25% { transform: translate(367.2px, 51.12px) rotate(95.6deg); opacity: 1; }
  30% { transform: translate(365.09px, 60.08px) rotate(111.8deg); opacity: 1; }
  35% { transform: translate(360.42px, 67.97px) rotate(132.5deg); opacity: 1; }
  40% { transform: translate(353.34px, 73.83px) rotate(145deg); opacity: 1; }
  45% { transform: translate(344.79px, 77.09px) rotate(168.2deg); opacity: 1; }
  50% { transform: translate(335.6px, 77.7px) rotate(-175.6deg); opacity: 1; }
  55% { transform: translate(326.62px, 75.65px) rotate(-158.2deg); opacity: 1; }
  60% { transform: translate(318.68px, 71.07px) rotate(-137.5deg); opacity: 1; }
  65% { transform: translate(312.83px, 63.98px) rotate(-125deg); opacity: 1; }
  70% { transform: translate(309.44px, 55.47px) rotate(-103.2deg); opacity: 1; }
  75% { transform: translate(308.8px, 46.29px) rotate(-85.6deg); opacity: 1; }
  80% { transform: translate(310.85px, 37.32px) rotate(-67.1deg); opacity: 1; }
  85% { transform: translate(315.57px, 29.45px) rotate(-47.5deg); opacity: 1; }
  90% { transform: translate(322.59px, 23.56px) rotate(-23.4deg); opacity: 1; }
  95% { transform: translate(331.25px, 20.46px) rotate(-11.2deg); opacity: 1; }
  100% { transform: translate(340.4px, 19.6px) rotate(0deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-12 { animation-name: landing-script-nib-12; }

@keyframes landing-script-nib-13 {
  0% { transform: translate(399px, 85.6px) rotate(-90deg); opacity: 0; }
  5% { transform: translate(399px, 73.9px) rotate(-90deg); opacity: 1; }
  10% { transform: translate(399px, 62.19px) rotate(-90deg); opacity: 1; }
  15% { transform: translate(399px, 50.49px) rotate(-90deg); opacity: 1; }
  20% { transform: translate(399px, 38.79px) rotate(-90deg); opacity: 1; }
  25% { transform: translate(399px, 27.08px) rotate(-90deg); opacity: 1; }
  30% { transform: translate(399px, 15.38px) rotate(-90deg); opacity: 1; }
  35% { transform: translate(403.33px, 18.89px) rotate(58.3deg); opacity: 1; }
  40% { transform: translate(409.48px, 28.85px) rotate(58.3deg); opacity: 1; }
  45% { transform: translate(415.64px, 38.8px) rotate(58.3deg); opacity: 1; }
  50% { transform: translate(421.8px, 48.75px) rotate(58.3deg); opacity: 1; }
  55% { transform: translate(427.96px, 58.7px) rotate(58.3deg); opacity: 1; }
  60% { transform: translate(434.12px, 68.65px) rotate(58.3deg); opacity: 1; }
  65% { transform: translate(440.27px, 78.61px) rotate(58.3deg); opacity: 1; }
  70% { transform: translate(444.6px, 82.12px) rotate(-90deg); opacity: 1; }
  75% { transform: translate(444.6px, 70.42px) rotate(-90deg); opacity: 1; }
  80% { transform: translate(444.6px, 58.71px) rotate(-90deg); opacity: 1; }
  85% { transform: translate(444.6px, 47.01px) rotate(-90deg); opacity: 1; }
  90% { transform: translate(444.6px, 35.31px) rotate(-90deg); opacity: 1; }
  95% { transform: translate(444.6px, 23.6px) rotate(-90deg); opacity: 1; }
  100% { transform: translate(444.6px, 11.9px) rotate(-90deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-13 { animation-name: landing-script-nib-13; }

@keyframes landing-script-nib-14 {
  0% { transform: translate(508px, 19.6px) rotate(11.2deg); opacity: 0; }
  5% { transform: translate(516.96px, 21.74px) rotate(21.8deg); opacity: 1; }
  10% { transform: translate(524.88px, 26.41px) rotate(34.3deg); opacity: 1; }
  15% { transform: translate(530.83px, 33.38px) rotate(57.6deg); opacity: 1; }
  20% { transform: translate(533.92px, 42px) rotate(79.8deg); opacity: 1; }
  25% { transform: translate(534.69px, 51.17px) rotate(95.5deg); opacity: 1; }
  30% { transform: translate(532.66px, 60.15px) rotate(111.8deg); opacity: 1; }
  35% { transform: translate(527.97px, 68.02px) rotate(132.5deg); opacity: 1; }
  40% { transform: translate(520.87px, 73.82px) rotate(155.9deg); opacity: 1; }
  45% { transform: translate(512.26px, 77.06px) rotate(166.8deg); opacity: 1; }
  50% { transform: translate(503.08px, 77.68px) rotate(-174.7deg); opacity: 1; }
  55% { transform: translate(494.11px, 75.62px) rotate(-158.6deg); opacity: 1; }
  60% { transform: translate(486.19px, 70.99px) rotate(-137.5deg); opacity: 1; }
  65% { transform: translate(480.31px, 63.93px) rotate(-125.6deg); opacity: 1; }
  70% { transform: translate(477.02px, 55.38px) rotate(-103.2deg); opacity: 1; }
  75% { transform: translate(476.32px, 46.2px) rotate(-85.2deg); opacity: 1; }
  80% { transform: translate(478.39px, 37.24px) rotate(-67.1deg); opacity: 1; }
  85% { transform: translate(483.09px, 29.36px) rotate(-46.6deg); opacity: 1; }
  90% { transform: translate(490.19px, 23.56px) rotate(-23.4deg); opacity: 1; }
  95% { transform: translate(498.85px, 20.46px) rotate(-11.2deg); opacity: 1; }
  100% { transform: translate(508px, 19.6px) rotate(0deg); opacity: 0; }
}

.landing-hero-script .dbs-nib.dbs-st-14 { animation-name: landing-script-nib-14; }




/* Prescription symbol (℞) leading the hero wordmark, like the superscription
   at the start of a real script. A plain glyph, not a badge. */
.landing-hero-name-rx {
  align-self: flex-start;
  font-family: var(--font-serif);
  font-size: 0.62em;
  font-weight: var(--font-weight-bold);
  line-height: 0.9;
  margin-top: 0.06em;
  /* Its own copy of the wordmark's white -> wisteria gradient so the ℞ and
     DEBONO read as one material; self-contained because the filter below
     isolates it from the parent's clipped background. */
  background: linear-gradient(180deg, #ffffff 0%, #f4ecfb 55%, #d9c2f0 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 0 8px rgba(191,64,255,0.28));
}

/* Hairline rule under wordmark */
.landing-hero-tagline {
  font-family: var(--font-display);
  /* ⚠️ --hero-n-tagline in .landing-hero mirrors this clamp (x1.5, its line
     box). Change it here and the first-screen fit must be re-measured. */
  font-size: clamp(0.75rem, 0.95vw, 0.95rem);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0;
  margin-top: calc(24 * var(--hero-u));  /* --space-6 at fit 1 */
  text-align: center;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  justify-content: center;
}

/* EDUCATION keeps the tagline's display font (matches GAMIFIED) but gets a
   soft lilac glow tuned to the hero's top-of-gradient palette. */
.landing-hero-tagline-glow {
  color: #fbf3ff;
  position: relative;
  z-index: 0;
  text-shadow:
    0 0 4px rgba(251, 243, 255, 0.9),
    0 0 10px rgba(214, 163, 240, 0.95),
    0 0 22px rgba(191, 64, 255, 0.8),
    0 0 40px rgba(191, 64, 255, 0.55);
}

/* soft radial aura sitting behind the letters */
.landing-hero-tagline-glow::before {
  content: '';
  position: absolute;
  inset: -0.7em -1em;
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    rgba(191, 64, 255, 0.5) 0%,
    rgba(214, 163, 240, 0.22) 50%,
    transparent 72%
  );
  filter: blur(7px);
}

.landing-hero-sub {
  font-family: var(--font-display);
  /* ⚠️ --hero-n-sub in .landing-hero mirrors this clamp AND this line-height AND
     the line count the copy happens to wrap to (3.54 line-boxes worst case).
     This block is the single biggest unshrinkable item in the first screen, so
     editing the font, the measure below, or the sentence itself all move the
     fit. Re-measure with harness/2026-08-05-hero-fit/sweep.mjs. */
  font-size: clamp(1.05rem, 1.25vw, 1.25rem);
  line-height: 1.55;
  font-weight: 400;
  color: var(--text-muted);
  /* Font size and measure are deliberately NOT in the flexible set — body copy
     that shrinks with the window is the one thing this must not trade away. */
  max-width: clamp(440px, 30vw, 620px);
  margin: calc(32 * var(--hero-u)) auto calc(40 * var(--hero-u));  /* --space-8 / --space-10 at fit 1 */
  text-align: center;
}

.landing-hero-sub em {
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 400;
  color: var(--text-primary);
  font-size: 1.18em;
  letter-spacing: -0.01em;
}

.landing-hero-ctas {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  /* One pill height for the whole row, whatever it holds. The primary has no
     border and the ghosts have two, so their natural heights differ by ~1.3px —
     enough to read as a wobble once there is more than one ghost beside the
     primary. Set here rather than per-button so a fourth CTA lines up for free.
     Yields with the rest of the hero on a short screen but stops at 48px — a
     control has a floor that a gap does not, and that 48 is the button floor
     counted into --hero-n-rest above (with the remaining 8px in --hero-flex).
     ⚠️ A fourth CTA, or a longer label, widens this row and can make it WRAP,
     which N only budgets for below 600px. */
  --cta-h: max(48px, calc(56 * var(--hero-u)));
}

.landing-hero-ctas > * {
  flex: 0 0 auto;
  height: var(--cta-h);
  box-sizing: border-box;
  white-space: nowrap;
}

/* ---- Buttons ---- */
.landing-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 1rem 2.2rem;
  /* --btn-bg is set per instance so CTAs step through the pastel gradient
     down the page; falls back to the original red for any button that
     doesn't set it. Pastel fills get dark-ink labels for contrast. */
  background: var(--btn-bg, var(--accent-primary));
  color: var(--btn-fg, #ffffff);
  border-radius: var(--radius-full);
  font-family: var(--font-display);
  font-size: 0.95rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-decoration: none;
  text-transform: uppercase;
  transition: all var(--transition-fast);
  position: relative;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.28) inset,
    0 -1px 0 rgba(0,0,0,0.22) inset,
    0 8px 26px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 42%, transparent),
    0 0 0 1px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 55%, transparent);
}

.landing-btn-primary::after {
  content: '→';
  font-family: var(--font-display);
  font-weight: 400;
  transition: transform var(--transition-fast);
}

.landing-btn-primary:hover {
  filter: brightness(1.08);
  transform: translateY(-2px);
  box-shadow:
    0 1px 0 rgba(255,255,255,0.35) inset,
    0 -1px 0 rgba(0,0,0,0.22) inset,
    0 16px 40px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 55%, transparent),
    0 0 0 1px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 70%, transparent);
}

.landing-btn-primary:hover::after {
  transform: translateX(4px);
}

.landing-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  text-decoration: none;
  font-family: var(--font-display);
  font-size: 0.92rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: all var(--transition-fast);
  padding: 1rem 1.4rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background: rgba(255,255,255,0.025);
}

.landing-btn-ghost:hover {
  color: var(--text-primary);
  border-color: var(--border-focus);
  background: rgba(255,255,255,0.05);
  transform: translateY(-2px);
}

/* Now a real <Link> to /compendium, so the button-era resets are gone: no \`font: inherit\`
   (there is no UA button chrome left to undo) and no \`cursor: default\` (a working
   link must show a pointer). Padding is back at Log In's 1.4rem — it was trimmed to
   1.15rem only to pay for the "soon" badge out of the pill, and the badge is gone. */
.landing-btn-compendium {
  justify-content: center;
  padding: 1rem 1.4rem;
  font-family: var(--font-display);
  font-size: 0.92rem;
  font-weight: var(--font-weight-medium);
}

/* The badge is the program strip's "soon" chip — same idiom, same page. */
.landing-btn-soon {
  font-size: 0.55rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-disabled);
  opacity: 0.8;
  padding: 0.15rem 0.35rem;
  border: 1px solid var(--border-subtle);
  border-radius: 3px;
  line-height: 1;
}

/* ---- Google Sign-In on hero ---- */
.landing-google-divider {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  width: 100%;
  /* Widened with the CTA row: a 210px rule under a 560px row of buttons reads as
     a stub rather than a separator. At desktop this lands on ~420px, the same
     width as the program strip directly below it, so the two share an edge.
     One-line revert to clamp(210px, 13vw, 300px) if the short rule is wanted. */
  max-width: clamp(260px, 30vw, 420px);
  margin-top: calc(20 * var(--hero-u));  /* --space-5 at fit 1 */
}

.landing-google-divider::before,
.landing-google-divider::after {
  content: '';
  flex: 1 1;
  height: 1px;
  background: var(--border-subtle);
}

.landing-google-divider span {
  font-size: var(--text-sm);
  color: var(--text-subtle);
  text-transform: lowercase;
}

.landing-google-btn {
  margin-top: calc(12 * var(--hero-u));  /* --space-3 at fit 1 */
  min-height: clamp(31px, 2vw, 44px);
  display: flex;
  justify-content: center;
}

.landing-google-error {
  font-size: var(--text-sm);
  color: var(--accent-primary);
  margin-top: var(--space-2);
}

/* ---- Footer brand ---- */
.landing-brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.landing-wordmark-sm {
  font-size: var(--text-base);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

/* ---- Sections shared ---- */
.landing-section-inner {
  position: relative;
  z-index: 1;
  max-width: clamp(770px, 49vw, 1100px);
  margin: 0 auto;
  padding: 0 var(--space-6);
}

.landing-section-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--text-subtle);
  margin-bottom: var(--space-12);
}

.landing-section-label::before,
.landing-section-label::after {
  content: '';
  flex: 0 0 clamp(40px, 6vw, 80px);
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border-default), transparent);
}

.landing-section-label::before {
  background: linear-gradient(90deg, transparent, var(--border-default));
}

.landing-section-label::after {
  background: linear-gradient(270deg, transparent, var(--border-default));
}

/* ============================================
   UPLOAD TO MASTERY — 3-step flow
   ============================================ */

.landing-flow {
  padding: var(--space-12) 0 var(--space-16);
  position: relative;
  z-index: 1;
}

.landing-flow-subtitle {
  font-family: var(--font-display);
  text-align: center;
  font-size: clamp(1.65rem, 2.4vw, 2.4rem);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.02em;
  color: var(--text-primary);
  margin-top: calc(-1 * var(--space-8));
  margin-bottom: var(--space-12);
  line-height: 1.15;
}

.landing-flow-subtitle::after {
  content: '';
  display: block;
  width: 36px;
  height: 2px;
  background: var(--accent-primary);
  margin: var(--space-4) auto 0;
  border-radius: 2px;
}

.landing-flow-grid {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: nowrap;
  max-width: 880px;
  margin: 0 auto;
}

.landing-flow-card {
  flex: 1 1;
  max-width: clamp(160px, 14vw, 260px);
  min-width: clamp(140px, 11vw, 220px);
  min-height: clamp(220px, 16vw, 320px);
  background:
    linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.015) 100%);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  padding: var(--space-12) var(--space-5) var(--space-6);
  text-align: center;
  backdrop-filter: var(--glass-blur);
  transition: all var(--transition-normal);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  isolation: isolate;
}

/* Atmospheric tint per step */
.landing-flow-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: 50%;
  transform: translateX(-50%);
  width: 200%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
  filter: blur(60px);
  opacity: 0.4;
  transition: opacity var(--transition-normal);
}

.landing-flow-card:nth-child(1)::before {
  background: radial-gradient(ellipse at top, rgba(239,68,68,0.4), transparent 60%);
}
.landing-flow-card:nth-child(3)::before {
  background: radial-gradient(ellipse at top, rgba(139,92,246,0.4), transparent 60%);
}
.landing-flow-card:nth-child(5)::before {
  background: radial-gradient(ellipse at top, rgba(251,191,36,0.4), transparent 60%);
}

/* Top hairline accent per step */
.landing-flow-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 1px;
  pointer-events: none;
}

.landing-flow-card:nth-child(1)::after { background: linear-gradient(90deg, transparent, rgba(239,68,68,0.7), transparent); }
.landing-flow-card:nth-child(3)::after { background: linear-gradient(90deg, transparent, rgba(139,92,246,0.7), transparent); }
.landing-flow-card:nth-child(5)::after { background: linear-gradient(90deg, transparent, rgba(251,191,36,0.7), transparent); }

.landing-flow-card:hover {
  border-color: var(--border-strong);
  transform: translateY(-6px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
}

.landing-flow-card:hover::before {
  opacity: 0.7;
}

.landing-flow-step-num {
  position: absolute;
  top: 14px;
  left: 14px;
  width: clamp(28px, 1.8vw, 36px);
  height: clamp(28px, 1.8vw, 36px);
  border-radius: 50%;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0;
  background: rgba(0,0,0,0.4);
  border: 1px solid rgba(255,255,255,0.08);
}

/* Mono-numbered chip with corner alignment per step */
.landing-flow-card:nth-child(1) .landing-flow-step-num {
  background: rgba(239,68,68,0.18);
  border-color: rgba(239,68,68,0.45);
  color: #fca5a5;
}
.landing-flow-card:nth-child(3) .landing-flow-step-num {
  background: rgba(139,92,246,0.18);
  border-color: rgba(139,92,246,0.45);
  color: #c4b5fd;
}
.landing-flow-card:nth-child(5) .landing-flow-step-num {
  background: rgba(251,191,36,0.18);
  border-color: rgba(251,191,36,0.45);
  color: #fcd34d;
}

/* Step num colors match their icon */
.landing-flow-card:nth-child(1) .landing-flow-step-num {
  background: var(--accent-primary-muted);
  border: 1px solid var(--accent-primary-border);
  color: var(--accent-primary);
}
.landing-flow-card:nth-child(3) .landing-flow-step-num {
  background: rgba(139,92,246,0.12);
  border: 1px solid rgba(139,92,246,0.3);
  color: #8b5cf6;
}
.landing-flow-card:nth-child(5) .landing-flow-step-num {
  background: rgba(251,191,36,0.12);
  border: 1px solid rgba(251,191,36,0.3);
  color: #fbbf24;
}

.landing-flow-icon {
  width: clamp(48px, 3vw, 64px);
  height: clamp(48px, 3vw, 64px);
  border-radius: var(--radius-xl);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-5);
  border: 1px solid transparent;
  position: relative;
}

.landing-flow-icon::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 18px;
  border: 1px dashed currentColor;
  opacity: 0.25;
}

.landing-flow-icon-upload { background: rgba(239,68,68,0.16); color: var(--accent-primary); border-color: rgba(239,68,68,0.35); }
.landing-flow-icon-play { background: rgba(139,92,246,0.16); color: #a78bfa; border-color: rgba(139,92,246,0.35); }
.landing-flow-icon-master { background: rgba(251,191,36,0.16); color: #fbbf24; border-color: rgba(251,191,36,0.35); }

.landing-flow-auto-badge {
  position: absolute;
  top: 16px;
  right: 14px;
  font-family: var(--font-mono);
  font-size: 0.6rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #4ade80;
  background: rgba(34,197,94,0.12);
  border: 1px solid rgba(34,197,94,0.35);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.landing-flow-auto-badge::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 6px #4ade80;
}

.landing-flow-auto-badge-mixed {
  color: #f59e0b;
  background: rgba(245,158,11,0.1);
  border-color: rgba(245,158,11,0.25);
}

.landing-flow-card h3 {
  font-family: var(--font-display);
  font-size: clamp(1.15rem, 1.4vw, 1.4rem);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.01em;
  margin-bottom: var(--space-3);
  color: var(--text-primary);
}

.landing-flow-card p {
  font-family: var(--font-display);
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--text-muted);
  flex: 1 1;
  font-weight: 400;
}

.landing-flow-arrow {
  display: flex;
  align-items: center;
  align-self: center;
  color: var(--text-disabled);
  flex-shrink: 0;
  opacity: 0.7;
}

/* Flow stagger */
.landing-flow-grid .landing-animate:nth-child(1) { transition-delay: 0s; }
.landing-flow-grid .landing-animate:nth-child(2) { transition-delay: 0.04s; }
.landing-flow-grid .landing-animate:nth-child(3) { transition-delay: 0.08s; }
.landing-flow-grid .landing-animate:nth-child(4) { transition-delay: 0.12s; }
.landing-flow-grid .landing-animate:nth-child(5) { transition-delay: 0.16s; }
.landing-flow-grid .landing-animate:nth-child(6) { transition-delay: 0.20s; }
.landing-flow-grid .landing-animate:nth-child(7) { transition-delay: 0.24s; }
.landing-flow-grid .landing-animate:nth-child(8) { transition-delay: 0.28s; }
.landing-flow-grid .landing-animate:nth-child(9) { transition-delay: 0.32s; }

/* ============================================
   FEATURES — Showcase cards (alternating layout)
   ============================================ */

.landing-features {
  padding: var(--space-16) 0;
  position: relative;
  z-index: 1;
}

.landing-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-10);
  align-items: center;
  margin-bottom: var(--space-16);
}

.landing-showcase:last-child {
  margin-bottom: 0;
}

.landing-showcase-reverse {
  direction: rtl;
}

.landing-showcase-reverse > * {
  direction: ltr;
}

.landing-showcase-text {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.landing-showcase-text h3 {
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 2vw, 1.95rem);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--text-primary);
  margin: 0;
}

.landing-showcase-text p {
  font-family: var(--font-display);
  font-size: clamp(0.95rem, 1.1vw, 1.05rem);
  line-height: 1.6;
  color: var(--text-muted);
  font-weight: 400;
  margin: 0;
}

.landing-showcase-visual {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  padding: var(--space-6);
  backdrop-filter: var(--glass-blur);
  overflow: hidden;
}

/* Showcase stagger */
.landing-features .landing-showcase:nth-child(3) { transition-delay: 0.06s; }
.landing-features .landing-showcase:nth-child(4) { transition-delay: 0.12s; }
.landing-features .landing-showcase:nth-child(5) { transition-delay: 0.18s; }
.landing-features .landing-showcase:nth-child(6) { transition-delay: 0.24s; }

/* ---- Boss Map SVG ---- */
.landing-map-svg {
  width: 100%;
  height: auto;
  display: block;
}

/* ---- Mock Concept Card ---- */
.landing-mock-concept {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.landing-mock-concept-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: #0ea5e9;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
}

.landing-mock-concept-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

.landing-mock-concept-table th {
  text-align: left;
  padding: var(--space-2) var(--space-3);
  font-size: 0.65rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--text-subtle);
  border-bottom: 1px solid var(--border-subtle);
}

.landing-mock-concept-table td {
  padding: var(--space-2) var(--space-3);
  color: var(--text-muted);
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.landing-mock-concept-table td:first-child {
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  font-size: 0.7rem;
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
}

.landing-mock-concept-mastery {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.landing-mock-concept-bar {
  flex: 1 1;
  height: clamp(4px, 0.3vw, 6px);
  background: rgba(255,255,255,0.06);
  border-radius: 3px;
  overflow: hidden;
}

.landing-mock-concept-fill {
  width: 72%;
  height: 100%;
  background: linear-gradient(90deg, #0ea5e9, #8b5cf6);
  border-radius: 3px;
}

.landing-mock-concept-mastery span {
  font-size: 0.65rem;
  font-weight: var(--font-weight-semibold);
  color: var(--text-subtle);
}

/* ---- Mock Quiz ---- */
.landing-mock-quiz {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.landing-mock-quiz-q {
  font-size: var(--text-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  line-height: var(--leading-relaxed);
}

.landing-mock-quiz-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.landing-mock-quiz-opt {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-lg);
  font-size: var(--text-sm);
  color: var(--text-muted);
  border: 1px solid var(--border-subtle);
  background: transparent;
}

.landing-mock-quiz-opt-correct {
  border-color: rgba(34,197,94,0.4);
  background: rgba(34,197,94,0.06);
  color: var(--text-primary);
}

.landing-mock-quiz-radio {
  width: clamp(10px, 0.55vw, 14px);
  height: clamp(10px, 0.55vw, 14px);
  border-radius: 50%;
  border: 1.5px solid var(--border-default);
  flex-shrink: 0;
}

.landing-mock-quiz-radio-correct {
  border-color: #22c55e;
  background: radial-gradient(circle, #22c55e 40%, transparent 41%);
}

.landing-mock-quiz-opt svg {
  margin-left: auto;
}

/* ============================================
   LEVEL UP — Player system strip
   ============================================ */

.landing-levelup {
  padding: var(--space-16) 0;
  position: relative;
  z-index: 1;
}

.landing-levelup-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
  backdrop-filter: var(--glass-blur);
}

.landing-levelup-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-3);
  padding: var(--space-8) var(--space-4);
  min-width: 0;
  max-width: none;
  position: relative;
  transition: background var(--transition-normal);
}

.landing-levelup-item:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 12%;
  bottom: 12%;
  width: 1px;
  background: linear-gradient(180deg, transparent, var(--border-subtle), transparent);
}

.landing-levelup-item:hover {
  background: rgba(255,255,255,0.025);
}

.landing-levelup-icon {
  width: clamp(46px, 2.6vw, 56px);
  height: clamp(46px, 2.6vw, 56px);
  border-radius: var(--radius-xl);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-2);
  position: relative;
}

.landing-levelup-icon::after {
  content: '';
  position: absolute;
  inset: -5px;
  border-radius: 16px;
  border: 1px dashed currentColor;
  opacity: 0.22;
}

/* XP — gold */
.landing-levelup-item:nth-child(1) .landing-levelup-icon {
  background: rgba(251,191,36,0.12);
  border: 1px solid rgba(251,191,36,0.3);
  color: #fbbf24;
}

/* Progression — cyan */
.landing-levelup-item:nth-child(2) .landing-levelup-icon {
  background: rgba(56,189,248,0.12);
  border: 1px solid rgba(56,189,248,0.3);
  color: #38bdf8;
}

/* Equipment — purple */
.landing-levelup-item:nth-child(3) .landing-levelup-icon {
  background: rgba(167,139,250,0.12);
  border: 1px solid rgba(167,139,250,0.3);
  color: #a78bfa;
}

/* Boss Battles — red */
.landing-levelup-item:nth-child(4) .landing-levelup-icon {
  background: rgba(239,68,68,0.12);
  border: 1px solid rgba(239,68,68,0.3);
  color: #f87171;
}

/* Honor — amber */
.landing-levelup-item:nth-child(5) .landing-levelup-icon {
  background: rgba(245,158,11,0.12);
  border: 1px solid rgba(245,158,11,0.3);
  color: #f59e0b;
}

.landing-levelup-label {
  font-family: var(--font-display);
  font-size: 0.95rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.005em;
  color: var(--text-primary);
}

.landing-levelup-desc {
  font-family: var(--font-display);
  font-size: 0.8rem;
  color: var(--text-muted);
  line-height: 1.5;
  font-weight: 400;
}

/* Level-up stagger */
.landing-levelup-grid .landing-animate:nth-child(2) { transition-delay: 0.06s; }
.landing-levelup-grid .landing-animate:nth-child(3) { transition-delay: 0.12s; }
.landing-levelup-grid .landing-animate:nth-child(4) { transition-delay: 0.18s; }
.landing-levelup-grid .landing-animate:nth-child(5) { transition-delay: 0.24s; }

/* ---- Motivational sentiments ---- */
.landing-sentiment {
  text-align: center;
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 400;
  font-size: clamp(1.5rem, 2.4vw, 2.4rem);
  letter-spacing: -0.015em;
  line-height: 1.25;
  color: var(--text-secondary);
  padding: var(--space-10) var(--space-6);
  position: relative;
  z-index: 1;
  max-width: clamp(420px, 36vw, 760px);
  margin: 0 auto;
}

.landing-sentiment::before,
.landing-sentiment::after {
  content: '';
  display: block;
  width: 24px;
  height: 1px;
  background: var(--accent-primary);
  opacity: 0.5;
  margin: var(--space-4) auto;
}

.landing-sentiment::before {
  margin-bottom: var(--space-5);
}

.landing-sentiment::after {
  margin-top: var(--space-5);
}

/* ---- Feature section lede ----
   Replaced the Study Features / Gamification toggle: the two tabs described the
   same feature list from two angles, so they were merged into one run of
   showcases that each name their learning method. This line sets that up. */
.landing-features-lede {
  max-width: 58ch;
  margin: calc(-1 * var(--space-6)) auto 0;
  padding: 0 var(--space-6);
  text-align: center;
  font-family: var(--font-display);
  font-size: clamp(0.95rem, 1.15vw, 1.1rem);
  line-height: 1.6;
  color: var(--text-muted);
}

/* ---- Method tag ----
   The learning method a showcase implements; the h3 under it is the mechanic
   that carries it. Uses the same uppercase micro-label grammar as the rest of
   the page so it reads as an eyebrow, not a second heading. */
.landing-method-tag {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  border: 1px solid rgba(var(--axis, 239 68 68) / 0.34);
  border-radius: var(--radius-full);
  background: rgba(var(--axis, 239 68 68) / 0.09);
  font-family: var(--font-display);
  font-size: 0.66rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.14em;
  text-transform: uppercase;
  line-height: 1;
  color: rgba(var(--axis, 248 113 113) / 0.95);
  margin-bottom: calc(-1 * var(--space-2));
}

/* ---- Axis assignment ----
   Each showcase declares WHICH WAY OF KNOWING its mechanic trains, and that one
   attribute drives every coloured thing inside it. The mapping is the point of
   the whole system, so it is stated once, here, rather than being smeared
   across the markup as hardcoded colours:

     understanding  the map, the builds, the tree — things whose meaning is in
                    how the parts interact, so you cannot hold one without the rest
     knowledge      the cards, the drills, the deck — plain recall, memorised
                    and rehearsed on a schedule
     intuition      PvP, live combat — answering faster than you can reason,
                    which only works when the knowing has stopped being recall

   An element with no data-axis keeps the old red. Nothing here is required.

   Note there is deliberately NO per-axis rule in this file. Landing.jsx
   resolves each tagged element's --axis from its own attribute, so the
   stylesheet never holds a list of axis names that could fall out of step with
   the markup. Adding an axis means declaring its colour token, and nothing
   else, anywhere. */

/* The element-scale echo of the page-scale cloud: the same hue, same softness,
   bled in from the card's leading edge. Sits behind the content and never
   touches a text box, so it tints without costing contrast. */
.landing [data-axis] {
  position: relative;
}

/* A gradient is clipped by its own box, so if it is still opaque when it
   reaches an edge, that edge becomes a straight line — measured at 8.2
   luminance units against a 4.4 noise floor on the first version, which is a
   visible seam across the top of every card. The geometry is therefore solved
   rather than tuned: centred vertically at 50% with a 44% radius, the fade
   completes at 6% and 94% of the box height, INSIDE it, so there is no top or
   bottom edge to see at any card height. Horizontally it still meets the box,
   but at 32% ± 40% the alpha there is under 0.01 — below the noise floor, and
   it lands off the content anyway. */
.landing [data-axis]::before {
  content: '';
  position: absolute;
  inset: -12% -6%;
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(40% 44% at 32% 50%,
    rgba(var(--axis) / 0.085) 0%,
    rgba(var(--axis) / 0.032) 46%,
    rgba(var(--axis) / 0) 100%);
}

.landing .landing-showcase-reverse[data-axis]::before {
  background: radial-gradient(40% 44% at 68% 50%,
    rgba(var(--axis) / 0.085) 0%,
    rgba(var(--axis) / 0.032) 46%,
    rgba(var(--axis) / 0) 100%);
}

/* Edge: one hairline in the axis hue under each showcase. This is the "clean"
   half of the brief — a 1px rule reads as precision where a glow reads as
   decoration, and it gives the eye a real boundary between mechanics. */
.landing [data-axis]::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(-1 * var(--space-8));
  height: 1px;
  pointer-events: none;
  background: linear-gradient(90deg,
    rgba(var(--axis) / 0.30) 0%,
    rgba(var(--axis) / 0.10) 34%,
    rgba(255, 255, 255, 0.04) 62%,
    transparent 100%);
}

.landing .landing-showcase-reverse[data-axis]::after {
  background: linear-gradient(270deg,
    rgba(var(--axis) / 0.30) 0%,
    rgba(var(--axis) / 0.10) 34%,
    rgba(255, 255, 255, 0.04) 62%,
    transparent 100%);
}

.landing [data-axis]:last-child::after { display: none; }

.landing-method-tag::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
}

/* Wide showcases stack their text in a plain <div>, so the tag needs its own
   block spacing there rather than the flex-column gap. */
.landing-showcase-wide-header .landing-method-tag {
  margin-bottom: var(--space-2);
}

/* ---- Live skill-tree preview: the REAL SkillTreeViewport, read-only ----
   Rendered in a FULL-WIDTH wide-showcase so the tree has horizontal room. */
.landing-showcase-wide-visual.landing-showcase-visual--skilltree {
  display: block;
  width: 100%;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  backdrop-filter: var(--glass-blur);
  overflow: hidden;
}
.landing-skilltree-embed {
  position: relative;
  width: 100%;
  /* Wider card → taller canvas so the branching tree spreads out, not cramped. */
  height: clamp(360px, 40vw, 560px);
  overflow: hidden;
  border-radius: var(--radius-2xl);
  /* Read-only preview — the interactive tree is the demo map above. Block
     clicks/hover so the marketing card never mutates the anon loadout. */
  pointer-events: none;
}
.landing-skilltree-embed .skill-tree-viewport,
.landing-skilltree-embed .skill-tree-viewport__body {
  width: 100%;
  height: 100%;
}

/* ---- Mock PvP Combat (matching prod PvPHPBar) ---- */
.landing-mock-pvp {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) 0;
}

.landing-mock-pvp-side {
  flex: 1 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.landing-mock-pvp-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

.landing-mock-pvp-name {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}

.landing-mock-pvp-name-player { color: #22c55e; }
.landing-mock-pvp-name-opponent { color: #f87171; }

.landing-mock-pvp-hp-value {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}

.landing-mock-pvp-track {
  height: 8px;
  border-radius: var(--radius-full);
  overflow: hidden;
  background: rgba(255,255,255,0.06);
}

.landing-mock-pvp-fill {
  height: 100%;
  border-radius: var(--radius-full);
  transition: width 0.5s ease;
}

.landing-mock-pvp-fill-player { background: #22c55e; }
.landing-mock-pvp-fill-opponent { background: #f87171; }

.landing-mock-pvp-vs {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
  letter-spacing: 0.1em;
  flex-shrink: 0;
  padding: 0 var(--space-1);
}

/* ---- Wide showcase (for Gold Card) ---- */
/* \`clip\`, NOT \`hidden\`, and the difference is the whole reason these two blocks
   used to carry a scrollbar. \`overflow-x: hidden\` with \`overflow-y\` left at
   \`visible\` does not mean "clip sideways": the used value of \`visible\` becomes
   \`auto\` whenever the other axis is neither \`visible\` nor \`clip\`, so the block
   silently became a SCROLL CONTAINER on the vertical axis. Then \`.landing
   [data-axis]::before\` — the axis glow, deliberately bled past the box at
   \`inset: -12% -6%\` — landed in that container's scrollable overflow region and
   gave it a scroll range. Measured at four widths: the range was 12.0% of the
   block's height and 6.0% of its width EVERY time (153/1276, 87/726, 82/678,
   69/576) — the glow's inset sets that extent exactly. (It was not the ONLY
   thing in there, as this comment first claimed: suppressing the glow leaves 32px
   on the Gold Card block, which is its \`::after\` hairline at \`bottom: -32px\`. So
   a reader who dragged that inner bar down 32px would have reached a hairline
   rather than nothing. The conclusion stands — it was a scrollbar onto
   decoration — but "nothing else" was wrong.)

   🔴 THE LAYOUT DOES MOVE, and this comment said it did not. A scroll container
   with a real range RESERVES A GUTTER: under the old rule both blocks lose 10px
   of content width (clientWidth 712 vs 722), which costs the Skill Tree block's
   header paragraph a fourth line and takes that block 726 -> 749px and the
   document 8776 -> 8799. So \`clip\` makes the page ~23px SHORTER and both blocks
   10px WIDER — a real improvement, not a no-op.
   The first A/B here reported zero deltas because it ran through the harness's
   \`cdp-pipe.mjs\`, which launches chrome with \`--hide-scrollbars\` — that flag pins
   the gutter, the one quantity that changes, at zero. Re-measure this with
   \`2026-08-05-landing-scroll/cdp-pipe-scrollbars.mjs\`, never the default driver.
   (In-run A/B is still mandatory for a different reason: the page renders with or
   without its ~950px demo section depending on whether the anon token mint is
   rate-limited, so a cross-LOAD diff is unattributable.)
   ONE thing does come back with it: \`.landing [data-axis]::after\`, the 1px axis
   hairline at \`bottom: -32px\`, which sat outside the old clip and never painted
   on the Gold Card block while five other showcases drew theirs. Measured on
   pixels — the row at that exact offset goes 17.78 -> 33.69 while every
   neighbouring row moves under 0.11. The Skill Tree block still draws none, and
   should not: it is \`:last-child\`, which \`.landing [data-axis]:last-child::after\`
   sets to \`display: none\` on purpose. The glow's vertical bleed is NOT a visible
   change either way — by the box edge its alpha is already ~0 by the geometry
   the comment above this describes, and the pixels agree (under 0.11 per row).
   The two declarations are a FALLBACK PAIR, not a leftover: a browser without
   \`overflow-x: clip\` (pre-16 Safari) keeps the old behaviour rather than silently
   dropping the clip. Verified in Chrome 144 and Firefox 153 — both resolve the
   pair to \`clip\`, gutter 0, scroll refused. Do not delete the second line.
   (An earlier draft called losing the horizontal clip "the one thing worse than
   the scrollbar". That was unfounded: with \`overflow-x: visible\` on both blocks
   nothing measurable changes — the 43px of bleed is entirely the glow, it lands
   in a 354px page margin, and \`.landing\`'s own \`overflow-x: hidden\` absorbs
   anything that escapes. The pair is cheap insurance, not a rescue.)

   ⚠️ KNOWN AND NOT ADDRESSED: \`clip\` on ONE axis clips one axis. The vertical
   edges of these blocks are now unbounded, where the old scroll container clipped
   both. Nothing real is out there today (it is why the hairline can paint at all),
   but \`overflow: clip; overflow-clip-margin: 33px\` was measured to give layout
   numbers bit-identical to this rule — 722 / 1276 / 726 / 8776, gutter 0, scroll
   refused — while bounding the escape at 33px, which is just enough for the
   hairline's \`bottom: -32px\` + 1px. It is NOT shipped because the containment
   half was never confirmed on an instrument of mine that could tell the two apart.
   See the todo before adopting it, and note it couples this rule to \`--space-8\`. */
.landing-showcase-wide {
  margin-bottom: var(--space-16);
  overflow-x: hidden; /* fallback for browsers without \`clip\` */
  overflow-x: clip;
}

.landing-showcase-wide-header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.landing-showcase-wide-header h3 {
  font-size: var(--text-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.landing-showcase-wide-header p {
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--text-muted);
}

.landing-showcase-wide-visual {
  display: flex;
  justify-content: center;
}

/* ---- Build Rows (synergistic card loadouts) ---- */
.landing-builds-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  overflow-x: hidden;
}

.landing-build-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.landing-build-meta {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

/* The row's synergy copy is a LIST of authored lines (a keystone build carries
   one per augmented card), so it stacks. Each line is its own registry string
   and gets its own box rather than being run together into one sentence. */
.landing-build-synergy {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  font-style: italic;
}

.landing-build-line {
  line-height: var(--leading-relaxed);
}

/* FONT FIDELITY. PillCard.css declares no font-family at all, so a mounted card
   inherits the page's — and \`.landing\` sets var(--font-display) (Outfit) at the
   top of this file, while the game draws every card in var(--font-primary) from
   design-tokens.css:12. The restore goes HERE, on the smallest box that holds
   only mounted game components: the cards then read exactly as they do in game,
   while the landing's own chrome (row label, synergy lines) keeps the page's
   display face. Same fix the compendium makes at Compendium.css:31, scoped instead of page
   -wide. */
.landing-build-cards {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
  justify-content: center;
  font-family: var(--font-primary);
}

/* The gap is 8 and not 12 for a MEASURED reason. This section's column is
   770px wide with 24px of padding, so a build row has 722px to fill, and a
   keystone build is a keystone card plus its augmented drugs: four 172px cards
   = 688px. At a 12px gap that is 724px and the row broke 3 + 1, orphaning the
   last card, for want of two pixels. At 8px it is 712px and the build reads as
   one line. Rows still wrap (a five-card build would), which is the behaviour a
   registry-driven list needs; this only stops a build the registry actually
   holds from wrapping for nothing. */

/* Build label. Tinted by the row's OWN accent, handed down as --build-accent by
   Landing.jsx from BUILD_ROWS (the keystone's accent, or the lead card's
   skill-tree node colour) — the same value the cards on that row paint their
   stripe and icon ring with. There is deliberately no per-build colour class:
   a build that arrives from the registry tomorrow cannot land here unstyled.
   The 82/18 mix with white lifts the darkest accent (#ef4444) off a near-black
   background at this size without inventing a second palette. */
.landing-build-label {
  font-size: 0.6rem;
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-widest);
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-full);
  width: fit-content;
  flex-shrink: 0;
  color: color-mix(in srgb, var(--build-accent, var(--text-muted)) 82%, #fff);
  background: color-mix(in srgb, var(--build-accent, transparent) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--build-accent, transparent) 28%, transparent);
}

/* ---- Mock Deck Upkeep ---- */
.landing-mock-upkeep {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.landing-mock-upkeep-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.landing-mock-upkeep-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.landing-mock-upkeep-name {
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.landing-mock-upkeep-status {
  font-size: 0.65rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  padding: 0.15rem 0.5rem;
  border-radius: var(--radius-full);
}

.landing-mock-upkeep-status-warn {
  color: #f59e0b;
  background: rgba(245,158,11,0.1);
  border: 1px solid rgba(245,158,11,0.25);
}

.landing-mock-upkeep-status-good {
  color: #22c55e;
  background: rgba(34,197,94,0.1);
  border: 1px solid rgba(34,197,94,0.25);
}

.landing-mock-upkeep-bar {
  height: clamp(6px, 0.35vw, 8px);
  background: rgba(255,255,255,0.06);
  border-radius: 4px;
  overflow: hidden;
}

.landing-mock-upkeep-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.landing-mock-upkeep-fill-warn {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.landing-mock-upkeep-fill-good {
  background: linear-gradient(90deg, #22c55e, #4ade80);
}

/* Decay timer inline */
.landing-mock-upkeep-decay {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  font-weight: var(--font-weight-semibold);
  margin-top: var(--space-1);
}

.landing-mock-upkeep-decay-danger {
  color: #f87171;
}

.landing-mock-upkeep-decay-safe {
  color: var(--text-subtle);
}

/* ============================================
   SOCIAL PROOF — Stats bar
   ============================================ */

.landing-proof {
  padding: var(--space-12) 0;
  position: relative;
  z-index: 1;
}

/* Side-by-side two-panel grid */
.landing-proof-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

.landing-proof-panel {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01));
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  padding: var(--space-6) var(--space-6) var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  position: relative;
  overflow: hidden;
}

.landing-proof-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 24px;
  right: 24px;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border-default), transparent);
}

.landing-proof-panel-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-bottom: var(--space-3);
  border-bottom: 1px dashed var(--border-subtle);
}

.landing-proof-panel-dot {
  width: clamp(6px, 0.4vw, 8px);
  height: clamp(6px, 0.4vw, 8px);
  border-radius: 50%;
  flex-shrink: 0;
}

.landing-proof-panel-dot-live {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.7);
  animation: landing-pulse-dot 2s ease-in-out infinite;
}

@keyframes landing-pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.landing-proof-panel-title {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  flex: 1 1;
}

.landing-proof-version {
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: var(--font-weight-medium);
  color: #60a5fa;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  letter-spacing: 0.08em;
}

.landing-proof-stats {
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  gap: var(--space-4);
  flex: 1 1;
}

.landing-proof-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

/* Live panel is now the sole (full-width) proof panel, so its 4 stats spread
   across a single row. (Was a 2x2 grid when it sat in a half-width column next
   to the since-removed Game Content panel.) Mobile drops back to 2x2 below. */
.landing-proof-panel-live .landing-proof-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3);
  align-items: end;
}

.landing-proof-number {
  font-family: var(--font-display);
  font-size: clamp(1.65rem, 2.8vw, 2.4rem);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: -0.025em;
}

.landing-proof-panel-live .landing-proof-number {
  color: var(--text-primary);
  background: linear-gradient(180deg, #ffffff 0%, #d1d5db 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.landing-proof-label {
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  white-space: nowrap;
}

/* ============================================
   PRICING — Tier cards
   ============================================ */

.landing-pricing {
  padding: var(--space-16) 0;
  position: relative;
  z-index: 1;
}

.landing-pricing-title {
  text-align: center;
  font-family: var(--font-display);
  font-size: clamp(1.85rem, 3vw, 2.6rem);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.025em;
  line-height: 1.1;
  /* The support line below now carries the gap down to the cards. */
  margin-bottom: var(--space-4);
  color: var(--text-primary);
  max-width: 540px;
  margin-left: auto;
  margin-right: auto;
}

/* The concrete claim under the rhetorical headline. Same 540px measure as the
   title so the two share an edge; muted so the headline stays the loud one. */
.landing-pricing-sub {
  text-align: center;
  font-family: var(--font-display);
  font-size: clamp(1.05rem, 1.25vw, 1.25rem);
  line-height: 1.55;
  font-weight: 400;
  color: var(--text-muted);
  max-width: 540px;
  margin: 0 auto var(--space-12);
}

.landing-pricing-sub strong {
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.landing-pricing-title em {
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 400;
  color: var(--accent-primary);
  font-size: 1.1em;
}

.landing-pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-5);
  max-width: clamp(680px, 44vw, 980px);
  margin: 0 auto;
}

.landing-pricing-dlc-row {
  display: flex;
  justify-content: center;
  gap: var(--space-5);
  max-width: clamp(680px, 44vw, 980px);
  margin: var(--space-5) auto 0;
}

.landing-pricing-dlc-row .landing-pricing-card {
  flex: 0 1 calc(50% - var(--space-2));
  max-width: clamp(301px, 19vw, 430px);
}

.landing-pricing-card {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01));
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-2xl);
  padding: var(--space-8) var(--space-6);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  position: relative;
  transition: all var(--transition-normal);
  backdrop-filter: var(--glass-blur);
}

.landing-pricing-card:hover {
  border-color: var(--border-default);
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.35);
}

.landing-pricing-card-highlight {
  border-color: rgba(168, 85, 247, 0.5);
  background:
    linear-gradient(180deg, rgba(168, 85, 247, 0.12) 0%, rgba(168, 85, 247, 0.02) 70%, rgba(255,255,255,0.01) 100%);
  box-shadow:
    0 0 0 1px rgba(168,85,247,0.15) inset,
    0 0 32px rgba(168,85,247,0.18);
  transform: scale(1.03);
}

.landing-pricing-card-highlight::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 10%;
  right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(168,85,247,0.9), transparent);
}

.landing-pricing-card-highlight:hover {
  transform: scale(1.04) translateY(-4px);
  border-color: rgba(168, 85, 247, 0.7);
}

.landing-pricing-card-naplex {
  border-color: rgba(var(--dlc-naplex-rgb), 0.35);
  background:
    linear-gradient(180deg, rgba(var(--dlc-naplex-rgb), 0.08) 0%, rgba(255,255,255,0.01) 100%);
}

.landing-pricing-badge {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  background: linear-gradient(135deg, #2a1a3e, #4c1d95);
  border: 1px solid rgba(168, 85, 247, 0.6);
  color: #e9d5ff;
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  padding: 0.3rem 0.75rem;
  border-radius: var(--radius-full);
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(168,85,247,0.3);
}

.landing-pricing-badge-green {
  background: #122b22;
  border-color: rgba(var(--dlc-naplex-rgb), 0.5);
  color: var(--dlc-naplex-hover);
}

.landing-pricing-name {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.22em;
  margin-bottom: var(--space-3);
}

.landing-pricing-price {
  font-family: var(--font-display);
  font-size: clamp(2.4rem, 4vw, 3.4rem);
  font-weight: var(--font-weight-black);
  letter-spacing: -0.04em;
  color: var(--text-primary);
  margin-bottom: var(--space-2);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.landing-pricing-price span {
  font-family: var(--font-display);
  font-size: 0.95rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  letter-spacing: 0;
}

.landing-pricing-price-was {
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-disabled);
  text-decoration: line-through;
  margin-right: 0.5rem;
  letter-spacing: 0;
}

.landing-pricing-effective {
  font-family: var(--font-display);
  font-size: 0.78rem;
  color: var(--text-subtle);
  margin-bottom: var(--space-4);
  font-style: italic;
}

.landing-pricing-features {
  list-style: none;
  padding: 0;
  margin: var(--space-5) 0 var(--space-6);
  flex: 1 1;
  border-top: 1px dashed var(--border-subtle);
  padding-top: var(--space-5);
}

.landing-pricing-features li {
  font-family: var(--font-display);
  font-size: 0.88rem;
  color: var(--text-secondary);
  padding: var(--space-2) 0;
  line-height: 1.5;
  position: relative;
  padding-left: 1.25rem;
  font-weight: 400;
}

.landing-pricing-features li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.7rem;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-primary);
  opacity: 0.7;
}

.landing-pricing-card-highlight .landing-pricing-features li::before { background: #c084fc; }
.landing-pricing-card-naplex .landing-pricing-features li::before { background: var(--dlc-naplex-hover); }
.landing-pricing-card-ptcb .landing-pricing-features li::before { background: var(--dlc-ptcb); }
.landing-pricing-card-mpje .landing-pricing-features li::before { background: var(--dlc-mpje-hover); }

.landing-pricing-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.85rem 0;
  font-family: var(--font-display);
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  text-decoration: none;
  border-radius: var(--radius-full);
  transition: all var(--transition-fast);
}

.landing-pricing-btn-free {
  background: rgba(107, 114, 128, 0.15);
  border: 1px solid rgba(107, 114, 128, 0.3);
  color: var(--text-secondary);
}

.landing-pricing-btn-free:hover {
  background: rgba(107, 114, 128, 0.3);
  color: var(--text-primary);
}

.landing-pricing-btn-premium {
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.landing-pricing-btn-premium:hover {
  background: rgba(59, 130, 246, 0.3);
  color: #93c5fd;
}

.landing-pricing-btn-naplex {
  background: rgba(var(--dlc-naplex-rgb), 0.15);
  border: 1px solid rgba(var(--dlc-naplex-rgb), 0.3);
  color: var(--dlc-naplex-hover);
}

.landing-pricing-btn-naplex:hover {
  background: rgba(var(--dlc-naplex-rgb), 0.3);
  color: #a7f3d0;
}

.landing-pricing-card-ptcb {
  border-color: rgba(var(--dlc-ptcb-rgb), 0.4);
  background: linear-gradient(180deg, rgba(var(--dlc-ptcb-rgb), 0.06) 0%, var(--bg-surface) 100%);
}

.landing-pricing-badge-ptcb {
  background: rgba(var(--dlc-ptcb-rgb), 0.14);
  border-color: rgba(var(--dlc-ptcb-rgb), 0.5);
  color: var(--dlc-ptcb);
}

.landing-pricing-btn-ptcb {
  background: rgba(var(--dlc-ptcb-rgb), 0.15);
  border: 1px solid rgba(var(--dlc-ptcb-rgb), 0.3);
  color: var(--dlc-ptcb);
}

.landing-pricing-btn-ptcb:hover {
  background: rgba(var(--dlc-ptcb-rgb), 0.3);
  color: var(--dlc-ptcb-hover);
}

.landing-pricing-card-mpje {
  border-color: rgba(var(--dlc-mpje-rgb), 0.4);
  background: linear-gradient(180deg, rgba(var(--dlc-mpje-rgb), 0.06) 0%, var(--bg-surface) 100%);
}

.landing-pricing-badge-mpje {
  background: #0c1a33;
  border-color: rgba(var(--dlc-mpje-rgb), 0.5);
  color: var(--dlc-mpje-hover);
}

.landing-pricing-btn-mpje {
  background: rgba(var(--dlc-mpje-rgb), 0.15);
  border: 1px solid rgba(var(--dlc-mpje-rgb), 0.3);
  color: var(--dlc-mpje-hover);
}

.landing-pricing-btn-mpje:hover {
  background: rgba(var(--dlc-mpje-rgb), 0.3);
  color: #93c5fd;
}

.landing-pricing-btn-purple {
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  color: #c084fc;
}

.landing-pricing-btn-purple:hover {
  background: rgba(168, 85, 247, 0.3);
  color: #e0b3ff;
}

.landing-pricing-footnote {
  text-align: center;
  font-size: var(--text-sm);
  color: var(--text-subtle);
  margin-top: var(--space-8);
  line-height: var(--leading-relaxed);
}

.landing-pricing-footnote a {
  color: var(--accent-primary);
  text-decoration: none;
}

.landing-pricing-footnote a:hover {
  text-decoration: underline;
}

/* Pricing stagger */
.landing-pricing-grid .landing-animate:nth-child(2) { transition-delay: 0.06s; }
.landing-pricing-grid .landing-animate:nth-child(3) { transition-delay: 0.12s; }
.landing-pricing-grid .landing-animate:nth-child(4) { transition-delay: 0.18s; }

/* ---- Bottom CTA ---- */
.landing-bottom-cta {
  padding: var(--space-16) 0 calc(var(--space-16) * 1.5);
  text-align: center;
  position: relative;
  z-index: 1;
}

.landing-bottom-cta::before {
  content: '';
  display: block;
  width: 1px;
  height: 60px;
  background: linear-gradient(180deg, transparent, var(--border-default));
  margin: 0 auto var(--space-12);
}

.landing-bottom-title {
  font-family: var(--font-display);
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: var(--font-weight-black);
  letter-spacing: -0.035em;
  line-height: 0.98;
  margin-bottom: var(--space-5);
  color: var(--text-primary);
  background: linear-gradient(180deg, #ffffff 0%, #d1d5db 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
}

.landing-bottom-title em {
  font-family: var(--font-serif);
  font-style: italic;
  font-weight: 400;
  -webkit-text-fill-color: var(--accent-primary);
}

.landing-bottom-sub {
  font-family: var(--font-display);
  font-size: clamp(1.05rem, 1.3vw, 1.2rem);
  color: var(--text-muted);
  max-width: clamp(380px, 28vw, 580px);
  margin: 0 auto var(--space-10);
  line-height: 1.55;
}

.landing-bottom-cta .landing-btn-primary {
  padding: 1.15rem 2.6rem;
  font-size: 1.05rem;
}

/* ---- Footer ---- */
.landing-footer {
  position: relative;
  z-index: 1;
  border-top: 1px solid var(--border-subtle);
  padding: var(--space-10) var(--space-6);
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(10px);
}

.landing-footer-inner {
  max-width: clamp(770px, 49vw, 1100px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  flex-wrap: wrap;
}

.landing-footer-text {
  font-family: var(--font-display);
  font-size: 0.85rem;
  color: var(--text-subtle);
  flex: 1 1;
  min-width: 280px;
  font-style: italic;
}

.landing-footer-origin-link {
  color: var(--text-muted);
  text-decoration: none;
  border-bottom: 1px dotted rgba(148, 163, 184, 0.35);
  padding-bottom: 1px;
  transition: color var(--transition-fast), border-color var(--transition-fast);
}

.landing-footer-origin-link:hover {
  color: var(--text-primary);
  border-bottom-color: rgba(148, 163, 184, 0.7);
}

.landing-footer-links {
  display: flex;
  gap: var(--space-4);
}

.landing-footer-links a {
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.landing-footer-links a:hover {
  color: var(--text-primary);
}

/* ============================================
   RESPONSIVE
   ============================================ */

@media (max-width: 768px) {
  /* Flow: vertical */
  .landing-flow-grid {
    flex-direction: column;
    align-items: center;
  }

  .landing-flow-card {
    max-width: 320px;
    width: 100%;
  }

  .landing-flow-arrow {
    transform: rotate(90deg);
    padding-top: 0;
  }

  /* Showcases: single column */
  .landing-showcase {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }

  .landing-showcase-reverse {
    direction: ltr;
  }

  /* Showcase visuals: tighter padding on tablet */
  .landing-showcase-visual {
    padding: var(--space-4);
  }

  /* Wide showcase: full-width on mobile */
  .landing-showcase-wide-header {
    flex-direction: column;
    gap: var(--space-3);
  }

  .landing-build-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-1);
  }

  .landing-build-cards {
    justify-content: center;
  }

  /* Level up: smaller gap */
  .landing-levelup-grid {
    gap: var(--space-4);
  }

  .landing-levelup-item {
    min-width: 100px;
  }

  /* Social proof: stack on mobile */
  .landing-proof-grid {
    grid-template-columns: 1fr;
  }

  /* Full-width Live panel: drop to 2x2 so 4 stats stay legible on narrow widths. */
  .landing-proof-panel-live .landing-proof-stats {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-5) var(--space-3);
    align-items: start;
  }

  /* Each stat must FILL its grid cell and center its content. Without this the
     generic \`.landing-proof-stat { width: calc(50% - space-2) }\` flex-wrap rule
     (from the <=480 block) constrains every stat to ~75px, so it left-anchors
     inside the ~165px cell (ragged) and wide values like "10,602" overflow the
     box. Higher specificity here wins over that generic rule at every width.
     (Edward 2026-06-09 — mobile live-tracker alignment.) */
  .landing-proof-panel-live .landing-proof-stat {
    width: 100%;
    align-items: center;
    text-align: center;
  }

  .landing-proof-panel {
    padding: var(--space-4);
  }

  .landing-proof-stats {
    gap: var(--space-3);
  }

  /* Pricing: 2-col on tablet, 1-col handled below */
  .landing-pricing-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-3);
  }

  .landing-pricing-dlc-row {
    flex-direction: row;
    gap: var(--space-3);
  }

  /* Footer */
  .landing-footer-inner {
    flex-direction: column;
    gap: var(--space-4);
    text-align: center;
  }

  /* Programs: smaller */
  .landing-program-tag {
    padding: 0.25rem 0.6rem;
    font-size: 0.6rem;
  }

  /* Feature section lede */
  .landing-features-lede {
    max-width: 42ch;
  }

  /* PvP mock: tighter gap */
  .landing-mock-pvp {
    gap: var(--space-3);
  }
}

@media (max-width: 480px) {
  .landing-hero {
    /* Longhands, NOT the padding shorthand. As a shorthand this rule is a later
       declaration of equal specificity, so it replaced the fitted vertical
       padding outright and pinned it at a flat 64px — silently un-solving the
       fit for this whole band (measured: padding-top 64px at 480x800 where the
       geometry had asked for 42.9px, i.e. the strip landing 21px lower than
       solved, more than the entire clearance budget).

       The phone's roomier 64px is kept and made to yield, the same shape as the
       desktop rule: identical to what was authored here at fit 1, smaller only
       when the screen cannot pay for it. */
    padding-left: var(--space-4);
    padding-right: var(--space-4);
    padding-top: min(var(--space-16), calc(64 * var(--hero-u)));
    padding-bottom: calc(64 * var(--hero-u));
  }

  .landing-hero-name {
    font-size: 3.5rem;
    letter-spacing: 0.2em;
    margin-right: -0.2em;
  }

  .landing-hero-sub {
    font-size: var(--text-base);
  }

  /* Section inner: tighter horizontal padding on phones */
  .landing-section-inner {
    padding: 0 var(--space-4);
  }

  /* Section label: less bottom margin */
  .landing-section-label {
    margin-bottom: var(--space-8);
  }

  /* Flow: full width */
  .landing-flow-card {
    max-width: 100%;
  }

  /* Showcase text: tighter spacing */
  .landing-showcase-text h3 {
    font-size: var(--text-lg);
  }

  .landing-showcase-text p {
    font-size: var(--text-sm);
  }

  /* Showcase visuals: minimal padding on phones */
  .landing-showcase-visual {
    padding: var(--space-3);
  }

  /* Concept table: prevent overflow */
  .landing-mock-concept-table {
    font-size: var(--text-xs);
  }

  .landing-mock-concept-table th,
  .landing-mock-concept-table td {
    padding: var(--space-1) var(--space-2);
  }

  /* Quiz mock: tighter */
  .landing-mock-quiz-q {
    font-size: var(--text-sm);
  }

  .landing-mock-quiz-opt {
    font-size: var(--text-xs);
    padding: var(--space-2);
    gap: var(--space-2);
  }

  /* PvP mock: stack vertically on very small screens */
  .landing-mock-pvp {
    flex-direction: column;
    gap: var(--space-2);
  }

  .landing-mock-pvp-vs {
    align-self: center;
  }

  /* Wide showcase header text */
  .landing-showcase-wide-header h3 {
    font-size: var(--text-lg);
  }

  .landing-showcase-wide-header p {
    font-size: var(--text-sm);
  }

  /* Build cards (pill cards) need nothing here any more. The card's own width
     is PillCard.css:11 (172px) and stays there: this file no longer reaches
     into a mounted component's class to resize it, and the base rule's
     var(--space-2) gap is already what this breakpoint used to re-declare. */

  /* Build meta: stack synergy label */
  .landing-build-label {
    font-size: 0.55rem;
  }

  /* Mock upkeep cards: tighter */
  .landing-mock-upkeep-name {
    font-size: var(--text-xs);
  }

  .landing-mock-upkeep-status {
    font-size: 0.6rem;
  }

  /* Level up: 2-col grid */
  .landing-levelup-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);
  }

  /* Items are max-width-capped (~112px) but grid cells are 1fr (~half-viewport).
     Without justify-self the items default-anchor to cell start, bunching all
     four 2x2 cards into the left half of the viewport and leaving the right
     half empty. Center them. */
  .landing-levelup-item {
    justify-self: center;
  }

  .landing-levelup-item:last-child:nth-child(odd) {
    grid-column: 1 / -1;
    justify-self: center;
  }

  /* Social proof: tighter padding */
  .landing-proof-panel {
    padding: var(--space-3);
  }

  .landing-proof-stats {
    gap: var(--space-2);
    flex-wrap: wrap;
    justify-content: center;
  }

  .landing-proof-stat {
    min-width: 0;
    width: calc(50% - var(--space-2));
  }

  .landing-proof-label {
    font-size: 0.6rem;
    white-space: normal;
  }

  /* Pricing: single column on small screens */
  .landing-pricing-grid {
    grid-template-columns: 1fr;
    max-width: 320px;
    margin: 0 auto;
  }

  .landing-pricing-dlc-row {
    flex-direction: column;
    align-items: center;
    max-width: 320px;
  }

  .landing-pricing-dlc-row .landing-pricing-card {
    flex: none;
    width: 100%;
    max-width: 320px;
  }

  .landing-pricing-card {
    padding: var(--space-5) var(--space-4);
  }

  /* Programs: tighter */
  .landing-program-strip {
    gap: 0.3rem;
  }

  .landing-program-tag {
    padding: 0.2rem 0.5rem;
    font-size: 0.55rem;
  }

  /* Method tags: smaller */
  .landing-method-tag {
    font-size: 0.6rem;
    padding: 0.25rem 0.55rem;
    letter-spacing: 0.12em;
  }

  /* Sentiment text: smaller on mobile */
  .landing-sentiment {
    font-size: var(--text-base);
    padding: var(--space-6) var(--space-4);
  }

  /* Sections: reduce vertical padding */
  .landing-features {
    padding: var(--space-10) 0;
  }

  .landing-showcase {
    margin-bottom: var(--space-10);
  }

  .landing-showcase-wide {
    margin-bottom: var(--space-10);
  }

  /* Bottom CTA: tighter */
  .landing-bottom-title {
    font-size: clamp(1.25rem, 5vw, 1.75rem);
  }

  .landing-bottom-sub {
    font-size: var(--text-base);
  }

  /* Footer: tighter */
  .landing-footer {
    padding: var(--space-6) var(--space-4);
  }

  .landing-footer-links {
    gap: var(--space-3);
  }
}

/* ============================================
   ACCESSIBILITY
   ============================================ */

@media (prefers-reduced-motion: reduce) {
  .landing-animate {
    opacity: 1;
    transform: none;
    transition: none;
  }

  /* Skip the intro sequence entirely and land on its finished state. */
  .landing-hero-logo .dbl-line,
  .landing-hero-logo .dbl-sweep {
    stroke-dashoffset: 0;
    animation: none;
  }

  .landing-hero-logo .dbl-flood {
    opacity: 1;
    animation: none;
  }

  .landing-hero-script .dbs-ink {
    stroke-dashoffset: 0;
    animation: none;
  }

  .landing-hero-script .dbs-nib {
    animation: none;
    opacity: 0;
  }



  .landing-glow {
    animation: none;
  }

  .landing-btn-primary:hover,
  .landing-flow-card:hover {
    transform: none;
  }
}

/* ============================================
   FOUNDERS DISCOUNT (revertible — see config/foundersDiscount.js)
   ============================================ */

.landing-pricing-card { position: relative; }

.landing-founders-flag {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 0.6rem 0;
  padding: 0.28rem 0.65rem 0.28rem 0.5rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid;
  font-family: var(--font-primary, 'Inter', system-ui, sans-serif);
  white-space: nowrap;
  align-self: flex-start;
}

.landing-founders-flag-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: currentColor;
  display: inline-block;
  box-shadow: 0 0 6px currentColor;
  animation: landing-founders-pulse 2.4s ease-in-out infinite;
}

@keyframes landing-founders-pulse {
  0%, 100% { opacity: 0.55; transform: scale(0.85); }
  50%      { opacity: 1;    transform: scale(1.1); }
}

.landing-founders-flag-naplex {
  background: rgba(var(--dlc-naplex-rgb), 0.12);
  border-color: rgba(var(--dlc-naplex-rgb), 0.4);
  color: #4ade80;
}

.landing-founders-flag-mpje {
  background: rgba(var(--dlc-mpje-rgb), 0.12);
  border-color: rgba(var(--dlc-mpje-rgb), 0.4);
  color: var(--dlc-mpje-hover);
}

.landing-pricing-price-was {
  display: inline-block;
  font-size: 0.55em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  text-decoration: line-through;
  text-decoration-color: rgba(239, 68, 68, 0.6);
  text-decoration-thickness: 1.5px;
  margin-right: 0.4rem;
  vertical-align: 0.55em;
  letter-spacing: -0.01em;
}

/* Deleted-account confirmation banner — shown when ?deleted=1 is in the URL */
.landing-deleted-banner {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem 0.7rem 1rem;
  border-radius: 999px;
  background: rgba(74, 222, 128, 0.12);
  border: 1px solid rgba(74, 222, 128, 0.32);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4), 0 0 24px rgba(74, 222, 128, 0.18);
  color: #d1fae5;
  font-size: 13px;
  font-weight: 500;
  animation: landing-deleted-banner-in 240ms cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes landing-deleted-banner-in {
  from { opacity: 0; transform: translate(-50%, -16px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
}

.landing-deleted-banner-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(74, 222, 128, 0.22);
  color: #4ade80;
  flex-shrink: 0;
}

.landing-deleted-banner-text {
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.landing-deleted-banner-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
  margin-left: 0.25rem;
}

.landing-deleted-banner-close:hover {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
}

@media (max-width: 640px) {
  .landing-deleted-banner-text {
    white-space: normal;
    max-width: 220px;
    font-size: 12px;
    line-height: 1.35;
  }
}

/* ============ Playable warfarin Drug-Lab — no-signup combat preview ============ */
.landing-demo-section {
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 14px;
  padding: 48px 0;
}
/* Sits directly above the stage label; the [program] word is swapped by the hero
   program picker, so the line is what re-frames the demo per audience. */
.landing-druglab-stage-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(22px, 2.6vw, 34px);
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.01em;
  line-height: 1.15;
  color: var(--text-primary);
  text-align: center;
}
.landing-druglab-stage-label {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(239, 68, 68, 0.85);
  text-align: center;
}
/* EXACT logged-in map size: the dashboard map viewport computes to
   (100vw − 280px sidebar) × (100vh − 196px chrome). The preview drops the
   sidebar/bars but keeps the map at the same real size, centered. The element
   ALSO carries \`.deck-map-area\` so BossMapModal's portal target lands here and
   combat renders confined to the stage (not the <body> fallback). The compound
   selector re-asserts the frame's own visual treatment so the dashboard
   .deck-map-area base chrome can't leak in regardless of load order. */
.landing-druglab-stage-frame,
.landing-druglab-stage-frame.deck-map-area {
  position: relative;
  display: block;
  width: calc(100vw - 280px);
  height: calc(100vh - 196px);
  min-height: 460px;
  margin: 0 auto;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(239, 68, 68, 0.06), transparent 60%),
    rgba(255, 255, 255, 0.02);
  box-shadow: 0 30px 80px -30px rgba(0, 0, 0, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  overflow: hidden;
  overflow-clip-margin: 0;
}
.landing-demo-cta {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}
@media (max-width: 768px) {
  .landing-druglab-stage-frame,
  .landing-druglab-stage-frame.deck-map-area {
    width: calc(100vw - 32px);
    height: calc(100vh - 160px);
    min-height: 420px;
  }
}

/* ===== Mobile corner-icon treatment (Edward 2026-06-12) =====
   On phones, the centered icon tiles that sit ABOVE card headings burn a full
   row of vertical space per card. At <=768px every such icon docks to the
   card's top-right corner and the text reclaims the column. Desktop keeps the
   centered tiles. Appended at end of file so these override the earlier base
   and media rules by order. */
@media (max-width: 768px) {
  /* -- 3-step flow cards: icon mirrors the step-num chip (top-left) in the
        top-right corner; the AUTO badge drops below the docked icon so the
        right edge reads as one corner cluster. 56px top padding clears the
        docked 40px icon; the heading no longer waits below a centered icon
        row (text used to start ~116px down: 48 pad + 48 icon + 20 margin). */
  .landing-flow-card {
    padding-top: 56px;
  }

  .landing-flow-icon {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    margin: 0;
  }

  .landing-flow-icon::after {
    inset: -4px;
    border-radius: 14px;
  }

  .landing-flow-auto-badge {
    top: 64px;
    right: 14px;
  }

  /* -- Level-up grid items: icon floats into the top-right corner; the label
        shares its row and the description wraps beneath it. Block flow (not
        flex) so the float shortens line boxes naturally. */
  .landing-levelup-item {
    display: block;
    text-align: left;
  }

  .landing-levelup-icon {
    float: right;
    width: 38px;
    height: 38px;
    margin: 0 0 var(--space-2) var(--space-3);
  }

  .landing-levelup-icon::after {
    inset: -4px;
    border-radius: 13px;
  }

  .landing-levelup-icon svg {
    width: 17px;
    height: 17px;
  }

  .landing-levelup-label {
    display: flex;
    align-items: center;
    min-height: 38px;
    margin-bottom: var(--space-2);
  }

  .landing-levelup-desc {
    display: block;
  }

}
`, "",{"version":3,"sources":["webpack://./src/pages/Landing.css"],"names":[],"mappings":"AAAA;;;iDAGiD;;AAEjD,qBAAqB;AACrB;EACE,iBAAiB;EACjB,0BAA0B;EAC1B,0BAA0B;EAC1B,gCAAgC;EAChC,kBAAkB;EAClB,kBAAkB;;EAElB;;;;;;;;;;;;;;;;;;gEAkB8D;EAC9D,iCAAiC;EACjC,6BAA6B;EAC7B,6BAA6B;EAC7B,4BAA4B;AAC9B;;AAEA,+DAA+D;AAC/D;EACE,WAAW;EACX,eAAe;EACf,QAAQ;EACR,oBAAoB;EACpB,UAAU;EACV;kFACgF;EAChF,wBAAwB;EACxB,YAAY;EACZ,uBAAuB;AACzB;;AAEA;;;;;;;;;;;;;+DAa+D;AAC/D;EACE,eAAe;EACf,QAAQ;EACR,oBAAoB;EACpB,UAAU;EACV,gBAAgB;EAChB,gCAAgC;AAClC;;AAEA;;;;;;;;+DAQ+D;AAC/D;EACE,kBAAkB;EAClB,QAAQ;EACR,oBAAoB;EACpB;;;8BAG4B;EAC5B,4BAA4B;EAC5B;8CAC4C;EAC5C,gEAAgE;AAClE;;AAEA;;;;4EAI4E;AAC5E;EACE,WAAW;EACX,kBAAkB;EAClB,QAAQ;EACR,oBAAoB;EACpB;;;;;;;;;;+BAU6B;AAC/B;;AAEA;EACE,cAAc,gBAAgB,EAAE;AAClC;;AAEA,+BAA+B;AAC/B;EACE,UAAU;EACV,2BAA2B;EAC3B,oGAAoG;AACtG;;AAEA;EACE,UAAU;EACV,wBAAwB;AAC1B;;AAEA;;iDAEiD;;AAEjD;EACE,kBAAkB;EAClB,UAAU;EACV,aAAa;EACb,uBAAuB;EACvB,uBAAuB;EACvB,kBAAkB;;EAElB;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;gDAyD8C;EAC9C,gBAAgB;;EAEhB,2EAA2E;EAC3E,mDAAmD;EACnD;uBACqB;EACrB,oDAAoD;EACpD;;;2DAGyD;EACzD,mDAAmD;EACnD;6EAC2E;EAC3E,mBAAmB;;EAEnB;4DAC0D;EAC1D,gBAAgB;EAChB;;;;;6DAK2D;EAC3D,0FAA0F;;EAE1F;uEACqE;EACrE;;mCAEiC;EACjC,0DAA0D;AAC5D;;AAEA;;;;;wDAKwD;AACxD;EACE,gBAAgB,iBAAiB,EAAE;AACrC;;AAEA;;;;;;;;;2CAS2C;AAC3C;EACE,gBAAgB,oBAAoB,EAAE,GAAG,2CAA2C;AACtF;;AAEA;EACE,oCAAoC;EACpC,WAAW;EACX,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA,8CAA8C;AAC9C;EACE,kBAAkB;EAClB,QAAQ;EACR,SAAS;EACT,gCAAgC;EAChC,gCAAgC;EAChC,iCAAiC;EACjC,oBAAoB;EACpB,UAAU;EACV,aAAa;EACb,4BAA4B;AAC9B;;AAEA;EACE,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,cAAc;EACd,8CAA8C;AAChD;;AAEA;EACE,OAAO,uBAAuB,EAAE;EAChC,KAAK,yBAAyB,EAAE;AAClC;;AAEA,sDAAsD;AACtD;EACE,kBAAkB;EAClB,MAAM;EACN,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,6BAA6B;EAC7B,iBAAiB;EACjB,sBAAsB;EACtB,yBAAyB;EACzB,yBAAyB;EACzB,cAAc;AAChB;;AAEA,2BAA2B,OAAO,EAAE;AACpC,4BAA4B,QAAQ,EAAE;;AAEtC;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,mBAAmB;EACnB,uCAAuC;EACvC,kDAAkD;AACpD;;AAEA;EACE,WAAW,UAAU,EAAE,mBAAmB,EAAE;EAC5C,MAAM,aAAa,EAAE,sBAAsB,EAAE;AAC/C;;AAEA;EACE,WAAW;EACX,WAAW;EACX,qEAAqE;AACvE;;AAEA;EACE,sEAAsE;AACxE;;AAEA,0DAA0D;AAC1D;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,MAAM;EACN;;wCAEsC;EACtC,uCAAuC,GAAG,uBAAuB;EACjE,gBAAgB;EAChB,mCAAmC;EACnC,sCAAsC;EACtC,iCAAiC;EACjC,2BAA2B;AAC7B;;AAEA;4EAC4E;AAC5E;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,wBAAwB;EACxB,iCAAiC;EACjC,gCAAgC;EAChC,kBAAkB;EAClB,wCAAwC;EACxC,sBAAsB;EACtB,yBAAyB;EACzB,cAAc;EACd,6BAA6B;EAC7B,uBAAuB;EACvB,eAAe;EACf,sCAAsC;AACxC;;AAEA;EACE,mBAAmB,EAAE,qDAAqD;EAC1E,yBAAyB;EACzB,cAAc;EACd,+EAA+E;AACjF;;AAEA;EACE,WAAW;EACX,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,mBAAmB;EACnB,oBAAoB;AACtB;;AAEA;EACE,uBAAuB;EACvB,yBAAyB;EACzB,yBAAyB;EACzB,aAAa;AACf;;AAEA;;EAEE,qCAAqC;EACrC,0BAA0B;EAC1B,UAAU;AACZ;;AAEA;kEACkE;AAClE;EACE,gCAAgC;EAChC,uCAAuC;EACvC,UAAU;AACZ;;AAEA;EACE,kBAAkB;EAClB,sCAAsC;EACtC,qBAAqB;EACrB,yBAAyB;EACzB,2BAA2B;EAC3B,YAAY;EACZ,wBAAwB;EACxB,sCAAsC;EACtC,kBAAkB;AACpB;;AAEA;;;;;;;;;;;;iDAYiD;AACjD;EACE,cAAc;EACd;;;kEAGgE;EAChE,gCAAgC;EAChC,YAAY;EACZ,+DAA+D;EAC/D,kBAAkB;AACpB;;AAEA;oEACoE;AACpE;;EAEE,mBAAmB;EACnB,oBAAoB;EACpB,qFAAqF;AACvF;;AAEA;2EAC2E;AAC3E;gCACgC,6CAA6C,EAAE;AAC/E;;gCAEgC,2CAA2C,EAAE;;AAE7E,gCAAgC,eAAe,KAAK,gBAAgB,EAAE;AACtE,gCAAgC,kBAAkB,EAAE,gBAAgB,EAAE;AACtE;0EAC0E;AAC1E,gCAAgC,kBAAkB,EAAE,gBAAgB,EAAE;AACtE,gCAAgC,kBAAkB,EAAE,gBAAgB,EAAE;AACtE,gCAAgC,kBAAkB,EAAE,gBAAgB,EAAE;;AAEtE;6DAC6D;AAC7D,gCAAgC,kBAAkB,EAAE,gBAAgB,EAAE;;AAEtE;EACE,KAAK,oBAAoB,EAAE;AAC7B;;AAEA;;;mDAGmD;AACnD;EACE,UAAU;EACV,8DAA8D;AAChE;;AAEA;EACE,KAAK,UAAU,EAAE;AACnB;;AAEA,wBAAwB;AACxB;EACE,SAAS;EACT,kBAAkB;EAClB,kBAAkB;EAClB,cAAc;EACd;wEACsE;EACtE,sDAAsD;AACxD;;AAEA;;;8EAG8E;AAC9E;EACE,cAAc;EACd,wCAAwC;EACxC,YAAY;EACZ,cAAc;EACd,iBAAiB;;EAEjB;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;2EA+ByE;EACzE,8BAA8B;AAChC;;AAEA;EACE,gCAAgC;EAChC,mBAAmB;EACnB,qCAAqC;EACrC,wBAAwB;EACxB,yCAAyC;AAC3C;;AAEA;EACE,8BAA8B;EAC9B,oBAAoB;EACpB,oCAAoC;EACpC,wBAAwB;AAC1B;;;AAGA;;;;;;;;6CAQ6C;AAC7C;EACE,eAAe;EACf,UAAU;AACZ;;AAEA;;EAEE,uDAAuD;EACvD,oEAAoE;EACpE,0DAA0D;EAC1D,6BAA6B;AAC/B;;AAEA;EACE,mBAAmB;EACnB,oBAAoB;EACpB,mCAAmC;AACrC;;;AAGA;EACE,KAAK,oBAAoB,EAAE;AAC7B;;AAEA;;;;;;;6EAO6E;AAC7E;EACE,aAAa;EACb,UAAU;EACV,uBAAuB;EACvB;wDACsD;AACxD;;AAEA,iCAAiC,aAAa,EAAE,kBAAkB,EAAE;AACpE,iCAAiC,kBAAkB,EAAE,kBAAkB,EAAE;AACzE,iCAAiC,kBAAkB,EAAE,kBAAkB,EAAE;AACzE,iCAAiC,kBAAkB,EAAE,kBAAkB,EAAE;AACzE,iCAAiC,gBAAgB,EAAE,iBAAiB,EAAE;AACtE,iCAAiC,kBAAkB,EAAE,kBAAkB,EAAE;AACzE,iCAAiC,gBAAgB,EAAE,kBAAkB,EAAE;AACvE,iCAAiC,kBAAkB,EAAE,kBAAkB,EAAE;AACzE,iCAAiC,gBAAgB,EAAE,kBAAkB,EAAE;AACvE,kCAAkC,kBAAkB,EAAE,kBAAkB,EAAE;AAC1E,kCAAkC,kBAAkB,EAAE,kBAAkB,EAAE;AAC1E,kCAAkC,gBAAgB,EAAE,gBAAgB,EAAE;AACtE,kCAAkC,gBAAgB,EAAE,eAAe,EAAE;AACrE,kCAAkC,eAAe,EAAE,gBAAgB,EAAE;;AAErE;EACE,KAAK,gDAAgD,EAAE,UAAU,EAAE;EACnE,KAAK,iDAAiD,EAAE,UAAU,EAAE;EACpE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,iDAAiD,EAAE,UAAU,EAAE;EACrE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,iDAAiD,EAAE,UAAU,EAAE;EACrE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,iDAAiD,EAAE,UAAU,EAAE;EACrE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,OAAO,+CAA+C,EAAE,UAAU,EAAE;AACtE;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,iDAAiD,EAAE,UAAU,EAAE;EACpE,KAAK,oDAAoD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,OAAO,qDAAqD,EAAE,UAAU,EAAE;AAC5E;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,oDAAoD,EAAE,UAAU,EAAE;EACvE,KAAK,qDAAqD,EAAE,UAAU,EAAE;EACxE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,OAAO,kDAAkD,EAAE,UAAU,EAAE;AACzE;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,qDAAqD,EAAE,UAAU,EAAE;EACxE,KAAK,uDAAuD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,OAAO,iDAAiD,EAAE,UAAU,EAAE;AACxE;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,kDAAkD,EAAE,UAAU,EAAE;EACrE,KAAK,mDAAmD,EAAE,UAAU,EAAE;EACtE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,OAAO,kDAAkD,EAAE,UAAU,EAAE;AACzE;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,mDAAmD,EAAE,UAAU,EAAE;EACtE,KAAK,qDAAqD,EAAE,UAAU,EAAE;EACxE,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,OAAO,qDAAqD,EAAE,UAAU,EAAE;AAC5E;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,uDAAuD,EAAE,UAAU,EAAE;EAC1E,KAAK,wDAAwD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,OAAO,qDAAqD,EAAE,UAAU,EAAE;AAC5E;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,kDAAkD,EAAE,UAAU,EAAE;EACrE,KAAK,mDAAmD,EAAE,UAAU,EAAE;EACtE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,OAAO,kDAAkD,EAAE,UAAU,EAAE;AACzE;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,oDAAoD,EAAE,UAAU,EAAE;EACvE,KAAK,qDAAqD,EAAE,UAAU,EAAE;EACxE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,OAAO,oDAAoD,EAAE,UAAU,EAAE;AAC3E;;AAEA,yCAAyC,oCAAoC,EAAE;;AAE/E;EACE,KAAK,kDAAkD,EAAE,UAAU,EAAE;EACrE,KAAK,mDAAmD,EAAE,UAAU,EAAE;EACtE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,kDAAkD,EAAE,UAAU,EAAE;EACtE,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,OAAO,uDAAuD,EAAE,UAAU,EAAE;AAC9E;;AAEA,0CAA0C,qCAAqC,EAAE;;AAEjF;EACE,KAAK,oDAAoD,EAAE,UAAU,EAAE;EACvE,KAAK,sDAAsD,EAAE,UAAU,EAAE;EACzE,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,OAAO,oDAAoD,EAAE,UAAU,EAAE;AAC3E;;AAEA,0CAA0C,qCAAqC,EAAE;;AAEjF;EACE,KAAK,qDAAqD,EAAE,UAAU,EAAE;EACxE,KAAK,uDAAuD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,OAAO,kDAAkD,EAAE,UAAU,EAAE;AACzE;;AAEA,0CAA0C,qCAAqC,EAAE;;AAEjF;EACE,KAAK,kDAAkD,EAAE,UAAU,EAAE;EACrE,KAAK,kDAAkD,EAAE,UAAU,EAAE;EACrE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,mDAAmD,EAAE,UAAU,EAAE;EACvE,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,sDAAsD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,qDAAqD,EAAE,UAAU,EAAE;EACzE,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,OAAO,oDAAoD,EAAE,UAAU,EAAE;AAC3E;;AAEA,0CAA0C,qCAAqC,EAAE;;AAEjF;EACE,KAAK,mDAAmD,EAAE,UAAU,EAAE;EACtE,KAAK,uDAAuD,EAAE,UAAU,EAAE;EAC1E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,oDAAoD,EAAE,UAAU,EAAE;EACxE,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,yDAAyD,EAAE,UAAU,EAAE;EAC7E,MAAM,uDAAuD,EAAE,UAAU,EAAE;EAC3E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,MAAM,wDAAwD,EAAE,UAAU,EAAE;EAC5E,OAAO,gDAAgD,EAAE,UAAU,EAAE;AACvE;;AAEA,0CAA0C,qCAAqC,EAAE;;;;;AAKjF;+DAC+D;AAC/D;EACE,sBAAsB;EACtB,8BAA8B;EAC9B,iBAAiB;EACjB,oCAAoC;EACpC,gBAAgB;EAChB,kBAAkB;EAClB;;wDAEsD;EACtD,0EAA0E;EAC1E,6BAA6B;EAC7B,qBAAqB;EACrB,oCAAoC;EACpC,kBAAkB;EAClB,kDAAkD;AACpD;;AAEA,iCAAiC;AACjC;EACE,gCAAgC;EAChC;yEACuE;EACvE,0CAA0C;EAC1C,wCAAwC;EACxC,sBAAsB;EACtB,yBAAyB;EACzB,wBAAwB;EACxB,gBAAgB;EAChB,oCAAoC,GAAG,uBAAuB;EAC9D,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,uBAAuB;AACzB;;AAEA;iEACiE;AACjE;EACE,cAAc;EACd,kBAAkB;EAClB,UAAU;EACV;;;;qCAImC;AACrC;;AAEA,gDAAgD;AAChD;EACE,WAAW;EACX,kBAAkB;EAClB,kBAAkB;EAClB,WAAW;EACX,oBAAoB;EACpB;;;;;GAKC;EACD,iBAAiB;AACnB;;AAEA;EACE,gCAAgC;EAChC;;;;kEAIgE;EAChE,0CAA0C;EAC1C,iBAAiB;EACjB,gBAAgB;EAChB,wBAAwB;EACxB;8EAC4E;EAC5E,oCAAoC;EACpC,8DAA8D,GAAG,oCAAoC;EACrG,kBAAkB;AACpB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,iBAAiB;EACjB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,mBAAmB;EACnB,eAAe;EACf;;;;;;;;4CAQ0C;EAC1C,4CAA4C;AAC9C;;AAEA;EACE,cAAc;EACd,oBAAoB;EACpB,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA,sBAAsB;AACtB;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,oBAAoB;EACpB;;qEAEmE;EACnE,gDAAgD;EAChD,6BAA6B;EAC7B,iCAAiC;EACjC,gCAAgC;EAChC,kBAAkB;EAClB,wCAAwC;EACxC,sBAAsB;EACtB,qBAAqB;EACrB,yBAAyB;EACzB,sCAAsC;EACtC,kBAAkB;EAClB,gBAAgB;EAChB;;;;uFAIqF;AACvF;;AAEA;EACE,YAAY;EACZ,gCAAgC;EAChC,gBAAgB;EAChB,4CAA4C;AAC9C;;AAEA;EACE,wBAAwB;EACxB,2BAA2B;EAC3B;;;;uFAIqF;AACvF;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,4BAA4B;EAC5B,qBAAqB;EACrB,gCAAgC;EAChC,kBAAkB;EAClB,sCAAsC;EACtC,sBAAsB;EACtB,yBAAyB;EACzB,sCAAsC;EACtC,oBAAoB;EACpB,uCAAuC;EACvC,iCAAiC;EACjC,mCAAmC;AACrC;;AAEA;EACE,0BAA0B;EAC1B,iCAAiC;EACjC,kCAAkC;EAClC,2BAA2B;AAC7B;;AAEA;;;qFAGqF;AACrF;EACE,uBAAuB;EACvB,oBAAoB;EACpB,gCAAgC;EAChC,kBAAkB;EAClB,sCAAsC;AACxC;;AAEA,0EAA0E;AAC1E;EACE,kBAAkB;EAClB,sCAAsC;EACtC,qBAAqB;EACrB,yBAAyB;EACzB,2BAA2B;EAC3B,YAAY;EACZ,wBAAwB;EACxB,sCAAsC;EACtC,kBAAkB;EAClB,cAAc;AAChB;;AAEA,qCAAqC;AACrC;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,WAAW;EACX;;;gFAG8E;EAC9E,oCAAoC;EACpC,oCAAoC,GAAG,uBAAuB;AAChE;;AAEA;;EAEE,WAAW;EACX,SAAO;EACP,WAAW;EACX,gCAAgC;AAClC;;AAEA;EACE,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;AAC3B;;AAEA;EACE,oCAAoC,GAAG,uBAAuB;EAC9D,kCAAkC;EAClC,aAAa;EACb,uBAAuB;AACzB;;AAEA;EACE,yBAAyB;EACzB,4BAA4B;EAC5B,0BAA0B;AAC5B;;AAEA,2BAA2B;AAC3B;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,2BAA2B;EAC3B,oCAAoC;EACpC,qBAAqB;EACrB,yBAAyB;AAC3B;;AAEA,8BAA8B;AAC9B;EACE,kBAAkB;EAClB,UAAU;EACV,qCAAqC;EACrC,cAAc;EACd,yBAAyB;AAC3B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,SAAS;EACT,kBAAkB;EAClB,6BAA6B;EAC7B,kBAAkB;EAClB,sCAAsC;EACtC,sBAAsB;EACtB,yBAAyB;EACzB,yBAAyB;EACzB,8BAA8B;AAChC;;AAEA;;EAEE,WAAW;EACX,gCAAgC;EAChC,WAAW;EACX,mFAAmF;AACrF;;AAEA;EACE,sEAAsE;AACxE;;AAEA;EACE,uEAAuE;AACzE;;AAEA;;iDAEiD;;AAEjD;EACE,0CAA0C;EAC1C,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,wCAAwC;EACxC,oCAAoC;EACpC,uBAAuB;EACvB,0BAA0B;EAC1B,qCAAqC;EACrC,8BAA8B;EAC9B,iBAAiB;AACnB;;AAEA;EACE,WAAW;EACX,cAAc;EACd,WAAW;EACX,WAAW;EACX,iCAAiC;EACjC,6BAA6B;EAC7B,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,oBAAoB;EACpB,uBAAuB;EACvB,mBAAmB;EACnB,iBAAiB;EACjB,gBAAgB;EAChB,cAAc;AAChB;;AAEA;EACE,SAAO;EACP,oCAAoC;EACpC,oCAAoC;EACpC,qCAAqC;EACrC;qFACmF;EACnF,sCAAsC;EACtC,gCAAgC;EAChC,sDAAsD;EACtD,kBAAkB;EAClB,kCAAkC;EAClC,wCAAwC;EACxC,kBAAkB;EAClB,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA,8BAA8B;AAC9B;EACE,WAAW;EACX,kBAAkB;EAClB,SAAS;EACT,SAAS;EACT,2BAA2B;EAC3B,WAAW;EACX,YAAY;EACZ,oBAAoB;EACpB,WAAW;EACX,kBAAkB;EAClB,YAAY;EACZ,4CAA4C;AAC9C;;AAEA;EACE,iFAAiF;AACnF;AACA;EACE,kFAAkF;AACpF;AACA;EACE,kFAAkF;AACpF;;AAEA,iCAAiC;AACjC;EACE,WAAW;EACX,kBAAkB;EAClB,MAAM;EACN,SAAS;EACT,2BAA2B;EAC3B,UAAU;EACV,WAAW;EACX,oBAAoB;AACtB;;AAEA,yCAAyC,iFAAiF,EAAE;AAC5H,yCAAyC,kFAAkF,EAAE;AAC7H,yCAAyC,kFAAkF,EAAE;;AAE7H;EACE,kCAAkC;EAClC,2BAA2B;EAC3B,uCAAuC;AACzC;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,UAAU;EACV,+BAA+B;EAC/B,gCAAgC;EAChC,kBAAkB;EAClB,6BAA6B;EAC7B,iBAAiB;EACjB,sCAAsC;EACtC,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,iBAAiB;EACjB,2BAA2B;EAC3B,wCAAwC;AAC1C;;AAEA,sDAAsD;AACtD;EACE,gCAAgC;EAChC,kCAAkC;EAClC,cAAc;AAChB;AACA;EACE,iCAAiC;EACjC,mCAAmC;EACnC,cAAc;AAChB;AACA;EACE,iCAAiC;EACjC,mCAAmC;EACnC,cAAc;AAChB;;AAEA,qCAAqC;AACrC;EACE,uCAAuC;EACvC,8CAA8C;EAC9C,4BAA4B;AAC9B;AACA;EACE,iCAAiC;EACjC,sCAAsC;EACtC,cAAc;AAChB;AACA;EACE,iCAAiC;EACjC,sCAAsC;EACtC,cAAc;AAChB;;AAEA;EACE,6BAA6B;EAC7B,8BAA8B;EAC9B,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,6BAA6B;EAC7B,6BAA6B;EAC7B,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,WAAW;EACX,mBAAmB;EACnB,+BAA+B;EAC/B,aAAa;AACf;;AAEA,4BAA4B,gCAAgC,EAAE,4BAA4B,EAAE,kCAAkC,EAAE;AAChI,0BAA0B,iCAAiC,EAAE,cAAc,EAAE,mCAAmC,EAAE;AAClH,4BAA4B,iCAAiC,EAAE,cAAc,EAAE,mCAAmC,EAAE;;AAEpH;EACE,kBAAkB;EAClB,SAAS;EACT,WAAW;EACX,6BAA6B;EAC7B,iBAAiB;EACjB,sCAAsC;EACtC,sBAAsB;EACtB,yBAAyB;EACzB,cAAc;EACd,gCAAgC;EAChC,sCAAsC;EACtC,uBAAuB;EACvB,kBAAkB;EAClB,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,WAAW;EACX,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,mBAAmB;EACnB,2BAA2B;AAC7B;;AAEA;EACE,cAAc;EACd,gCAAgC;EAChC,mCAAmC;AACrC;;AAEA;EACE,gCAAgC;EAChC,wCAAwC;EACxC,oCAAoC;EACpC,uBAAuB;EACvB,6BAA6B;EAC7B,0BAA0B;AAC5B;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,iBAAiB;EACjB,wBAAwB;EACxB,SAAO;EACP,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,kBAAkB;EAClB,2BAA2B;EAC3B,cAAc;EACd,YAAY;AACd;;AAEA,iBAAiB;AACjB,mDAAmD,oBAAoB,EAAE;AACzE,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;;AAE5E;;iDAEiD;;AAEjD;EACE,0BAA0B;EAC1B,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,oBAAoB;EACpB,mBAAmB;EACnB,8BAA8B;AAChC;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,gCAAgC;EAChC,sCAAsC;EACtC,oCAAoC;EACpC,wBAAwB;EACxB,gBAAgB;EAChB,0BAA0B;EAC1B,SAAS;AACX;;AAEA;EACE,gCAAgC;EAChC,yCAAyC;EACzC,gBAAgB;EAChB,wBAAwB;EACxB,gBAAgB;EAChB,SAAS;AACX;;AAEA;EACE,0BAA0B;EAC1B,sCAAsC;EACtC,gCAAgC;EAChC,uBAAuB;EACvB,kCAAkC;EAClC,gBAAgB;AAClB;;AAEA,qBAAqB;AACrB,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;AAC5E,mDAAmD,uBAAuB,EAAE;;AAE5E,2BAA2B;AAC3B;EACE,WAAW;EACX,YAAY;EACZ,cAAc;AAChB;;AAEA,gCAAgC;AAChC;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,cAAc;EACd,yBAAyB;EACzB,wCAAwC;AAC1C;;AAEA;EACE,WAAW;EACX,yBAAyB;EACzB,yBAAyB;AAC3B;;AAEA;EACE,gBAAgB;EAChB,sCAAsC;EACtC,kBAAkB;EAClB,wCAAwC;EACxC,qCAAqC;EACrC,yBAAyB;EACzB,yBAAyB;EACzB,6CAA6C;AAC/C;;AAEA;EACE,sCAAsC;EACtC,wBAAwB;EACxB,+CAA+C;AACjD;;AAEA;EACE,wCAAwC;EACxC,4BAA4B;EAC5B,iBAAiB;EACjB,oCAAoC;EACpC,yBAAyB;AAC3B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,SAAO;EACP,8BAA8B;EAC9B,kCAAkC;EAClC,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,UAAU;EACV,YAAY;EACZ,oDAAoD;EACpD,kBAAkB;AACpB;;AAEA;EACE,kBAAkB;EAClB,wCAAwC;EACxC,yBAAyB;AAC3B;;AAEA,wBAAwB;AACxB;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,2BAA2B;EAC3B,wCAAwC;EACxC,0BAA0B;EAC1B,mCAAmC;AACrC;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,sCAAsC;EACtC,+BAA+B;EAC/B,yBAAyB;EACzB,wBAAwB;EACxB,sCAAsC;EACtC,uBAAuB;AACzB;;AAEA;EACE,iCAAiC;EACjC,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA;EACE,gCAAgC;EAChC,iCAAiC;EACjC,kBAAkB;EAClB,yCAAyC;EACzC,cAAc;AAChB;;AAEA;EACE,qBAAqB;EACrB,iEAAiE;AACnE;;AAEA;EACE,iBAAiB;AACnB;;AAEA;;iDAEiD;;AAEjD;EACE,0BAA0B;EAC1B,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,aAAa;EACb,qCAAqC;EACrC,MAAM;EACN,sCAAsC;EACtC,gCAAgC;EAChC,gBAAgB;EAChB;2EACyE;EACzE,kCAAkC;AACpC;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,kBAAkB;EAClB,mBAAmB;EACnB,sCAAsC;EACtC,YAAY;EACZ,eAAe;EACf,kBAAkB;EAClB,+CAA+C;AACjD;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,QAAQ;EACR,QAAQ;EACR,WAAW;EACX,UAAU;EACV,mFAAmF;AACrF;;AAEA;EACE,mCAAmC;AACrC;;AAEA;EACE,+BAA+B;EAC/B,gCAAgC;EAChC,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,6BAA6B;EAC7B,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,WAAW;EACX,mBAAmB;EACnB,+BAA+B;EAC/B,aAAa;AACf;;AAEA,cAAc;AACd;EACE,iCAAiC;EACjC,sCAAsC;EACtC,cAAc;AAChB;;AAEA,uBAAuB;AACvB;EACE,iCAAiC;EACjC,sCAAsC;EACtC,cAAc;AAChB;;AAEA,uBAAuB;AACvB;EACE,kCAAkC;EAClC,uCAAuC;EACvC,cAAc;AAChB;;AAEA,uBAAuB;AACvB;EACE,gCAAgC;EAChC,qCAAqC;EACrC,cAAc;AAChB;;AAEA,kBAAkB;AAClB;EACE,iCAAiC;EACjC,sCAAsC;EACtC,cAAc;AAChB;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,oCAAoC;EACpC,wBAAwB;EACxB,0BAA0B;AAC5B;;AAEA;EACE,gCAAgC;EAChC,iBAAiB;EACjB,wBAAwB;EACxB,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA,qBAAqB;AACrB,sDAAsD,uBAAuB,EAAE;AAC/E,sDAAsD,uBAAuB,EAAE;AAC/E,sDAAsD,uBAAuB,EAAE;AAC/E,sDAAsD,uBAAuB,EAAE;;AAE/E,sCAAsC;AACtC;EACE,kBAAkB;EAClB,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,uCAAuC;EACvC,wBAAwB;EACxB,iBAAiB;EACjB,4BAA4B;EAC5B,uCAAuC;EACvC,kBAAkB;EAClB,UAAU;EACV,oCAAoC;EACpC,cAAc;AAChB;;AAEA;;EAEE,WAAW;EACX,cAAc;EACd,WAAW;EACX,WAAW;EACX,iCAAiC;EACjC,YAAY;EACZ,2BAA2B;AAC7B;;AAEA;EACE,6BAA6B;AAC/B;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;;;4EAG4E;AAC5E;EACE,eAAe;EACf,wCAAwC;EACxC,yBAAyB;EACzB,kBAAkB;EAClB,gCAAgC;EAChC,yCAAyC;EACzC,gBAAgB;EAChB,wBAAwB;AAC1B;;AAEA;;;8DAG8D;AAC9D;EACE,sBAAsB;EACtB,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,sBAAsB;EACtB,qDAAqD;EACrD,iCAAiC;EACjC,+CAA+C;EAC/C,gCAAgC;EAChC,kBAAkB;EAClB,wCAAwC;EACxC,sBAAsB;EACtB,yBAAyB;EACzB,cAAc;EACd,4CAA4C;EAC5C,wCAAwC;AAC1C;;AAEA;;;;;;;;;;;;;;;;;;;oBAmBoB;;AAEpB;;8DAE8D;AAC9D;EACE,kBAAkB;AACpB;;AAEA;;;;;;;;qCAQqC;AACrC;EACE,WAAW;EACX,kBAAkB;EAClB,eAAe;EACf,WAAW;EACX,oBAAoB;EACpB;;;+BAG6B;AAC/B;;AAEA;EACE;;;+BAG6B;AAC/B;;AAEA;;wEAEwE;AACxE;EACE,WAAW;EACX,kBAAkB;EAClB,OAAO;EACP,QAAQ;EACR,iCAAiC;EACjC,WAAW;EACX,oBAAoB;EACpB;;;;qBAImB;AACrB;;AAEA;EACE;;;;qBAImB;AACrB;;AAEA,yCAAyC,aAAa,EAAE;;AAExD;EACE,WAAW;EACX,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,wBAAwB;AAC1B;;AAEA;yDACyD;AACzD;EACE,6BAA6B;AAC/B;;AAEA;4EAC4E;AAC5E;EACE,cAAc;EACd,WAAW;EACX,0BAA0B;EAC1B,sCAAsC;EACtC,gCAAgC;EAChC,kCAAkC;EAClC,gBAAgB;AAClB;AACA;EACE,kBAAkB;EAClB,WAAW;EACX,+EAA+E;EAC/E,iCAAiC;EACjC,gBAAgB;EAChB,gCAAgC;EAChC;yEACuE;EACvE,oBAAoB;AACtB;AACA;;EAEE,WAAW;EACX,YAAY;AACd;;AAEA,uDAAuD;AACvD;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,yBAAyB;AAC3B;;AAEA;EACE,SAAO;EACP,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,aAAa;EACb,qBAAqB;EACrB,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA;EACE,yBAAyB;EACzB,oCAAoC;EACpC,yBAAyB;EACzB,oCAAoC;AACtC;;AAEA,gCAAgC,cAAc,EAAE;AAChD,kCAAkC,cAAc,EAAE;;AAElD;EACE,yBAAyB;EACzB,oCAAoC;EACpC,4BAA4B;EAC5B,kCAAkC;AACpC;;AAEA;EACE,WAAW;EACX,iCAAiC;EACjC,gBAAgB;EAChB,kCAAkC;AACpC;;AAEA;EACE,YAAY;EACZ,iCAAiC;EACjC,2BAA2B;AAC7B;;AAEA,gCAAgC,mBAAmB,EAAE;AACrD,kCAAkC,mBAAmB,EAAE;;AAEvD;EACE,yBAAyB;EACzB,oCAAoC;EACpC,wBAAwB;EACxB,qBAAqB;EACrB,cAAc;EACd,yBAAyB;AAC3B;;AAEA,4CAA4C;AAC5C;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;mFAwDmF;AACnF;EACE,8BAA8B;EAC9B,kBAAkB,EAAE,yCAAyC;EAC7D,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,6BAA6B;AAC/B;;AAEA;EACE,yBAAyB;EACzB,wCAAwC;EACxC,0BAA0B;EAC1B,6BAA6B;AAC/B;;AAEA;EACE,2BAA2B;EAC3B,mCAAmC;EACnC,wBAAwB;AAC1B;;AAEA;EACE,aAAa;EACb,uBAAuB;AACzB;;AAEA,qDAAqD;AACrD;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;;2EAE2E;AAC3E;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,YAAY;EACZ,yBAAyB;EACzB,wBAAwB;EACxB,kBAAkB;AACpB;;AAEA;EACE,mCAAmC;AACrC;;AAEA;;;;;;;WAOW;AACX;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,uBAAuB;EACvB,gCAAgC;AAClC;;AAEA;;;;;;;qCAOqC;;AAErC;;;;;;gEAMgE;AAChE;EACE,iBAAiB;EACjB,oCAAoC;EACpC,yBAAyB;EACzB,sCAAsC;EACtC,uBAAuB;EACvB,iCAAiC;EACjC,kBAAkB;EAClB,cAAc;EACd,2EAA2E;EAC3E,iFAAiF;EACjF,uFAAuF;AACzF;;AAEA,+BAA+B;AAC/B;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;AAChC;;AAEA;EACE,yBAAyB;EACzB,wCAAwC;EACxC,0BAA0B;AAC5B;;AAEA;EACE,kBAAkB;EAClB,wCAAwC;EACxC,oCAAoC;EACpC,yBAAyB;EACzB,uBAAuB;EACvB,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,gCAAgC;EAChC,uCAAuC;AACzC;;AAEA;EACE,cAAc;EACd,+BAA+B;EAC/B,sCAAsC;AACxC;;AAEA;EACE,+BAA+B;EAC/B,kCAAkC;EAClC,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,YAAY;EACZ,kBAAkB;EAClB,2BAA2B;AAC7B;;AAEA;EACE,oDAAoD;AACtD;;AAEA;EACE,oDAAoD;AACtD;;AAEA,uBAAuB;AACvB;EACE,oBAAoB;EACpB,mBAAmB;EACnB,YAAY;EACZ,kBAAkB;EAClB,wCAAwC;EACxC,0BAA0B;AAC5B;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;;iDAEiD;;AAEjD;EACE,0BAA0B;EAC1B,kBAAkB;EAClB,UAAU;AACZ;;AAEA,gCAAgC;AAChC;EACE,aAAa;EACb,0BAA0B;EAC1B,mBAAmB;AACrB;;AAEA;EACE;4EAC0E;EAC1E,sCAAsC;EACtC,gCAAgC;EAChC,qDAAqD;EACrD,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,MAAM;EACN,UAAU;EACV,WAAW;EACX,WAAW;EACX,mFAAmF;AACrF;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,mBAAmB;EACnB,8BAA8B;EAC9B,8CAA8C;AAChD;;AAEA;EACE,6BAA6B;EAC7B,8BAA8B;EAC9B,kBAAkB;EAClB,cAAc;AAChB;;AAEA;EACE,mBAAmB;EACnB,0CAA0C;EAC1C,oDAAoD;AACtD;;AAEA;EACE,WAAW,UAAU,EAAE;EACvB,MAAM,YAAY,EAAE;AACtB;;AAEA;EACE,6BAA6B;EAC7B,kBAAkB;EAClB,sCAAsC;EACtC,4BAA4B;EAC5B,yBAAyB;EACzB,sBAAsB;EACtB,SAAO;AACT;;AAEA;EACE,6BAA6B;EAC7B,kBAAkB;EAClB,sCAAsC;EACtC,cAAc;EACd,mCAAmC;EACnC,yCAAyC;EACzC,uBAAuB;EACvB,kBAAkB;EAClB,sBAAsB;AACxB;;AAEA;EACE,aAAa;EACb,qBAAqB;EACrB,6BAA6B;EAC7B,mBAAmB;EACnB,SAAO;AACT;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,mBAAmB;EACnB,YAAY;AACd;;AAEA;;8EAE8E;AAC9E;EACE,aAAa;EACb,qCAAqC;EACrC,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,gCAAgC;EAChC,wCAAwC;EACxC,oCAAoC;EACpC,0BAA0B;EAC1B,kCAAkC;EAClC,cAAc;EACd,wBAAwB;AAC1B;;AAEA;EACE,0BAA0B;EAC1B,6DAA6D;EAC7D,6BAA6B;EAC7B,qBAAqB;EACrB,oCAAoC;AACtC;;AAEA;EACE,6BAA6B;EAC7B,kBAAkB;EAClB,sCAAsC;EACtC,yBAAyB;EACzB,yBAAyB;EACzB,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;;iDAEiD;;AAEjD;EACE,0BAA0B;EAC1B,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,kBAAkB;EAClB,gCAAgC;EAChC,sCAAsC;EACtC,oCAAoC;EACpC,wBAAwB;EACxB,gBAAgB;EAChB,kEAAkE;EAClE,6BAA6B;EAC7B,0BAA0B;EAC1B,gBAAgB;EAChB,iBAAiB;EACjB,kBAAkB;AACpB;;AAEA;8EAC8E;AAC9E;EACE,kBAAkB;EAClB,gCAAgC;EAChC,0CAA0C;EAC1C,iBAAiB;EACjB,gBAAgB;EAChB,wBAAwB;EACxB,gBAAgB;EAChB,8BAA8B;AAChC;;AAEA;EACE,oCAAoC;EACpC,0BAA0B;AAC5B;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,4BAA4B;EAC5B,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,qCAAqC;EACrC,mBAAmB;EACnB,oCAAoC;EACpC,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,oCAAoC;EACpC,6BAA6B;AAC/B;;AAEA;EACE,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE;4EAC0E;EAC1E,sCAAsC;EACtC,gCAAgC;EAChC,sCAAsC;EACtC,aAAa;EACb,sBAAsB;EACtB,oBAAoB;EACpB,gBAAgB;EAChB,kBAAkB;EAClB,wCAAwC;EACxC,kCAAkC;AACpC;;AAEA;EACE,mCAAmC;EACnC,2BAA2B;EAC3B,wCAAwC;AAC1C;;AAEA;EACE,qCAAqC;EACrC;mHACiH;EACjH;;kCAEgC;EAChC,sBAAsB;AACxB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,SAAS;EACT,SAAS;EACT,UAAU;EACV,WAAW;EACX,kFAAkF;AACpF;;AAEA;EACE,uCAAuC;EACvC,qCAAqC;AACvC;;AAEA;EACE,+CAA+C;EAC/C;8FAC4F;AAC9F;;AAEA;EACE,kBAAkB;EAClB,UAAU;EACV,SAAS;EACT,2BAA2B;EAC3B,UAAU;EACV,qDAAqD;EACrD,yCAAyC;EACzC,cAAc;EACd,6BAA6B;EAC7B,kBAAkB;EAClB,sCAAsC;EACtC,yBAAyB;EACzB,sBAAsB;EACtB,uBAAuB;EACvB,iCAAiC;EACjC,mBAAmB;EACnB,2CAA2C;AAC7C;;AAEA;EACE,mBAAmB;EACnB,8CAA8C;EAC9C,8BAA8B;AAChC;;AAEA;EACE,6BAA6B;EAC7B,kBAAkB;EAClB,sCAAsC;EACtC,wBAAwB;EACxB,yBAAyB;EACzB,sBAAsB;EACtB,6BAA6B;AAC/B;;AAEA;EACE,gCAAgC;EAChC,qCAAqC;EACrC,qCAAqC;EACrC,uBAAuB;EACvB,0BAA0B;EAC1B,6BAA6B;EAC7B,cAAc;EACd,kCAAkC;AACpC;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,sCAAsC;EACtC,wBAAwB;EACxB,iBAAiB;AACnB;;AAEA;EACE,gCAAgC;EAChC,iBAAiB;EACjB,sCAAsC;EACtC,2BAA2B;EAC3B,6BAA6B;EAC7B,oBAAoB;EACpB,iBAAiB;AACnB;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,yBAAyB;EACzB,6BAA6B;EAC7B,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,UAAU;EACV,uCAAuC;EACvC,SAAO;EACP,2CAA2C;EAC3C,2BAA2B;AAC7B;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,4BAA4B;EAC5B,yBAAyB;EACzB,gBAAgB;EAChB,kBAAkB;EAClB,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,OAAO;EACP,WAAW;EACX,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,iCAAiC;EACjC,YAAY;AACd;;AAEA,uEAAuE,mBAAmB,EAAE;AAC5F,oEAAoE,mCAAmC,EAAE;AACzG,kEAAkE,2BAA2B,EAAE;AAC/F,kEAAkE,iCAAiC,EAAE;;AAErG;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,kBAAkB;EAClB,gCAAgC;EAChC,kBAAkB;EAClB,wCAAwC;EACxC,sBAAsB;EACtB,yBAAyB;EACzB,kBAAkB;EAClB,qBAAqB;EACrB,iCAAiC;EACjC,sCAAsC;AACxC;;AAEA;EACE,qCAAqC;EACrC,0CAA0C;EAC1C,4BAA4B;AAC9B;;AAEA;EACE,oCAAoC;EACpC,0BAA0B;AAC5B;;AAEA;EACE,oCAAoC;EACpC,yCAAyC;EACzC,cAAc;AAChB;;AAEA;EACE,mCAAmC;EACnC,cAAc;AAChB;;AAEA;EACE,6CAA6C;EAC7C,kDAAkD;EAClD,8BAA8B;AAChC;;AAEA;EACE,4CAA4C;EAC5C,cAAc;AAChB;;AAEA;EACE,4CAA4C;EAC5C,+FAA+F;AACjG;;AAEA;EACE,2CAA2C;EAC3C,4CAA4C;EAC5C,sBAAsB;AACxB;;AAEA;EACE,2CAA2C;EAC3C,gDAAgD;EAChD,sBAAsB;AACxB;;AAEA;EACE,0CAA0C;EAC1C,4BAA4B;AAC9B;;AAEA;EACE,4CAA4C;EAC5C,+FAA+F;AACjG;;AAEA;EACE,mBAAmB;EACnB,4CAA4C;EAC5C,4BAA4B;AAC9B;;AAEA;EACE,2CAA2C;EAC3C,gDAAgD;EAChD,4BAA4B;AAC9B;;AAEA;EACE,0CAA0C;EAC1C,cAAc;AAChB;;AAEA;EACE,oCAAoC;EACpC,yCAAyC;EACzC,cAAc;AAChB;;AAEA;EACE,mCAAmC;EACnC,cAAc;AAChB;;AAEA;EACE,kBAAkB;EAClB,yBAAyB;EACzB,yBAAyB;EACzB,0BAA0B;EAC1B,mCAAmC;AACrC;;AAEA;EACE,4BAA4B;EAC5B,qBAAqB;AACvB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA,oBAAoB;AACpB,sDAAsD,uBAAuB,EAAE;AAC/E,sDAAsD,uBAAuB,EAAE;AAC/E,sDAAsD,uBAAuB,EAAE;;AAE/E,yBAAyB;AACzB;EACE,sDAAsD;EACtD,kBAAkB;EAClB,kBAAkB;EAClB,UAAU;AACZ;;AAEA;EACE,WAAW;EACX,cAAc;EACd,UAAU;EACV,YAAY;EACZ,uEAAuE;EACvE,8BAA8B;AAChC;;AAEA;EACE,gCAAgC;EAChC,mCAAmC;EACnC,qCAAqC;EACrC,wBAAwB;EACxB,iBAAiB;EACjB,6BAA6B;EAC7B,0BAA0B;EAC1B,6DAA6D;EAC7D,6BAA6B;EAC7B,qBAAqB;EACrB,oCAAoC;EACpC,gBAAgB;EAChB,iBAAiB;EACjB,kBAAkB;AACpB;;AAEA;EACE,8BAA8B;EAC9B,kBAAkB;EAClB,gBAAgB;EAChB,8CAA8C;AAChD;;AAEA;EACE,gCAAgC;EAChC,wCAAwC;EACxC,wBAAwB;EACxB,oCAAoC;EACpC,8BAA8B;EAC9B,iBAAiB;AACnB;;AAEA;EACE,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA,qBAAqB;AACrB;EACE,kBAAkB;EAClB,UAAU;EACV,0CAA0C;EAC1C,uCAAuC;EACvC,2BAA2B;EAC3B,2BAA2B;AAC7B;;AAEA;EACE,qCAAqC;EACrC,cAAc;EACd,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,gCAAgC;EAChC,kBAAkB;EAClB,yBAAyB;EACzB,SAAO;EACP,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,wBAAwB;EACxB,qBAAqB;EACrB,mDAAmD;EACnD,mBAAmB;EACnB,6EAA6E;AAC/E;;AAEA;EACE,0BAA0B;EAC1B,6CAA6C;AAC/C;;AAEA;EACE,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,yBAAyB;EACzB,wBAAwB;EACxB,qBAAqB;EACrB,wCAAwC;AAC1C;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;;iDAEiD;;AAEjD;EACE,mBAAmB;EACnB;IACE,sBAAsB;IACtB,mBAAmB;EACrB;;EAEA;IACE,gBAAgB;IAChB,WAAW;EACb;;EAEA;IACE,wBAAwB;IACxB,cAAc;EAChB;;EAEA,6BAA6B;EAC7B;IACE,0BAA0B;IAC1B,mBAAmB;EACrB;;EAEA;IACE,cAAc;EAChB;;EAEA,gDAAgD;EAChD;IACE,uBAAuB;EACzB;;EAEA,wCAAwC;EACxC;IACE,sBAAsB;IACtB,mBAAmB;EACrB;;EAEA;IACE,sBAAsB;IACtB,uBAAuB;IACvB,mBAAmB;EACrB;;EAEA;IACE,uBAAuB;EACzB;;EAEA,0BAA0B;EAC1B;IACE,mBAAmB;EACrB;;EAEA;IACE,gBAAgB;EAClB;;EAEA,kCAAkC;EAClC;IACE,0BAA0B;EAC5B;;EAEA,iFAAiF;EACjF;IACE,8BAA8B;IAC9B,kCAAkC;IAClC,kBAAkB;EACpB;;EAEA;;;;;2DAKyD;EACzD;IACE,WAAW;IACX,mBAAmB;IACnB,kBAAkB;EACpB;;EAEA;IACE,uBAAuB;EACzB;;EAEA;IACE,mBAAmB;EACrB;;EAEA,kDAAkD;EAClD;IACE,qCAAqC;IACrC,mBAAmB;EACrB;;EAEA;IACE,mBAAmB;IACnB,mBAAmB;EACrB;;EAEA,WAAW;EACX;IACE,sBAAsB;IACtB,mBAAmB;IACnB,kBAAkB;EACpB;;EAEA,sBAAsB;EACtB;IACE,uBAAuB;IACvB,iBAAiB;EACnB;;EAEA,yBAAyB;EACzB;IACE,eAAe;EACjB;;EAEA,0BAA0B;EAC1B;IACE,mBAAmB;EACrB;AACF;;AAEA;EACE;IACE;;;;;;;;;2CASuC;IACvC,4BAA4B;IAC5B,6BAA6B;IAC7B,2DAA2D;IAC3D,wCAAwC;EAC1C;;EAEA;IACE,iBAAiB;IACjB,qBAAqB;IACrB,oBAAoB;EACtB;;EAEA;IACE,2BAA2B;EAC7B;;EAEA,wDAAwD;EACxD;IACE,yBAAyB;EAC3B;;EAEA,sCAAsC;EACtC;IACE,6BAA6B;EAC/B;;EAEA,qBAAqB;EACrB;IACE,eAAe;EACjB;;EAEA,mCAAmC;EACnC;IACE,yBAAyB;EAC3B;;EAEA;IACE,yBAAyB;EAC3B;;EAEA,gDAAgD;EAChD;IACE,uBAAuB;EACzB;;EAEA,oCAAoC;EACpC;IACE,yBAAyB;EAC3B;;EAEA;;IAEE,sCAAsC;EACxC;;EAEA,uBAAuB;EACvB;IACE,yBAAyB;EAC3B;;EAEA;IACE,yBAAyB;IACzB,uBAAuB;IACvB,mBAAmB;EACrB;;EAEA,qDAAqD;EACrD;IACE,sBAAsB;IACtB,mBAAmB;EACrB;;EAEA;IACE,kBAAkB;EACpB;;EAEA,8BAA8B;EAC9B;IACE,yBAAyB;EAC3B;;EAEA;IACE,yBAAyB;EAC3B;;EAEA;;;6EAG2E;;EAE3E,oCAAoC;EACpC;IACE,kBAAkB;EACpB;;EAEA,+BAA+B;EAC/B;IACE,yBAAyB;EAC3B;;EAEA;IACE,iBAAiB;EACnB;;EAEA,yBAAyB;EACzB;IACE,aAAa;IACb,qCAAqC;IACrC,mBAAmB;EACrB;;EAEA;;;+BAG6B;EAC7B;IACE,oBAAoB;EACtB;;EAEA;IACE,mBAAmB;IACnB,oBAAoB;EACtB;;EAEA,kCAAkC;EAClC;IACE,uBAAuB;EACzB;;EAEA;IACE,mBAAmB;IACnB,eAAe;IACf,uBAAuB;EACzB;;EAEA;IACE,YAAY;IACZ,iCAAiC;EACnC;;EAEA;IACE,iBAAiB;IACjB,mBAAmB;EACrB;;EAEA,4CAA4C;EAC5C;IACE,0BAA0B;IAC1B,gBAAgB;IAChB,cAAc;EAChB;;EAEA;IACE,sBAAsB;IACtB,mBAAmB;IACnB,gBAAgB;EAClB;;EAEA;IACE,UAAU;IACV,WAAW;IACX,gBAAgB;EAClB;;EAEA;IACE,sCAAsC;EACxC;;EAEA,sBAAsB;EACtB;IACE,WAAW;EACb;;EAEA;IACE,sBAAsB;IACtB,kBAAkB;EACpB;;EAEA,yBAAyB;EACzB;IACE,iBAAiB;IACjB,wBAAwB;IACxB,sBAAsB;EACxB;;EAEA,sCAAsC;EACtC;IACE,2BAA2B;IAC3B,sCAAsC;EACxC;;EAEA,sCAAsC;EACtC;IACE,0BAA0B;EAC5B;;EAEA;IACE,8BAA8B;EAChC;;EAEA;IACE,8BAA8B;EAChC;;EAEA,wBAAwB;EACxB;IACE,uCAAuC;EACzC;;EAEA;IACE,2BAA2B;EAC7B;;EAEA,oBAAoB;EACpB;IACE,sCAAsC;EACxC;;EAEA;IACE,mBAAmB;EACrB;AACF;;AAEA;;iDAEiD;;AAEjD;EACE;IACE,UAAU;IACV,eAAe;IACf,gBAAgB;EAClB;;EAEA,qEAAqE;EACrE;;IAEE,oBAAoB;IACpB,eAAe;EACjB;;EAEA;IACE,UAAU;IACV,eAAe;EACjB;;EAEA;IACE,oBAAoB;IACpB,eAAe;EACjB;;EAEA;IACE,eAAe;IACf,UAAU;EACZ;;;;EAIA;IACE,eAAe;EACjB;;EAEA;;IAEE,eAAe;EACjB;AACF;;AAEA;;iDAEiD;;AAEjD,wBAAwB,kBAAkB,EAAE;;AAE5C;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,oBAAoB;EACpB,uCAAuC;EACvC,oBAAoB;EACpB,kBAAkB;EAClB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,iBAAiB;EACjB,gEAAgE;EAChE,mBAAmB;EACnB,sBAAsB;AACxB;;AAEA;EACE,UAAU,EAAE,WAAW,EAAE,kBAAkB;EAC3C,wBAAwB;EACxB,qBAAqB;EACrB,gCAAgC;EAChC,2DAA2D;AAC7D;;AAEA;EACE,WAAW,aAAa,EAAE,sBAAsB,EAAE;EAClD,WAAW,UAAU,KAAK,qBAAqB,EAAE;AACnD;;AAEA;EACE,6CAA6C;EAC7C,8CAA8C;EAC9C,cAAc;AAChB;;AAEA;EACE,2CAA2C;EAC3C,4CAA4C;EAC5C,4BAA4B;AAC9B;;AAEA;EACE,qBAAqB;EACrB,iBAAiB;EACjB,gBAAgB;EAChB,gCAAgC;EAChC,6BAA6B;EAC7B,6CAA6C;EAC7C,gCAAgC;EAChC,oBAAoB;EACpB,sBAAsB;EACtB,uBAAuB;AACzB;;AAEA,8EAA8E;AAC9E;EACE,eAAe;EACf,SAAS;EACT,SAAS;EACT,2BAA2B;EAC3B,YAAY;EACZ,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,mCAAmC;EACnC,oBAAoB;EACpB,oCAAoC;EACpC,0CAA0C;EAC1C,2BAA2B;EAC3B,mCAAmC;EACnC,4EAA4E;EAC5E,cAAc;EACd,eAAe;EACf,gBAAgB;EAChB,wEAAwE;AAC1E;;AAEA;EACE,OAAO,UAAU,EAAE,iCAAiC,EAAE;EACtD,OAAO,UAAU,EAAE,6BAA6B,EAAE;AACpD;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,oCAAoC;EACpC,cAAc;EACd,cAAc;AAChB;;AAEA;EACE,mBAAmB;EACnB,sBAAsB;AACxB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,qCAAqC;EACrC,2CAA2C;EAC3C,gCAAgC;EAChC,eAAe;EACf,mDAAmD;EACnD,oBAAoB;AACtB;;AAEA;EACE,qCAAqC;EACrC,+BAA+B;AACjC;;AAEA;EACE;IACE,mBAAmB;IACnB,gBAAgB;IAChB,eAAe;IACf,iBAAiB;EACnB;AACF;;AAEA,oFAAoF;AACpF;EACE,kBAAkB;EAClB,iBAAiB;EACjB,WAAW;EACX,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,mBAAmB;EACnB,SAAS;EACT,eAAe;AACjB;AACA;yEACyE;AACzE;EACE,SAAS;EACT,gCAAgC;EAChC,mCAAmC;EACnC,oCAAoC;EACpC,uBAAuB;EACvB,iBAAiB;EACjB,0BAA0B;EAC1B,kBAAkB;AACpB;AACA;EACE,SAAS;EACT,eAAe;EACf,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,8BAA8B;EAC9B,kBAAkB;AACpB;AACA;;;;;;uEAMuE;AACvE;;EAEE,kBAAkB;EAClB,cAAc;EACd,0BAA0B;EAC1B,2BAA2B;EAC3B,iBAAiB;EACjB,cAAc;EACd,UAAU;EACV,2CAA2C;EAC3C,mBAAmB;EACnB;;6BAE2B;EAC3B,0FAA0F;EAC1F,gBAAgB;EAChB,uBAAuB;AACzB;AACA;EACE,aAAa;EACb,SAAS;EACT,uBAAuB;EACvB,mBAAmB;AACrB;AACA;EACE;;IAEE,yBAAyB;IACzB,2BAA2B;IAC3B,iBAAiB;EACnB;AACF;;AAEA;;;;;8BAK8B;AAC9B;EACE;;;;6EAI2E;EAC3E;IACE,iBAAiB;EACnB;;EAEA;IACE,kBAAkB;IAClB,SAAS;IACT,WAAW;IACX,WAAW;IACX,YAAY;IACZ,SAAS;EACX;;EAEA;IACE,WAAW;IACX,mBAAmB;EACrB;;EAEA;IACE,SAAS;IACT,WAAW;EACb;;EAEA;;2DAEyD;EACzD;IACE,cAAc;IACd,gBAAgB;EAClB;;EAEA;IACE,YAAY;IACZ,WAAW;IACX,YAAY;IACZ,yCAAyC;EAC3C;;EAEA;IACE,WAAW;IACX,mBAAmB;EACrB;;EAEA;IACE,WAAW;IACX,YAAY;EACd;;EAEA;IACE,aAAa;IACb,mBAAmB;IACnB,gBAAgB;IAChB,6BAA6B;EAC/B;;EAEA;IACE,cAAc;EAChB;;AAEF","sourcesContent":["/* ============================================\n   LANDING PAGE — Apothecary RPG\n   Dark editorial + pharmaceutical document motifs\n   ============================================ */\n\n/* ---- Layout ---- */\n.landing {\n  min-height: 100vh;\n  background: var(--bg-base);\n  color: var(--text-primary);\n  font-family: var(--font-display);\n  position: relative;\n  overflow-x: hidden;\n\n  /* ---- The three axes ----\n     Debono's colour is semantic, never decorative. These three are the whole\n     vocabulary of the page background, and each one names a WAY OF KNOWING:\n\n       understanding — many things held at once, and how they interact\n       knowledge     — plain fact recall, memorised and retrieved\n       intuition     — what can be known without having been learned\n\n     Values are the logo-derived pastels (mint / sky / blush) kept as raw RGB\n     triples so the same hue can be spent at any alpha. Declared HERE, on the\n     page root, because a custom property has to sit on an ancestor of\n     everything that reads it — the clouds, the cards and the tags all do.\n\n     TO ADD AN AXIS: define --axis-<name> here, then put data-axis=\"<name>\" on\n     the thing. A cloud for it appears, its cards colour themselves, and the\n     scroll lighting picks it up — there is no list of axes in the JS to update\n     and no gradient to add. An axis with no token declared falls back to the\n     neutral below rather than painting nothing, so a typo degrades quietly\n     instead of leaving an invisible layer nobody can explain. */\n  --axis-understanding: 138 230 180;\n  --axis-knowledge: 150 194 246;\n  --axis-intuition: 242 150 150;\n  --axis-fallback: 148 148 156;\n}\n\n/* Grain + scan overlay — gives the page film/print character */\n.landing::before {\n  content: '';\n  position: fixed;\n  inset: 0;\n  pointer-events: none;\n  z-index: 0;\n  background-image:\n    radial-gradient(circle at 1px 1px, rgba(255,255,255,0.025) 1px, transparent 0);\n  background-size: 3px 3px;\n  opacity: 0.5;\n  mix-blend-mode: overlay;\n}\n\n/* ---- The background: dark ground, three pastel clouds ----\n\n   REPLACES the full-bleed pastel wash that used to live here. That version laid\n   a five-stop linear gradient over the base at alpha 0.52-0.56, which is enough\n   to bury a #0a0a0a ground completely — the page read as lavender, not as a\n   dark-only product, and the pastels were spent evenly on every section whether\n   or not they meant anything there.\n\n   Now the ground is the near-black base and the pastels survive only as three\n   soft CLOUDS, one per axis. Each cloud is a large, very soft radial that never\n   exceeds ~0.13 alpha at its core, so it tints the dark rather than replacing\n   it. Their weights are driven by what you are actually reading: a section\n   about understanding lights the green cloud and lets the other two fall back.\n   Colour therefore states which way of knowing is on screen. */\n.landing-bg {\n  position: fixed;\n  inset: 0;\n  pointer-events: none;\n  z-index: 0;\n  overflow: hidden;\n  background-color: var(--bg-base);\n}\n\n/* One cloud, painted once per axis the page uses. Landing.jsx renders one of\n   these for each distinct data-axis value it finds and hands it two things: the\n   colour (--axis) and where it sits (--cx / --cy). Nothing about the SET of\n   clouds is written down here, so adding a mechanic on a new axis needs a\n   colour token and no changes to this rule.\n\n   Size is in vmax so the shape stays soft and off-frame at any window — an\n   ellipse wider than the viewport has no visible edge, which is what keeps\n   this reading as atmosphere rather than as a coloured blob. */\n.landing-cloud {\n  position: absolute;\n  inset: 0;\n  pointer-events: none;\n  background-image: radial-gradient(80vmax 62vmax at var(--cx, 50%) var(--cy, 50%),\n    rgba(var(--axis) / calc(0.13 * var(--w, 0.62))) 0%,\n    rgba(var(--axis) / calc(0.05 * var(--w, 0.62))) 39%,\n    rgba(var(--axis) / 0) 69%);\n  background-repeat: no-repeat;\n  /* Weight is crossfaded rather than snapped, so scrolling past a boundary is\n     a shift in the light and not a repaint. */\n  transition: background-image 900ms cubic-bezier(0.16, 1, 0.3, 1);\n}\n\n/* ---- Edge ----\n   Two hairline passes over the clouds. The vignette pulls the corners back to\n   true black so the page is darkest where it meets the browser chrome and the\n   content sits in the lit middle; the scanline keeps a fine mechanical texture\n   in the flat areas, which is what stops a soft gradient reading as mush. */\n.landing-bg::after {\n  content: '';\n  position: absolute;\n  inset: 0;\n  pointer-events: none;\n  background:\n    repeating-linear-gradient(\n      0deg,\n      rgba(255, 255, 255, 0.014) 0px,\n      rgba(255, 255, 255, 0.014) 1px,\n      transparent 1px,\n      transparent 3px),\n    radial-gradient(120% 88% at 50% 42%,\n      transparent 42%,\n      rgba(6, 6, 8, 0.55) 78%,\n      rgba(6, 6, 8, 0.86) 100%);\n}\n\n@media (prefers-reduced-motion: reduce) {\n  .landing-bg { transition: none; }\n}\n\n/* ---- Scroll animation ---- */\n.landing-animate {\n  opacity: 0;\n  transform: translateY(24px);\n  transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);\n}\n\n.landing-animate.landing-visible {\n  opacity: 1;\n  transform: translateY(0);\n}\n\n/* ============================================\n   HERO\n   ============================================ */\n\n.landing-hero {\n  position: relative;\n  z-index: 1;\n  display: flex;\n  align-items: flex-start;\n  justify-content: center;\n  text-align: center;\n\n  /* ---- Fit the first screen ----------------------------------------------\n     The hero used to be a fixed-height column: every quantity in it answered\n     to the viewport's WIDTH (vw clamps) or to nothing at all (the mark was a\n     flat 396px), and none of them to its HEIGHT. So the column stood ~825px\n     tall whatever screen it was on, and the program strip — the last thing a\n     visitor needs to see before deciding anything — fell below the fold on\n     every common laptop: measured 56px under at 1366x768, 67px at 1440x765,\n     116px at 1536x730, 228px at 1280x585. It only cleared the fold from about\n     864px of viewport height up.\n\n     ONE ratio now governs the whole vertical rhythm. --hero-u is a LENGTH\n     equal to (fit x 1px), which is the only shape CSS allows here: calc()\n     cannot divide a length by a length to make a number, but it can multiply a\n     length by one. So every flexible quantity below is written\n     calc(<its authored px> * var(--hero-u)) and is EXACTLY its authored value\n     whenever the fit is 1 — the change is a provable no-op on any screen that\n     already worked.\n\n     The fit is SOLVED, not tuned. The strip's bottom sits at N + F*fit, where\n\n       N = what must not shrink -- the wordmark, the tagline, the sub, the\n           divider, the strip itself, and a 48px floor under the button row\n       F = what yields -- the mark (331px of it, margins included) and every\n           vertical gap: 511px in total\n\n     Requiring the strip to land at least 16px clear of the fold gives\n     fit = (viewport - N - 16) / F, capped at 1 so a tall screen gets the\n     design exactly as authored, and floored so an extreme window degrades to\n     scrolling rather than to nothing readable.\n\n     🔴 EVERY TERM OF N IS A FUNCTION OF WIDTH, and it has to be. The first cut\n     of this expressed only the wordmark that way and lumped the rest into one\n     247px constant calibrated near 1366. That was measured across 17 widths and\n     it does not hold: the sub alone swings 92 -> 110px, because its font-size is\n     a vw clamp and its measure is a DIFFERENT vw clamp, so the line count is a\n     step function of the ratio between them -- and not even a monotonic one\n     (3.54 line-boxes at a 440px measure, 3.36 at 480, back to 3.54 at 504-564,\n     then 2.36 at 576+). The consequence was not academic: the 16px clearance\n     collapsed to 3.5px between 1680 and 1880 wide, and the strip went fully\n     UNDER the fold at wide-and-short windows (1680x581, 1728x584, 1880x560,\n     2048x569, 2304x571, 3440x571).\n\n     So each term below converts its own AUTHORED source into a height, and\n     tracks width exactly as that source does. Where a term cannot be derived it\n     is the measured worst case over the whole width range, which is safe in one\n     direction only: over-reserving costs a little mark size, under-reserving\n     costs the requirement. Verified conservative at every width sampled, with\n     the tightest margin +0.9px at 1680-1880 (where it used to be -12.6px).\n\n     Every term is in REM, not px, and that matters: all of it measures TEXT,\n     and text grows with the reader's default font size. A px reserve would\n     under-count the very thing it is reserving for on a browser set to 20px,\n     which is a fold failure and not a cosmetic one.\n\n     (The flexible side stays in px by construction -- --hero-u is a length, so\n     the multipliers below it have to be plain numbers. That direction is safe:\n     the gaps come out tighter than authored under a large root font rather than\n     looser, so the strip only ever moves UP.) */\n  --hero-vh: 100vh;\n\n  /* .landing-hero-script's own width clamp x 95.4/550.1, the SVG's aspect. */\n  --hero-n-wordmark: clamp(3.87rem, 6.625vw, 8.65rem);\n  /* 1.5 line-boxes of .landing-hero-tagline's font clamp (measured 18 -> 22.8px\n     at a 16px root). */\n  --hero-n-tagline: clamp(1.125rem, 1.425vw, 1.425rem);\n  /* 3.54 line-boxes (the worst of the step function above) x 1.55 line-height\n     x .landing-hero-sub's font clamp = 92.2 -> 109.7px at a 16px root.\n     ⚠️ The 3.54 is a MEASUREMENT of the current copy, not a derivation --\n     lengthen that sentence and this must be re-measured. */\n  --hero-n-sub: clamp(5.7625rem, 6.859vw, 6.85625rem);\n  /* The genuinely width-invariant remainder, each one measured: divider 18 +\n     program strip 46 + button-row floor 48 + 16 clear of the fold = 128px. */\n  --hero-n-rest: 8rem;\n\n  --hero-fixed: calc(var(--hero-n-wordmark) + var(--hero-n-tagline)\n                   + var(--hero-n-sub) + var(--hero-n-rest));\n  --hero-flex: 511;\n  /* Floor 0.3, not 0.45: at the floor the fit stops answering the viewport, so\n     the floor IS the height below which the requirement fails. 0.45 put that\n     boundary at ~600px of viewport height on a wide window, which is why the\n     wide-and-short failures above existed. 0.3 moves it to ~552px at 3440 wide.\n     Nothing textual shrinks on the way down -- only the mark and the gaps -- so\n     a low floor degrades gracefully rather than illegibly. */\n  --hero-u: clamp(0.3px, calc((var(--hero-vh) - var(--hero-fixed)) / var(--hero-flex)), 1px);\n\n  /* min() rather than a bare calc so the authored vw ramp still governs a tall\n     screen: the scaled term can only ever be the smaller of the two. */\n  padding: min(clamp(1.75rem, 4vw, 3.5rem), calc(56 * var(--hero-u)))\n           var(--space-6)\n           calc(48 * var(--hero-u));\n  min-height: min(clamp(640px, 80vh, 880px), var(--hero-vh));\n}\n\n/* svh, not dvh: the question is what fits WITHOUT scrolling, and the small\n   viewport is the one you get on load. dvh would also re-fit the hero live\n   while a mobile URL bar retracts, which is a resize nobody asked for.\n   Guarded rather than declared twice, because a custom property holding an\n   unsupported unit does NOT fall back to an earlier declaration — it parses\n   fine and then poisons every property that reads it. */\n@supports (height: 100svh) {\n  .landing-hero { --hero-vh: 100svh; }\n}\n\n/* The button row is flex-wrap: wrap with three items in the logged-out state,\n   and N counts it as ONE row. Once .landing-hero-inner is narrower than the\n   row's ~517px it becomes two, and the unbudgeted second row plus its 12px gap\n   put the strip back under the fold — measured +24.4px at 500x800, +19.4 at\n   480x800, +20.3 at 480x700, +46.5 at 390x664.\n\n   So N gains a row here. The breakpoint is 600px rather than the ~567px where\n   the wrap actually begins, because the row's width depends on its own text and\n   font: erring wide over-reserves 60px on a handful of narrow windows, erring\n   narrow loses the requirement outright. */\n@media (max-width: 600px) {\n  .landing-hero { --hero-n-rest: 188px; }  /* 128 + a second 48px row + its 12px gap */\n}\n\n.landing-hero-inner {\n  max-width: clamp(620px, 44vw, 980px);\n  width: 100%;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  position: relative;\n}\n\n/* Decorative RX cross watermark behind hero */\n.landing-hero-watermark {\n  position: absolute;\n  top: 48%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  width: clamp(520px, 56vw, 880px);\n  height: clamp(520px, 56vw, 880px);\n  pointer-events: none;\n  z-index: 0;\n  opacity: 0.05;\n  color: var(--accent-primary);\n}\n\n.landing-hero-inner > *:not(.landing-hero-watermark) {\n  position: relative;\n  z-index: 1;\n}\n\n.landing-hero-watermark svg {\n  width: 100%;\n  height: 100%;\n  display: block;\n  animation: landing-rotate 120s linear infinite;\n}\n\n@keyframes landing-rotate {\n  from { transform: rotate(0deg); }\n  to { transform: rotate(360deg); }\n}\n\n/* Hero corner stamps — pharmaceutical document feel */\n.landing-hero-stamp {\n  position: absolute;\n  top: 0;\n  display: flex;\n  align-items: center;\n  gap: 0.55rem;\n  font-family: var(--font-mono);\n  font-size: 0.7rem;\n  letter-spacing: 0.12em;\n  text-transform: uppercase;\n  color: var(--text-subtle);\n  line-height: 1;\n}\n\n.landing-hero-stamp-left { left: 0; }\n.landing-hero-stamp-right { right: 0; }\n\n.landing-hero-stamp-dot {\n  width: 6px;\n  height: 6px;\n  border-radius: 50%;\n  background: #22c55e;\n  box-shadow: 0 0 8px rgba(34,197,94,0.6);\n  animation: landing-pulse 2.4s ease-in-out infinite;\n}\n\n@keyframes landing-pulse {\n  0%, 100% { opacity: 1; transform: scale(1); }\n  50% { opacity: 0.55; transform: scale(0.85); }\n}\n\n.landing-hero-stamp-bar {\n  width: 28px;\n  height: 1px;\n  background: linear-gradient(90deg, transparent, var(--text-disabled));\n}\n\n.landing-hero-stamp-right .landing-hero-stamp-bar {\n  background: linear-gradient(270deg, transparent, var(--text-disabled));\n}\n\n/* ---- Program strip (inline in hero, above title) ---- */\n.landing-program-strip {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 0;\n  /* Below the fit target, so this gap does not decide whether the strip is\n     visible — it scales anyway, because a shrunken column with full-size\n     trailing gaps reads as a mistake. */\n  margin-bottom: calc(32 * var(--hero-u));  /* --space-8 at fit 1 */\n  padding: 0.35rem;\n  background: rgba(255,255,255,0.025);\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-full);\n  backdrop-filter: blur(12px);\n}\n\n/* Rendered as a <button> — the strip is a real picker (it reframes the demo\n   headline below), so the element resets browser chrome back to the pill. */\n.landing-program-tag {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.4rem;\n  padding: 0.45rem 0.95rem;\n  border-radius: var(--radius-full);\n  font-family: var(--font-display);\n  font-size: 0.72rem;\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: 0.12em;\n  text-transform: uppercase;\n  line-height: 1;\n  border: 1px solid transparent;\n  background: transparent;\n  cursor: pointer;\n  transition: all var(--transition-fast);\n}\n\n.landing-program-tag-active {\n  background: #c265f5; /* vivid orchid — first stop of the button sequence */\n  border-color: transparent;\n  color: #ffffff;\n  box-shadow: 0 0 22px rgba(194,101,245,0.5), inset 0 1px 0 rgba(255,255,255,0.3);\n}\n\n.landing-program-tag-active::before {\n  content: '';\n  width: 5px;\n  height: 5px;\n  border-radius: 50%;\n  background: #ffffff;\n  margin-right: 0.1rem;\n}\n\n.landing-program-tag-idle {\n  background: transparent;\n  border-color: transparent;\n  color: var(--text-subtle);\n  opacity: 0.75;\n}\n\n.landing-program-tag-idle:hover,\n.landing-program-tag-idle:focus-visible {\n  background: rgba(255, 255, 255, 0.05);\n  color: var(--text-primary);\n  opacity: 1;\n}\n\n/* On the picked pill the badge sits on orchid, so it needs its own contrast —\n   the disabled grey it uses when idle disappears into the fill. */\n.landing-program-tag-active .landing-program-tag-soon {\n  color: rgba(255, 255, 255, 0.92);\n  border-color: rgba(255, 255, 255, 0.45);\n  opacity: 1;\n}\n\n.landing-program-tag-soon {\n  font-size: 0.55rem;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--text-disabled);\n  opacity: 0.8;\n  padding: 0.15rem 0.35rem;\n  border: 1px solid var(--border-subtle);\n  border-radius: 3px;\n}\n\n/* ============================================\n   HERO INTRO SEQUENCE\n   One timeline, two movements. The logo is drawn stroke by stroke and the colour\n   floods in behind the nib; then the script is written, the prescription symbol\n   first and the wordmark after it, left to right in a single pass.\n\n     0.00s  silhouette, with colour sweeping in behind the nib\n     0.58s  hole\n     0.90s  the three limbs, staggered\n     1.35s  colour settles\n     1.65s  the script is written\n     2.65s  done\n   ============================================ */\n.landing-hero-logo {\n  display: block;\n  /* The single largest item in the hero, and the one that used to ignore the\n     viewport completely: 347px of a ~825px column. Height stays `auto`, so the\n     SVG's own 396x347 intrinsic ratio carries it — scaling the width is enough,\n     and the stroke-draw above scales with the viewBox for free. */\n  width: calc(396 * var(--hero-u));\n  height: auto;\n  margin: calc(-36 * var(--hero-u)) auto calc(20 * var(--hero-u));\n  isolation: isolate;\n}\n\n/* The mark draws itself. `pathLength=\"1\"` normalises every path so one keyframe\n   serves all five and the timings below stay readable as seconds. */\n.landing-hero-logo .dbl-line,\n.landing-hero-logo .dbl-sweep {\n  stroke-dasharray: 1;\n  stroke-dashoffset: 1;\n  animation: landing-logo-draw var(--dbl-dur) var(--dbl-ease) var(--dbl-delay) forwards;\n}\n\n/* A nib gets up to speed and settles; it does not start and stop instantly.\n   Long strokes carry the acceleration, short ones only need the landing. */\n.landing-hero-logo .dbl-line,\n.landing-hero-logo .dbl-sweep { --dbl-ease: cubic-bezier(0.37, 0.01, 0.25, 1); }\n.landing-hero-logo .dbl-seg-3,\n.landing-hero-logo .dbl-seg-4,\n.landing-hero-logo .dbl-seg-5 { --dbl-ease: cubic-bezier(0.25, 0.6, 0.3, 1); }\n\n.landing-hero-logo .dbl-seg-1 { --dbl-delay: 0s;    --dbl-dur: 0.95s; }\n.landing-hero-logo .dbl-seg-2 { --dbl-delay: 0.62s; --dbl-dur: 0.40s; }\n/* Staggered by less than half their own length so the three limbs overlap heavily\n   and read as one continuous passage rather than three separate events. */\n.landing-hero-logo .dbl-seg-3 { --dbl-delay: 0.86s; --dbl-dur: 0.42s; }\n.landing-hero-logo .dbl-seg-4 { --dbl-delay: 1.00s; --dbl-dur: 0.42s; }\n.landing-hero-logo .dbl-seg-5 { --dbl-delay: 1.14s; --dbl-dur: 0.42s; }\n\n/* Starts after the nib and runs longer, so colour trails the outline instead of\n   racing ahead of it as it did when both started together. */\n.landing-hero-logo .dbl-sweep { --dbl-delay: 0.12s; --dbl-dur: 1.25s; }\n\n@keyframes landing-logo-draw {\n  to { stroke-dashoffset: 0; }\n}\n\n/* Starts while the sweep still has a third to go. The sweep's tail is the corner it\n   began on, which is also the corner the nib drew first, so without this overlap the\n   outline sits finished for a beat with its top-left still uncoloured. Eased both\n   ends so it arrives as a settle, not a fade-up. */\n.landing-hero-logo .dbl-flood {\n  opacity: 0;\n  animation: landing-logo-flood 0.65s ease-in-out 0.95s forwards;\n}\n\n@keyframes landing-logo-flood {\n  to { opacity: 1; }\n}\n\n/* ---- Hero text ---- */\n.landing-hero-name {\n  margin: 0;\n  text-align: center;\n  position: relative;\n  display: block;\n  /* The wordmark is now an SVG so the reveal can be stroke-driven. The glow stays\n     here on the h1, outside the SVG's own mask, so nothing shears it. */\n  filter: drop-shadow(0 0 70px rgba(191, 64, 255, 0.30));\n}\n\n/* 550.1 / 103.68 = 5.306, so this reproduces the box the live text used to make\n   from font-size: clamp(4.2rem, 7.2vw, 9.4rem).\n   ⚠️ --hero-n-wordmark in .landing-hero mirrors the width clamp below, scaled by\n   95.4/550.1 to get this mark's height. Change one and re-derive the other. */\n.landing-hero-script {\n  display: block;\n  width: clamp(22.29rem, 38.2vw, 49.88rem);\n  height: auto;\n  margin: 0 auto;\n  overflow: visible;\n\n  /* ---- DEBONO is centred on the page; the ℞ hangs off to its left ----\n     `margin: 0 auto` centres the SVG BOX, and the box holds the WHOLE lockup —\n     so what was centred was ℞ + DEBONO together, which left DEBONO itself right\n     of the page axis and off the centreline of the logo above it.\n\n     The offset is readable straight off the markup, which is why the correction\n     is written as it is rather than as a tuned number: the wordmark's <text>\n     starts at x=75.2 in a 550.1-wide viewBox that is fitted to the lockup's\n     advance, so DEBONO's centre sits at (75.2 + 550.1)/2 = 312.65 where the\n     box's is 275.05 — 37.6 units right, i.e. 37.6/550.1 = 6.835% of the mark's\n     width, at every size.\n\n     Confirmed live, not assumed: with the rule removed in-run the wordmark\n     measured +35.62px off axis at 1366, +50.07 at 1920, +33.36 at 1280, +37.54\n     at 1440 and +54.48 at 2560 — which is 6.8253% of the rendered width every\n     time (spread 0.004%), agreeing with the figure above to 0.05px.\n\n     Corrected with a TRANSLATE on the <svg>, NOT by moving the <text> inside\n     it. That is not a preference: the wordmark is revealed by a stroke-driven\n     mask whose pen routes (SCRIPT_STROKES in Landing.jsx) are authored in this\n     same coordinate space, so moving the text would leave the mask tracing\n     glyphs that are no longer there. Translating the root carries text, mask and\n     nibs together and the reveal cannot come apart.\n\n     Percent, because translateX(%) resolves against the element's OWN box, so\n     one number holds at every width where a px offset would only be right at\n     the size it was measured on. A transform does not reflow, so the hero's fit\n     budget above is untouched (asserted: the program strip's deficit is\n     identical with the rule on and off, at all five sizes).\n\n     The ℞ keeps its distance from the D — that gap lives inside the SVG and\n     rides along, so it stays proportional to the mark rather than fixed. */\n  transform: translateX(-6.835%);\n}\n\n.landing-hero-script-word {\n  font-family: var(--font-display);\n  font-size: 103.68px;\n  font-weight: var(--font-weight-black);\n  letter-spacing: 2.0736px;\n  font-feature-settings: 'ss01' 1, 'salt' 1;\n}\n\n.landing-hero-script-rx {\n  font-family: var(--font-serif);\n  font-size: 64.2816px;\n  font-weight: var(--font-weight-bold);\n  letter-spacing: 2.0736px;\n}\n\n\n/* The script is written by drawing the pen route through each glyph. Same primitive\n   as the logo: pathLength normalises every route to 1, so one keyframe serves all.\n\n   --dbs-s is a speed multiplier on BOTH the offsets and the durations, so the whole\n   passage stretches without the rhythm between glyphs changing. 1 is real time; raise\n   it to watch the mechanism.\n\n   The gap between the R and the D is a real gap between two draws, not a hold inside\n   one sweep, which is why the pause reads. */\n.landing-hero-script {\n  --dbs-t0: 1.65s;\n  --dbs-s: 1;\n}\n\n.landing-hero-script .dbs-ink,\n.landing-hero-script .dbs-nib {\n  animation-duration: calc(var(--dbs-dur) * var(--dbs-s));\n  animation-delay: calc(var(--dbs-t0) + var(--dbs-off) * var(--dbs-s));\n  animation-timing-function: cubic-bezier(0.4, 0.05, 0.3, 1);\n  animation-fill-mode: forwards;\n}\n\n.landing-hero-script .dbs-ink {\n  stroke-dasharray: 1;\n  stroke-dashoffset: 1;\n  animation-name: landing-script-draw;\n}\n\n\n@keyframes landing-script-draw {\n  to { stroke-dashoffset: 0; }\n}\n\n/* Nib. Sampled positions along each pen route, replayed as plain transforms.\n   This started as CSS offset-path, which is exact but is a recent Chromium\n   feature; where it is unsupported every nib silently parks at the origin,\n   off-canvas, and reads as \"there is no nib\". Sampling costs a few keyframes and\n   works everywhere.\n   Sized to the mask stroke (26) so the ink's leading edge and the nib's leading\n   edge are the same edge: no ink appears that the nib has not reached. The nib\n   fades out where a route jumps between strokes, which is the pen lifting. */\n.landing-hero-script .dbs-nib {\n  fill: #ffffff;\n  opacity: 0;\n  transform-box: view-box;\n  filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.95))\n          drop-shadow(0 0 16px rgba(191, 64, 255, 0.95));\n}\n\n.landing-hero-script .dbs-st-1 { --dbs-off: 0s; --dbs-dur: 0.0683s; }\n.landing-hero-script .dbs-st-2 { --dbs-off: 0.0832s; --dbs-dur: 0.0881s; }\n.landing-hero-script .dbs-st-3 { --dbs-off: 0.1863s; --dbs-dur: 0.0347s; }\n.landing-hero-script .dbs-st-4 { --dbs-off: 0.2359s; --dbs-dur: 0.0441s; }\n.landing-hero-script .dbs-st-5 { --dbs-off: 0.48s; --dbs-dur: 0.066s; }\n.landing-hero-script .dbs-st-6 { --dbs-off: 0.5812s; --dbs-dur: 0.1188s; }\n.landing-hero-script .dbs-st-7 { --dbs-off: 0.68s; --dbs-dur: 0.1158s; }\n.landing-hero-script .dbs-st-8 { --dbs-off: 0.8246s; --dbs-dur: 0.0354s; }\n.landing-hero-script .dbs-st-9 { --dbs-off: 0.84s; --dbs-dur: 0.0474s; }\n.landing-hero-script .dbs-st-10 { --dbs-off: 0.9034s; --dbs-dur: 0.0585s; }\n.landing-hero-script .dbs-st-11 { --dbs-off: 0.9779s; --dbs-dur: 0.0621s; }\n.landing-hero-script .dbs-st-12 { --dbs-off: 1.02s; --dbs-dur: 0.22s; }\n.landing-hero-script .dbs-st-13 { --dbs-off: 1.22s; --dbs-dur: 0.2s; }\n.landing-hero-script .dbs-st-14 { --dbs-off: 1.4s; --dbs-dur: 0.22s; }\n\n@keyframes landing-script-nib-1 {\n  0% { transform: translate(8.5px, 6.2px) rotate(90deg); opacity: 0; }\n  5% { transform: translate(8.5px, 8.64px) rotate(90deg); opacity: 1; }\n  10% { transform: translate(8.5px, 11.08px) rotate(90deg); opacity: 1; }\n  15% { transform: translate(8.5px, 13.52px) rotate(90deg); opacity: 1; }\n  20% { transform: translate(8.5px, 15.96px) rotate(90deg); opacity: 1; }\n  25% { transform: translate(8.5px, 18.4px) rotate(90deg); opacity: 1; }\n  30% { transform: translate(8.5px, 20.84px) rotate(90deg); opacity: 1; }\n  35% { transform: translate(8.5px, 23.28px) rotate(90deg); opacity: 1; }\n  40% { transform: translate(8.5px, 25.72px) rotate(90deg); opacity: 1; }\n  45% { transform: translate(8.5px, 28.16px) rotate(90deg); opacity: 1; }\n  50% { transform: translate(8.5px, 30.6px) rotate(90deg); opacity: 1; }\n  55% { transform: translate(8.5px, 33.04px) rotate(90deg); opacity: 1; }\n  60% { transform: translate(8.5px, 35.48px) rotate(90deg); opacity: 1; }\n  65% { transform: translate(8.5px, 37.92px) rotate(90deg); opacity: 1; }\n  70% { transform: translate(8.5px, 40.36px) rotate(90deg); opacity: 1; }\n  75% { transform: translate(8.5px, 42.8px) rotate(90deg); opacity: 1; }\n  80% { transform: translate(8.5px, 45.24px) rotate(90deg); opacity: 1; }\n  85% { transform: translate(8.5px, 47.68px) rotate(90deg); opacity: 1; }\n  90% { transform: translate(8.5px, 50.12px) rotate(90deg); opacity: 1; }\n  95% { transform: translate(8.5px, 52.56px) rotate(90deg); opacity: 1; }\n  100% { transform: translate(8.5px, 55px) rotate(90deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-1 { animation-name: landing-script-nib-1; }\n\n@keyframes landing-script-nib-2 {\n  0% { transform: translate(8.5px, 9.8px) rotate(1.7deg); opacity: 0; }\n  5% { transform: translate(11.65px, 9.89px) rotate(1.7deg); opacity: 1; }\n  10% { transform: translate(14.8px, 9.99px) rotate(1.7deg); opacity: 1; }\n  15% { transform: translate(17.95px, 10.08px) rotate(1.7deg); opacity: 1; }\n  20% { transform: translate(21.09px, 10.18px) rotate(3.2deg); opacity: 1; }\n  25% { transform: translate(23.98px, 11.31px) rotate(27deg); opacity: 1; }\n  30% { transform: translate(26.78px, 12.74px) rotate(27deg); opacity: 1; }\n  35% { transform: translate(29.2px, 14.03px) rotate(50.8deg); opacity: 1; }\n  40% { transform: translate(31.19px, 16.47px) rotate(61.8deg); opacity: 1; }\n  45% { transform: translate(31.87px, 19.53px) rotate(78.9deg); opacity: 1; }\n  50% { transform: translate(32.2px, 22.64px) rotate(90deg); opacity: 1; }\n  55% { transform: translate(32.2px, 25.79px) rotate(90deg); opacity: 1; }\n  60% { transform: translate(32.02px, 28.92px) rotate(102.5deg); opacity: 1; }\n  65% { transform: translate(30.37px, 30.85px) rotate(177.7deg); opacity: 1; }\n  70% { transform: translate(27.23px, 30.98px) rotate(176deg); opacity: 1; }\n  75% { transform: translate(24.14px, 31.55px) rotate(167.8deg); opacity: 1; }\n  80% { transform: translate(21.06px, 32.22px) rotate(167.8deg); opacity: 1; }\n  85% { transform: translate(17.95px, 32.59px) rotate(-179.5deg); opacity: 1; }\n  90% { transform: translate(14.8px, 32.56px) rotate(-179.5deg); opacity: 1; }\n  95% { transform: translate(11.65px, 32.53px) rotate(-179.5deg); opacity: 1; }\n  100% { transform: translate(8.5px, 32.5px) rotate(-179.5deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-2 { animation-name: landing-script-nib-2; }\n\n@keyframes landing-script-nib-3 {\n  0% { transform: translate(34.8px, 34.5px) rotate(55.7deg); opacity: 0; }\n  5% { transform: translate(35.5px, 35.53px) rotate(55.7deg); opacity: 1; }\n  10% { transform: translate(36.2px, 36.55px) rotate(55.7deg); opacity: 1; }\n  15% { transform: translate(36.9px, 37.57px) rotate(55.7deg); opacity: 1; }\n  20% { transform: translate(37.6px, 38.6px) rotate(55.7deg); opacity: 1; }\n  25% { transform: translate(38.3px, 39.63px) rotate(55.7deg); opacity: 1; }\n  30% { transform: translate(39px, 40.65px) rotate(55.7deg); opacity: 1; }\n  35% { transform: translate(39.7px, 41.67px) rotate(55.7deg); opacity: 1; }\n  40% { transform: translate(40.4px, 42.7px) rotate(55.7deg); opacity: 1; }\n  45% { transform: translate(41.1px, 43.72px) rotate(55.7deg); opacity: 1; }\n  50% { transform: translate(41.8px, 44.75px) rotate(55.7deg); opacity: 1; }\n  55% { transform: translate(42.5px, 45.78px) rotate(55.7deg); opacity: 1; }\n  60% { transform: translate(43.2px, 46.8px) rotate(55.7deg); opacity: 1; }\n  65% { transform: translate(43.9px, 47.83px) rotate(55.7deg); opacity: 1; }\n  70% { transform: translate(44.6px, 48.85px) rotate(55.7deg); opacity: 1; }\n  75% { transform: translate(45.3px, 49.88px) rotate(55.7deg); opacity: 1; }\n  80% { transform: translate(46px, 50.9px) rotate(55.7deg); opacity: 1; }\n  85% { transform: translate(46.7px, 51.92px) rotate(55.7deg); opacity: 1; }\n  90% { transform: translate(47.4px, 52.95px) rotate(55.7deg); opacity: 1; }\n  95% { transform: translate(48.1px, 53.98px) rotate(55.7deg); opacity: 1; }\n  100% { transform: translate(48.8px, 55px) rotate(55.7deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-3 { animation-name: landing-script-nib-3; }\n\n@keyframes landing-script-nib-4 {\n  0% { transform: translate(47.8px, 30.5px) rotate(128.9deg); opacity: 0; }\n  5% { transform: translate(46.81px, 31.72px) rotate(128.9deg); opacity: 1; }\n  10% { transform: translate(45.82px, 32.95px) rotate(128.9deg); opacity: 1; }\n  15% { transform: translate(44.83px, 34.17px) rotate(128.9deg); opacity: 1; }\n  20% { transform: translate(43.84px, 35.4px) rotate(128.9deg); opacity: 1; }\n  25% { transform: translate(42.85px, 36.63px) rotate(128.9deg); opacity: 1; }\n  30% { transform: translate(41.86px, 37.85px) rotate(128.9deg); opacity: 1; }\n  35% { transform: translate(40.87px, 39.07px) rotate(128.9deg); opacity: 1; }\n  40% { transform: translate(39.88px, 40.3px) rotate(128.9deg); opacity: 1; }\n  45% { transform: translate(38.89px, 41.52px) rotate(128.9deg); opacity: 1; }\n  50% { transform: translate(37.9px, 42.75px) rotate(128.9deg); opacity: 1; }\n  55% { transform: translate(36.91px, 43.97px) rotate(128.9deg); opacity: 1; }\n  60% { transform: translate(35.92px, 45.2px) rotate(128.9deg); opacity: 1; }\n  65% { transform: translate(34.93px, 46.42px) rotate(128.9deg); opacity: 1; }\n  70% { transform: translate(33.94px, 47.65px) rotate(128.9deg); opacity: 1; }\n  75% { transform: translate(32.95px, 48.88px) rotate(128.9deg); opacity: 1; }\n  80% { transform: translate(31.96px, 50.1px) rotate(128.9deg); opacity: 1; }\n  85% { transform: translate(30.97px, 51.33px) rotate(128.9deg); opacity: 1; }\n  90% { transform: translate(29.98px, 52.55px) rotate(128.9deg); opacity: 1; }\n  95% { transform: translate(28.99px, 53.78px) rotate(128.9deg); opacity: 1; }\n  100% { transform: translate(28px, 55px) rotate(128.9deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-4 { animation-name: landing-script-nib-4; }\n\n@keyframes landing-script-nib-5 {\n  0% { transform: translate(91.7px, 11.9px) rotate(90deg); opacity: 0; }\n  5% { transform: translate(91.7px, 15.58px) rotate(90deg); opacity: 1; }\n  10% { transform: translate(91.7px, 19.27px) rotate(90deg); opacity: 1; }\n  15% { transform: translate(91.7px, 22.96px) rotate(90deg); opacity: 1; }\n  20% { transform: translate(91.7px, 26.64px) rotate(90deg); opacity: 1; }\n  25% { transform: translate(91.7px, 30.33px) rotate(90deg); opacity: 1; }\n  30% { transform: translate(91.7px, 34.01px) rotate(90deg); opacity: 1; }\n  35% { transform: translate(91.7px, 37.69px) rotate(90deg); opacity: 1; }\n  40% { transform: translate(91.7px, 41.38px) rotate(90deg); opacity: 1; }\n  45% { transform: translate(91.7px, 45.06px) rotate(90deg); opacity: 1; }\n  50% { transform: translate(91.7px, 48.75px) rotate(90deg); opacity: 1; }\n  55% { transform: translate(91.7px, 52.44px) rotate(90deg); opacity: 1; }\n  60% { transform: translate(91.7px, 56.12px) rotate(90deg); opacity: 1; }\n  65% { transform: translate(91.7px, 59.81px) rotate(90deg); opacity: 1; }\n  70% { transform: translate(91.7px, 63.49px) rotate(90deg); opacity: 1; }\n  75% { transform: translate(91.7px, 67.18px) rotate(90deg); opacity: 1; }\n  80% { transform: translate(91.7px, 70.86px) rotate(90deg); opacity: 1; }\n  85% { transform: translate(91.7px, 74.54px) rotate(90deg); opacity: 1; }\n  90% { transform: translate(91.7px, 78.23px) rotate(90deg); opacity: 1; }\n  95% { transform: translate(91.7px, 81.92px) rotate(90deg); opacity: 1; }\n  100% { transform: translate(91.7px, 85.6px) rotate(90deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-5 { animation-name: landing-script-nib-5; }\n\n@keyframes landing-script-nib-6 {\n  0% { transform: translate(91.7px, 20.6px) rotate(0.7deg); opacity: 0; }\n  5% { transform: translate(98.33px, 20.68px) rotate(0.7deg); opacity: 1; }\n  10% { transform: translate(104.96px, 20.76px) rotate(0.7deg); opacity: 1; }\n  15% { transform: translate(111.59px, 20.84px) rotate(0.7deg); opacity: 1; }\n  20% { transform: translate(118.2px, 21.16px) rotate(11.3deg); opacity: 1; }\n  25% { transform: translate(124.57px, 22.88px) rotate(22.2deg); opacity: 1; }\n  30% { transform: translate(130.34px, 26.05px) rotate(36.1deg); opacity: 1; }\n  35% { transform: translate(135.32px, 30.35px) rotate(51.8deg); opacity: 1; }\n  40% { transform: translate(138.8px, 35.93px) rotate(67.7deg); opacity: 1; }\n  45% { transform: translate(140.97px, 42.15px) rotate(82.9deg); opacity: 1; }\n  50% { transform: translate(141.79px, 48.73px) rotate(89.4deg); opacity: 1; }\n  55% { transform: translate(140.98px, 55.31px) rotate(97.2deg); opacity: 1; }\n  60% { transform: translate(138.81px, 61.54px) rotate(112.3deg); opacity: 1; }\n  65% { transform: translate(135.34px, 67.12px) rotate(128.2deg); opacity: 1; }\n  70% { transform: translate(130.37px, 71.42px) rotate(143.9deg); opacity: 1; }\n  75% { transform: translate(124.59px, 74.55px) rotate(158.7deg); opacity: 1; }\n  80% { transform: translate(118.2px, 76.24px) rotate(168.7deg); opacity: 1; }\n  85% { transform: translate(111.59px, 76.56px) rotate(179.3deg); opacity: 1; }\n  90% { transform: translate(104.96px, 76.64px) rotate(179.3deg); opacity: 1; }\n  95% { transform: translate(98.33px, 76.72px) rotate(179.3deg); opacity: 1; }\n  100% { transform: translate(91.7px, 76.8px) rotate(179.3deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-6 { animation-name: landing-script-nib-6; }\n\n@keyframes landing-script-nib-7 {\n  0% { transform: translate(212.6px, 20.5px) rotate(-177.7deg); opacity: 0; }\n  5% { transform: translate(205.7px, 20.22px) rotate(-177.7deg); opacity: 1; }\n  10% { transform: translate(198.8px, 19.94px) rotate(-177.7deg); opacity: 1; }\n  15% { transform: translate(191.89px, 19.65px) rotate(-177.7deg); opacity: 1; }\n  20% { transform: translate(184.99px, 19.37px) rotate(-177.7deg); opacity: 1; }\n  25% { transform: translate(178.09px, 19.09px) rotate(-177.7deg); opacity: 1; }\n  30% { transform: translate(173.4px, 21.12px) rotate(90deg); opacity: 1; }\n  35% { transform: translate(173.4px, 28.02px) rotate(90deg); opacity: 1; }\n  40% { transform: translate(173.4px, 34.93px) rotate(90deg); opacity: 1; }\n  45% { transform: translate(173.4px, 41.84px) rotate(90deg); opacity: 1; }\n  50% { transform: translate(173.4px, 48.75px) rotate(90deg); opacity: 1; }\n  55% { transform: translate(173.4px, 55.66px) rotate(90deg); opacity: 1; }\n  60% { transform: translate(173.4px, 62.56px) rotate(90deg); opacity: 1; }\n  65% { transform: translate(173.4px, 69.47px) rotate(90deg); opacity: 1; }\n  70% { transform: translate(173.4px, 76.38px) rotate(90deg); opacity: 1; }\n  75% { transform: translate(178.08px, 78.42px) rotate(-2.2deg); opacity: 1; }\n  80% { transform: translate(184.99px, 78.16px) rotate(-2.2deg); opacity: 1; }\n  85% { transform: translate(191.89px, 77.89px) rotate(-2.2deg); opacity: 1; }\n  90% { transform: translate(198.79px, 77.63px) rotate(-2.2deg); opacity: 1; }\n  95% { transform: translate(205.7px, 77.36px) rotate(-2.2deg); opacity: 1; }\n  100% { transform: translate(212.6px, 77.1px) rotate(-2.2deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-7 { animation-name: landing-script-nib-7; }\n\n@keyframes landing-script-nib-8 {\n  0% { transform: translate(173.4px, 48.1px) rotate(0deg); opacity: 0; }\n  5% { transform: translate(175.51px, 48.1px) rotate(0deg); opacity: 1; }\n  10% { transform: translate(177.62px, 48.1px) rotate(0deg); opacity: 1; }\n  15% { transform: translate(179.73px, 48.1px) rotate(0deg); opacity: 1; }\n  20% { transform: translate(181.84px, 48.1px) rotate(0deg); opacity: 1; }\n  25% { transform: translate(183.95px, 48.1px) rotate(0deg); opacity: 1; }\n  30% { transform: translate(186.06px, 48.1px) rotate(0deg); opacity: 1; }\n  35% { transform: translate(188.17px, 48.1px) rotate(0deg); opacity: 1; }\n  40% { transform: translate(190.28px, 48.1px) rotate(0deg); opacity: 1; }\n  45% { transform: translate(192.39px, 48.1px) rotate(0deg); opacity: 1; }\n  50% { transform: translate(194.5px, 48.1px) rotate(0deg); opacity: 1; }\n  55% { transform: translate(196.61px, 48.1px) rotate(0deg); opacity: 1; }\n  60% { transform: translate(198.72px, 48.1px) rotate(0deg); opacity: 1; }\n  65% { transform: translate(200.83px, 48.1px) rotate(0deg); opacity: 1; }\n  70% { transform: translate(202.94px, 48.1px) rotate(0deg); opacity: 1; }\n  75% { transform: translate(205.05px, 48.1px) rotate(0deg); opacity: 1; }\n  80% { transform: translate(207.16px, 48.1px) rotate(0deg); opacity: 1; }\n  85% { transform: translate(209.27px, 48.1px) rotate(0deg); opacity: 1; }\n  90% { transform: translate(211.38px, 48.1px) rotate(0deg); opacity: 1; }\n  95% { transform: translate(213.49px, 48.1px) rotate(0deg); opacity: 1; }\n  100% { transform: translate(215.6px, 48.1px) rotate(0deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-8 { animation-name: landing-script-nib-8; }\n\n@keyframes landing-script-nib-9 {\n  0% { transform: translate(241.5px, 85.6px) rotate(-90deg); opacity: 0; }\n  5% { transform: translate(241.5px, 81.92px) rotate(-90deg); opacity: 1; }\n  10% { transform: translate(241.5px, 78.23px) rotate(-90deg); opacity: 1; }\n  15% { transform: translate(241.5px, 74.54px) rotate(-90deg); opacity: 1; }\n  20% { transform: translate(241.5px, 70.86px) rotate(-90deg); opacity: 1; }\n  25% { transform: translate(241.5px, 67.17px) rotate(-90deg); opacity: 1; }\n  30% { transform: translate(241.5px, 63.49px) rotate(-90deg); opacity: 1; }\n  35% { transform: translate(241.5px, 59.81px) rotate(-90deg); opacity: 1; }\n  40% { transform: translate(241.5px, 56.12px) rotate(-90deg); opacity: 1; }\n  45% { transform: translate(241.5px, 52.43px) rotate(-90deg); opacity: 1; }\n  50% { transform: translate(241.5px, 48.75px) rotate(-90deg); opacity: 1; }\n  55% { transform: translate(241.5px, 45.06px) rotate(-90deg); opacity: 1; }\n  60% { transform: translate(241.5px, 41.38px) rotate(-90deg); opacity: 1; }\n  65% { transform: translate(241.5px, 37.69px) rotate(-90deg); opacity: 1; }\n  70% { transform: translate(241.5px, 34.01px) rotate(-90deg); opacity: 1; }\n  75% { transform: translate(241.5px, 30.32px) rotate(-90deg); opacity: 1; }\n  80% { transform: translate(241.5px, 26.64px) rotate(-90deg); opacity: 1; }\n  85% { transform: translate(241.5px, 22.95px) rotate(-90deg); opacity: 1; }\n  90% { transform: translate(241.5px, 19.27px) rotate(-90deg); opacity: 1; }\n  95% { transform: translate(241.5px, 15.58px) rotate(-90deg); opacity: 1; }\n  100% { transform: translate(241.5px, 11.9px) rotate(-90deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-9 { animation-name: landing-script-nib-9; }\n\n@keyframes landing-script-nib-10 {\n  0% { transform: translate(241.5px, 19.3px) rotate(0deg); opacity: 0; }\n  5% { transform: translate(246.05px, 19.3px) rotate(0deg); opacity: 1; }\n  10% { transform: translate(250.6px, 19.3px) rotate(0deg); opacity: 1; }\n  15% { transform: translate(255.15px, 19.3px) rotate(0deg); opacity: 1; }\n  20% { transform: translate(259.7px, 19.3px) rotate(0deg); opacity: 1; }\n  25% { transform: translate(264.25px, 19.33px) rotate(5.9deg); opacity: 1; }\n  30% { transform: translate(268.73px, 20.11px) rotate(10deg); opacity: 1; }\n  35% { transform: translate(272.84px, 21.96px) rotate(34.4deg); opacity: 1; }\n  40% { transform: translate(275.96px, 25.24px) rotate(58.2deg); opacity: 1; }\n  45% { transform: translate(277.74px, 29.41px) rotate(77.4deg); opacity: 1; }\n  50% { transform: translate(277.86px, 33.92px) rotate(97.6deg); opacity: 1; }\n  55% { transform: translate(276.57px, 38.27px) rotate(109.7deg); opacity: 1; }\n  60% { transform: translate(275.03px, 42.55px) rotate(110.3deg); opacity: 1; }\n  65% { transform: translate(272.92px, 46.56px) rotate(133.8deg); opacity: 1; }\n  70% { transform: translate(268.76px, 48.19px) rotate(167.5deg); opacity: 1; }\n  75% { transform: translate(264.25px, 48.59px) rotate(-179.8deg); opacity: 1; }\n  80% { transform: translate(259.7px, 48.57px) rotate(-179.8deg); opacity: 1; }\n  85% { transform: translate(255.15px, 48.55px) rotate(-179.8deg); opacity: 1; }\n  90% { transform: translate(250.6px, 48.54px) rotate(-179.8deg); opacity: 1; }\n  95% { transform: translate(246.05px, 48.52px) rotate(-179.8deg); opacity: 1; }\n  100% { transform: translate(241.5px, 48.5px) rotate(-179.8deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-10 { animation-name: landing-script-nib-10; }\n\n@keyframes landing-script-nib-11 {\n  0% { transform: translate(241.5px, 45.5px) rotate(0.8deg); opacity: 0; }\n  5% { transform: translate(246.33px, 45.57px) rotate(0.8deg); opacity: 1; }\n  10% { transform: translate(251.15px, 45.63px) rotate(0.8deg); opacity: 1; }\n  15% { transform: translate(255.98px, 45.7px) rotate(0.8deg); opacity: 1; }\n  20% { transform: translate(260.81px, 45.77px) rotate(0.8deg); opacity: 1; }\n  25% { transform: translate(265.64px, 45.83px) rotate(0.8deg); opacity: 1; }\n  30% { transform: translate(270.46px, 45.93px) rotate(15.3deg); opacity: 1; }\n  35% { transform: translate(273.92px, 49.12px) rotate(53.9deg); opacity: 1; }\n  40% { transform: translate(276.54px, 53.15px) rotate(63.1deg); opacity: 1; }\n  45% { transform: translate(278.72px, 57.46px) rotate(63.1deg); opacity: 1; }\n  50% { transform: translate(279.93px, 62.1px) rotate(78.9deg); opacity: 1; }\n  55% { transform: translate(279.8px, 66.88px) rotate(104.6deg); opacity: 1; }\n  60% { transform: translate(277.93px, 71.33px) rotate(122.1deg); opacity: 1; }\n  65% { transform: translate(274.63px, 74.84px) rotate(143.1deg); opacity: 1; }\n  70% { transform: translate(270.35px, 77.01px) rotate(166deg); opacity: 1; }\n  75% { transform: translate(265.61px, 77.97px) rotate(168.5deg); opacity: 1; }\n  80% { transform: translate(260.81px, 78.2px) rotate(180deg); opacity: 1; }\n  85% { transform: translate(255.98px, 78.2px) rotate(180deg); opacity: 1; }\n  90% { transform: translate(251.16px, 78.2px) rotate(180deg); opacity: 1; }\n  95% { transform: translate(246.33px, 78.2px) rotate(180deg); opacity: 1; }\n  100% { transform: translate(241.5px, 78.2px) rotate(180deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-11 { animation-name: landing-script-nib-11; }\n\n@keyframes landing-script-nib-12 {\n  0% { transform: translate(340.4px, 19.6px) rotate(11.2deg); opacity: 0; }\n  5% { transform: translate(349.36px, 21.74px) rotate(21.8deg); opacity: 1; }\n  10% { transform: translate(357.28px, 26.36px) rotate(41.6deg); opacity: 1; }\n  15% { transform: translate(363.2px, 33.36px) rotate(64.8deg); opacity: 1; }\n  20% { transform: translate(366.41px, 41.95px) rotate(79.8deg); opacity: 1; }\n  25% { transform: translate(367.2px, 51.12px) rotate(95.6deg); opacity: 1; }\n  30% { transform: translate(365.09px, 60.08px) rotate(111.8deg); opacity: 1; }\n  35% { transform: translate(360.42px, 67.97px) rotate(132.5deg); opacity: 1; }\n  40% { transform: translate(353.34px, 73.83px) rotate(145deg); opacity: 1; }\n  45% { transform: translate(344.79px, 77.09px) rotate(168.2deg); opacity: 1; }\n  50% { transform: translate(335.6px, 77.7px) rotate(-175.6deg); opacity: 1; }\n  55% { transform: translate(326.62px, 75.65px) rotate(-158.2deg); opacity: 1; }\n  60% { transform: translate(318.68px, 71.07px) rotate(-137.5deg); opacity: 1; }\n  65% { transform: translate(312.83px, 63.98px) rotate(-125deg); opacity: 1; }\n  70% { transform: translate(309.44px, 55.47px) rotate(-103.2deg); opacity: 1; }\n  75% { transform: translate(308.8px, 46.29px) rotate(-85.6deg); opacity: 1; }\n  80% { transform: translate(310.85px, 37.32px) rotate(-67.1deg); opacity: 1; }\n  85% { transform: translate(315.57px, 29.45px) rotate(-47.5deg); opacity: 1; }\n  90% { transform: translate(322.59px, 23.56px) rotate(-23.4deg); opacity: 1; }\n  95% { transform: translate(331.25px, 20.46px) rotate(-11.2deg); opacity: 1; }\n  100% { transform: translate(340.4px, 19.6px) rotate(0deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-12 { animation-name: landing-script-nib-12; }\n\n@keyframes landing-script-nib-13 {\n  0% { transform: translate(399px, 85.6px) rotate(-90deg); opacity: 0; }\n  5% { transform: translate(399px, 73.9px) rotate(-90deg); opacity: 1; }\n  10% { transform: translate(399px, 62.19px) rotate(-90deg); opacity: 1; }\n  15% { transform: translate(399px, 50.49px) rotate(-90deg); opacity: 1; }\n  20% { transform: translate(399px, 38.79px) rotate(-90deg); opacity: 1; }\n  25% { transform: translate(399px, 27.08px) rotate(-90deg); opacity: 1; }\n  30% { transform: translate(399px, 15.38px) rotate(-90deg); opacity: 1; }\n  35% { transform: translate(403.33px, 18.89px) rotate(58.3deg); opacity: 1; }\n  40% { transform: translate(409.48px, 28.85px) rotate(58.3deg); opacity: 1; }\n  45% { transform: translate(415.64px, 38.8px) rotate(58.3deg); opacity: 1; }\n  50% { transform: translate(421.8px, 48.75px) rotate(58.3deg); opacity: 1; }\n  55% { transform: translate(427.96px, 58.7px) rotate(58.3deg); opacity: 1; }\n  60% { transform: translate(434.12px, 68.65px) rotate(58.3deg); opacity: 1; }\n  65% { transform: translate(440.27px, 78.61px) rotate(58.3deg); opacity: 1; }\n  70% { transform: translate(444.6px, 82.12px) rotate(-90deg); opacity: 1; }\n  75% { transform: translate(444.6px, 70.42px) rotate(-90deg); opacity: 1; }\n  80% { transform: translate(444.6px, 58.71px) rotate(-90deg); opacity: 1; }\n  85% { transform: translate(444.6px, 47.01px) rotate(-90deg); opacity: 1; }\n  90% { transform: translate(444.6px, 35.31px) rotate(-90deg); opacity: 1; }\n  95% { transform: translate(444.6px, 23.6px) rotate(-90deg); opacity: 1; }\n  100% { transform: translate(444.6px, 11.9px) rotate(-90deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-13 { animation-name: landing-script-nib-13; }\n\n@keyframes landing-script-nib-14 {\n  0% { transform: translate(508px, 19.6px) rotate(11.2deg); opacity: 0; }\n  5% { transform: translate(516.96px, 21.74px) rotate(21.8deg); opacity: 1; }\n  10% { transform: translate(524.88px, 26.41px) rotate(34.3deg); opacity: 1; }\n  15% { transform: translate(530.83px, 33.38px) rotate(57.6deg); opacity: 1; }\n  20% { transform: translate(533.92px, 42px) rotate(79.8deg); opacity: 1; }\n  25% { transform: translate(534.69px, 51.17px) rotate(95.5deg); opacity: 1; }\n  30% { transform: translate(532.66px, 60.15px) rotate(111.8deg); opacity: 1; }\n  35% { transform: translate(527.97px, 68.02px) rotate(132.5deg); opacity: 1; }\n  40% { transform: translate(520.87px, 73.82px) rotate(155.9deg); opacity: 1; }\n  45% { transform: translate(512.26px, 77.06px) rotate(166.8deg); opacity: 1; }\n  50% { transform: translate(503.08px, 77.68px) rotate(-174.7deg); opacity: 1; }\n  55% { transform: translate(494.11px, 75.62px) rotate(-158.6deg); opacity: 1; }\n  60% { transform: translate(486.19px, 70.99px) rotate(-137.5deg); opacity: 1; }\n  65% { transform: translate(480.31px, 63.93px) rotate(-125.6deg); opacity: 1; }\n  70% { transform: translate(477.02px, 55.38px) rotate(-103.2deg); opacity: 1; }\n  75% { transform: translate(476.32px, 46.2px) rotate(-85.2deg); opacity: 1; }\n  80% { transform: translate(478.39px, 37.24px) rotate(-67.1deg); opacity: 1; }\n  85% { transform: translate(483.09px, 29.36px) rotate(-46.6deg); opacity: 1; }\n  90% { transform: translate(490.19px, 23.56px) rotate(-23.4deg); opacity: 1; }\n  95% { transform: translate(498.85px, 20.46px) rotate(-11.2deg); opacity: 1; }\n  100% { transform: translate(508px, 19.6px) rotate(0deg); opacity: 0; }\n}\n\n.landing-hero-script .dbs-nib.dbs-st-14 { animation-name: landing-script-nib-14; }\n\n\n\n\n/* Prescription symbol (℞) leading the hero wordmark, like the superscription\n   at the start of a real script. A plain glyph, not a badge. */\n.landing-hero-name-rx {\n  align-self: flex-start;\n  font-family: var(--font-serif);\n  font-size: 0.62em;\n  font-weight: var(--font-weight-bold);\n  line-height: 0.9;\n  margin-top: 0.06em;\n  /* Its own copy of the wordmark's white -> wisteria gradient so the ℞ and\n     DEBONO read as one material; self-contained because the filter below\n     isolates it from the parent's clipped background. */\n  background: linear-gradient(180deg, #ffffff 0%, #f4ecfb 55%, #d9c2f0 100%);\n  -webkit-background-clip: text;\n  background-clip: text;\n  -webkit-text-fill-color: transparent;\n  color: transparent;\n  filter: drop-shadow(0 0 8px rgba(191,64,255,0.28));\n}\n\n/* Hairline rule under wordmark */\n.landing-hero-tagline {\n  font-family: var(--font-display);\n  /* ⚠️ --hero-n-tagline in .landing-hero mirrors this clamp (x1.5, its line\n     box). Change it here and the first-screen fit must be re-measured. */\n  font-size: clamp(0.75rem, 0.95vw, 0.95rem);\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: 0.32em;\n  text-transform: uppercase;\n  color: var(--text-muted);\n  margin-bottom: 0;\n  margin-top: calc(24 * var(--hero-u));  /* --space-6 at fit 1 */\n  text-align: center;\n  display: flex;\n  align-items: center;\n  gap: 0.85rem;\n  justify-content: center;\n}\n\n/* EDUCATION keeps the tagline's display font (matches GAMIFIED) but gets a\n   soft lilac glow tuned to the hero's top-of-gradient palette. */\n.landing-hero-tagline-glow {\n  color: #fbf3ff;\n  position: relative;\n  z-index: 0;\n  text-shadow:\n    0 0 4px rgba(251, 243, 255, 0.9),\n    0 0 10px rgba(214, 163, 240, 0.95),\n    0 0 22px rgba(191, 64, 255, 0.8),\n    0 0 40px rgba(191, 64, 255, 0.55);\n}\n\n/* soft radial aura sitting behind the letters */\n.landing-hero-tagline-glow::before {\n  content: '';\n  position: absolute;\n  inset: -0.7em -1em;\n  z-index: -1;\n  pointer-events: none;\n  background: radial-gradient(\n    ellipse at center,\n    rgba(191, 64, 255, 0.5) 0%,\n    rgba(214, 163, 240, 0.22) 50%,\n    transparent 72%\n  );\n  filter: blur(7px);\n}\n\n.landing-hero-sub {\n  font-family: var(--font-display);\n  /* ⚠️ --hero-n-sub in .landing-hero mirrors this clamp AND this line-height AND\n     the line count the copy happens to wrap to (3.54 line-boxes worst case).\n     This block is the single biggest unshrinkable item in the first screen, so\n     editing the font, the measure below, or the sentence itself all move the\n     fit. Re-measure with harness/2026-08-05-hero-fit/sweep.mjs. */\n  font-size: clamp(1.05rem, 1.25vw, 1.25rem);\n  line-height: 1.55;\n  font-weight: 400;\n  color: var(--text-muted);\n  /* Font size and measure are deliberately NOT in the flexible set — body copy\n     that shrinks with the window is the one thing this must not trade away. */\n  max-width: clamp(440px, 30vw, 620px);\n  margin: calc(32 * var(--hero-u)) auto calc(40 * var(--hero-u));  /* --space-8 / --space-10 at fit 1 */\n  text-align: center;\n}\n\n.landing-hero-sub em {\n  font-family: var(--font-serif);\n  font-style: italic;\n  font-weight: 400;\n  color: var(--text-primary);\n  font-size: 1.18em;\n  letter-spacing: -0.01em;\n}\n\n.landing-hero-ctas {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: var(--space-3);\n  flex-wrap: wrap;\n  /* One pill height for the whole row, whatever it holds. The primary has no\n     border and the ghosts have two, so their natural heights differ by ~1.3px —\n     enough to read as a wobble once there is more than one ghost beside the\n     primary. Set here rather than per-button so a fourth CTA lines up for free.\n     Yields with the rest of the hero on a short screen but stops at 48px — a\n     control has a floor that a gap does not, and that 48 is the button floor\n     counted into --hero-n-rest above (with the remaining 8px in --hero-flex).\n     ⚠️ A fourth CTA, or a longer label, widens this row and can make it WRAP,\n     which N only budgets for below 600px. */\n  --cta-h: max(48px, calc(56 * var(--hero-u)));\n}\n\n.landing-hero-ctas > * {\n  flex: 0 0 auto;\n  height: var(--cta-h);\n  box-sizing: border-box;\n  white-space: nowrap;\n}\n\n/* ---- Buttons ---- */\n.landing-btn-primary {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 0.6rem;\n  padding: 1rem 2.2rem;\n  /* --btn-bg is set per instance so CTAs step through the pastel gradient\n     down the page; falls back to the original red for any button that\n     doesn't set it. Pastel fills get dark-ink labels for contrast. */\n  background: var(--btn-bg, var(--accent-primary));\n  color: var(--btn-fg, #ffffff);\n  border-radius: var(--radius-full);\n  font-family: var(--font-display);\n  font-size: 0.95rem;\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: 0.04em;\n  text-decoration: none;\n  text-transform: uppercase;\n  transition: all var(--transition-fast);\n  position: relative;\n  overflow: hidden;\n  box-shadow:\n    0 1px 0 rgba(255,255,255,0.28) inset,\n    0 -1px 0 rgba(0,0,0,0.22) inset,\n    0 8px 26px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 42%, transparent),\n    0 0 0 1px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 55%, transparent);\n}\n\n.landing-btn-primary::after {\n  content: '→';\n  font-family: var(--font-display);\n  font-weight: 400;\n  transition: transform var(--transition-fast);\n}\n\n.landing-btn-primary:hover {\n  filter: brightness(1.08);\n  transform: translateY(-2px);\n  box-shadow:\n    0 1px 0 rgba(255,255,255,0.35) inset,\n    0 -1px 0 rgba(0,0,0,0.22) inset,\n    0 16px 40px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 55%, transparent),\n    0 0 0 1px color-mix(in srgb, var(--btn-bg, var(--accent-primary)) 70%, transparent);\n}\n\n.landing-btn-primary:hover::after {\n  transform: translateX(4px);\n}\n\n.landing-btn-ghost {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.5rem;\n  color: var(--text-secondary);\n  text-decoration: none;\n  font-family: var(--font-display);\n  font-size: 0.92rem;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: 0.04em;\n  text-transform: uppercase;\n  transition: all var(--transition-fast);\n  padding: 1rem 1.4rem;\n  border: 1px solid var(--border-default);\n  border-radius: var(--radius-full);\n  background: rgba(255,255,255,0.025);\n}\n\n.landing-btn-ghost:hover {\n  color: var(--text-primary);\n  border-color: var(--border-focus);\n  background: rgba(255,255,255,0.05);\n  transform: translateY(-2px);\n}\n\n/* Now a real <Link> to /compendium, so the button-era resets are gone: no `font: inherit`\n   (there is no UA button chrome left to undo) and no `cursor: default` (a working\n   link must show a pointer). Padding is back at Log In's 1.4rem — it was trimmed to\n   1.15rem only to pay for the \"soon\" badge out of the pill, and the badge is gone. */\n.landing-btn-compendium {\n  justify-content: center;\n  padding: 1rem 1.4rem;\n  font-family: var(--font-display);\n  font-size: 0.92rem;\n  font-weight: var(--font-weight-medium);\n}\n\n/* The badge is the program strip's \"soon\" chip — same idiom, same page. */\n.landing-btn-soon {\n  font-size: 0.55rem;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: 0.1em;\n  text-transform: uppercase;\n  color: var(--text-disabled);\n  opacity: 0.8;\n  padding: 0.15rem 0.35rem;\n  border: 1px solid var(--border-subtle);\n  border-radius: 3px;\n  line-height: 1;\n}\n\n/* ---- Google Sign-In on hero ---- */\n.landing-google-divider {\n  display: flex;\n  align-items: center;\n  gap: var(--space-4);\n  width: 100%;\n  /* Widened with the CTA row: a 210px rule under a 560px row of buttons reads as\n     a stub rather than a separator. At desktop this lands on ~420px, the same\n     width as the program strip directly below it, so the two share an edge.\n     One-line revert to clamp(210px, 13vw, 300px) if the short rule is wanted. */\n  max-width: clamp(260px, 30vw, 420px);\n  margin-top: calc(20 * var(--hero-u));  /* --space-5 at fit 1 */\n}\n\n.landing-google-divider::before,\n.landing-google-divider::after {\n  content: '';\n  flex: 1;\n  height: 1px;\n  background: var(--border-subtle);\n}\n\n.landing-google-divider span {\n  font-size: var(--text-sm);\n  color: var(--text-subtle);\n  text-transform: lowercase;\n}\n\n.landing-google-btn {\n  margin-top: calc(12 * var(--hero-u));  /* --space-3 at fit 1 */\n  min-height: clamp(31px, 2vw, 44px);\n  display: flex;\n  justify-content: center;\n}\n\n.landing-google-error {\n  font-size: var(--text-sm);\n  color: var(--accent-primary);\n  margin-top: var(--space-2);\n}\n\n/* ---- Footer brand ---- */\n.landing-brand {\n  display: flex;\n  align-items: center;\n  gap: var(--space-3);\n}\n\n.landing-wordmark-sm {\n  font-size: var(--text-base);\n  font-weight: var(--font-weight-bold);\n  letter-spacing: 0.2em;\n  text-transform: uppercase;\n}\n\n/* ---- Sections shared ---- */\n.landing-section-inner {\n  position: relative;\n  z-index: 1;\n  max-width: clamp(770px, 49vw, 1100px);\n  margin: 0 auto;\n  padding: 0 var(--space-6);\n}\n\n.landing-section-label {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 1rem;\n  text-align: center;\n  font-family: var(--font-mono);\n  font-size: 0.72rem;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: 0.32em;\n  text-transform: uppercase;\n  color: var(--text-subtle);\n  margin-bottom: var(--space-12);\n}\n\n.landing-section-label::before,\n.landing-section-label::after {\n  content: '';\n  flex: 0 0 clamp(40px, 6vw, 80px);\n  height: 1px;\n  background: linear-gradient(90deg, transparent, var(--border-default), transparent);\n}\n\n.landing-section-label::before {\n  background: linear-gradient(90deg, transparent, var(--border-default));\n}\n\n.landing-section-label::after {\n  background: linear-gradient(270deg, transparent, var(--border-default));\n}\n\n/* ============================================\n   UPLOAD TO MASTERY — 3-step flow\n   ============================================ */\n\n.landing-flow {\n  padding: var(--space-12) 0 var(--space-16);\n  position: relative;\n  z-index: 1;\n}\n\n.landing-flow-subtitle {\n  font-family: var(--font-display);\n  text-align: center;\n  font-size: clamp(1.65rem, 2.4vw, 2.4rem);\n  font-weight: var(--font-weight-bold);\n  letter-spacing: -0.02em;\n  color: var(--text-primary);\n  margin-top: calc(-1 * var(--space-8));\n  margin-bottom: var(--space-12);\n  line-height: 1.15;\n}\n\n.landing-flow-subtitle::after {\n  content: '';\n  display: block;\n  width: 36px;\n  height: 2px;\n  background: var(--accent-primary);\n  margin: var(--space-4) auto 0;\n  border-radius: 2px;\n}\n\n.landing-flow-grid {\n  display: flex;\n  align-items: stretch;\n  justify-content: center;\n  gap: var(--space-4);\n  flex-wrap: nowrap;\n  max-width: 880px;\n  margin: 0 auto;\n}\n\n.landing-flow-card {\n  flex: 1;\n  max-width: clamp(160px, 14vw, 260px);\n  min-width: clamp(140px, 11vw, 220px);\n  min-height: clamp(220px, 16vw, 320px);\n  background:\n    linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.015) 100%);\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-2xl);\n  padding: var(--space-12) var(--space-5) var(--space-6);\n  text-align: center;\n  backdrop-filter: var(--glass-blur);\n  transition: all var(--transition-normal);\n  position: relative;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  overflow: hidden;\n  isolation: isolate;\n}\n\n/* Atmospheric tint per step */\n.landing-flow-card::before {\n  content: '';\n  position: absolute;\n  top: -50%;\n  left: 50%;\n  transform: translateX(-50%);\n  width: 200%;\n  height: 100%;\n  pointer-events: none;\n  z-index: -1;\n  filter: blur(60px);\n  opacity: 0.4;\n  transition: opacity var(--transition-normal);\n}\n\n.landing-flow-card:nth-child(1)::before {\n  background: radial-gradient(ellipse at top, rgba(239,68,68,0.4), transparent 60%);\n}\n.landing-flow-card:nth-child(3)::before {\n  background: radial-gradient(ellipse at top, rgba(139,92,246,0.4), transparent 60%);\n}\n.landing-flow-card:nth-child(5)::before {\n  background: radial-gradient(ellipse at top, rgba(251,191,36,0.4), transparent 60%);\n}\n\n/* Top hairline accent per step */\n.landing-flow-card::after {\n  content: '';\n  position: absolute;\n  top: 0;\n  left: 50%;\n  transform: translateX(-50%);\n  width: 60%;\n  height: 1px;\n  pointer-events: none;\n}\n\n.landing-flow-card:nth-child(1)::after { background: linear-gradient(90deg, transparent, rgba(239,68,68,0.7), transparent); }\n.landing-flow-card:nth-child(3)::after { background: linear-gradient(90deg, transparent, rgba(139,92,246,0.7), transparent); }\n.landing-flow-card:nth-child(5)::after { background: linear-gradient(90deg, transparent, rgba(251,191,36,0.7), transparent); }\n\n.landing-flow-card:hover {\n  border-color: var(--border-strong);\n  transform: translateY(-6px);\n  box-shadow: 0 20px 40px rgba(0,0,0,0.4);\n}\n\n.landing-flow-card:hover::before {\n  opacity: 0.7;\n}\n\n.landing-flow-step-num {\n  position: absolute;\n  top: 14px;\n  left: 14px;\n  width: clamp(28px, 1.8vw, 36px);\n  height: clamp(28px, 1.8vw, 36px);\n  border-radius: 50%;\n  font-family: var(--font-mono);\n  font-size: 0.8rem;\n  font-weight: var(--font-weight-medium);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  letter-spacing: 0;\n  background: rgba(0,0,0,0.4);\n  border: 1px solid rgba(255,255,255,0.08);\n}\n\n/* Mono-numbered chip with corner alignment per step */\n.landing-flow-card:nth-child(1) .landing-flow-step-num {\n  background: rgba(239,68,68,0.18);\n  border-color: rgba(239,68,68,0.45);\n  color: #fca5a5;\n}\n.landing-flow-card:nth-child(3) .landing-flow-step-num {\n  background: rgba(139,92,246,0.18);\n  border-color: rgba(139,92,246,0.45);\n  color: #c4b5fd;\n}\n.landing-flow-card:nth-child(5) .landing-flow-step-num {\n  background: rgba(251,191,36,0.18);\n  border-color: rgba(251,191,36,0.45);\n  color: #fcd34d;\n}\n\n/* Step num colors match their icon */\n.landing-flow-card:nth-child(1) .landing-flow-step-num {\n  background: var(--accent-primary-muted);\n  border: 1px solid var(--accent-primary-border);\n  color: var(--accent-primary);\n}\n.landing-flow-card:nth-child(3) .landing-flow-step-num {\n  background: rgba(139,92,246,0.12);\n  border: 1px solid rgba(139,92,246,0.3);\n  color: #8b5cf6;\n}\n.landing-flow-card:nth-child(5) .landing-flow-step-num {\n  background: rgba(251,191,36,0.12);\n  border: 1px solid rgba(251,191,36,0.3);\n  color: #fbbf24;\n}\n\n.landing-flow-icon {\n  width: clamp(48px, 3vw, 64px);\n  height: clamp(48px, 3vw, 64px);\n  border-radius: var(--radius-xl);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin: 0 auto var(--space-5);\n  border: 1px solid transparent;\n  position: relative;\n}\n\n.landing-flow-icon::after {\n  content: '';\n  position: absolute;\n  inset: -6px;\n  border-radius: 18px;\n  border: 1px dashed currentColor;\n  opacity: 0.25;\n}\n\n.landing-flow-icon-upload { background: rgba(239,68,68,0.16); color: var(--accent-primary); border-color: rgba(239,68,68,0.35); }\n.landing-flow-icon-play { background: rgba(139,92,246,0.16); color: #a78bfa; border-color: rgba(139,92,246,0.35); }\n.landing-flow-icon-master { background: rgba(251,191,36,0.16); color: #fbbf24; border-color: rgba(251,191,36,0.35); }\n\n.landing-flow-auto-badge {\n  position: absolute;\n  top: 16px;\n  right: 14px;\n  font-family: var(--font-mono);\n  font-size: 0.6rem;\n  font-weight: var(--font-weight-medium);\n  letter-spacing: 0.15em;\n  text-transform: uppercase;\n  color: #4ade80;\n  background: rgba(34,197,94,0.12);\n  border: 1px solid rgba(34,197,94,0.35);\n  padding: 0.25rem 0.5rem;\n  border-radius: 4px;\n  line-height: 1;\n  display: inline-flex;\n  align-items: center;\n  gap: 0.35rem;\n}\n\n.landing-flow-auto-badge::before {\n  content: '';\n  width: 4px;\n  height: 4px;\n  border-radius: 50%;\n  background: #4ade80;\n  box-shadow: 0 0 6px #4ade80;\n}\n\n.landing-flow-auto-badge-mixed {\n  color: #f59e0b;\n  background: rgba(245,158,11,0.1);\n  border-color: rgba(245,158,11,0.25);\n}\n\n.landing-flow-card h3 {\n  font-family: var(--font-display);\n  font-size: clamp(1.15rem, 1.4vw, 1.4rem);\n  font-weight: var(--font-weight-bold);\n  letter-spacing: -0.01em;\n  margin-bottom: var(--space-3);\n  color: var(--text-primary);\n}\n\n.landing-flow-card p {\n  font-family: var(--font-display);\n  font-size: 0.85rem;\n  line-height: 1.55;\n  color: var(--text-muted);\n  flex: 1;\n  font-weight: 400;\n}\n\n.landing-flow-arrow {\n  display: flex;\n  align-items: center;\n  align-self: center;\n  color: var(--text-disabled);\n  flex-shrink: 0;\n  opacity: 0.7;\n}\n\n/* Flow stagger */\n.landing-flow-grid .landing-animate:nth-child(1) { transition-delay: 0s; }\n.landing-flow-grid .landing-animate:nth-child(2) { transition-delay: 0.04s; }\n.landing-flow-grid .landing-animate:nth-child(3) { transition-delay: 0.08s; }\n.landing-flow-grid .landing-animate:nth-child(4) { transition-delay: 0.12s; }\n.landing-flow-grid .landing-animate:nth-child(5) { transition-delay: 0.16s; }\n.landing-flow-grid .landing-animate:nth-child(6) { transition-delay: 0.20s; }\n.landing-flow-grid .landing-animate:nth-child(7) { transition-delay: 0.24s; }\n.landing-flow-grid .landing-animate:nth-child(8) { transition-delay: 0.28s; }\n.landing-flow-grid .landing-animate:nth-child(9) { transition-delay: 0.32s; }\n\n/* ============================================\n   FEATURES — Showcase cards (alternating layout)\n   ============================================ */\n\n.landing-features {\n  padding: var(--space-16) 0;\n  position: relative;\n  z-index: 1;\n}\n\n.landing-showcase {\n  display: grid;\n  grid-template-columns: 1fr 1fr;\n  gap: var(--space-10);\n  align-items: center;\n  margin-bottom: var(--space-16);\n}\n\n.landing-showcase:last-child {\n  margin-bottom: 0;\n}\n\n.landing-showcase-reverse {\n  direction: rtl;\n}\n\n.landing-showcase-reverse > * {\n  direction: ltr;\n}\n\n.landing-showcase-text {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-4);\n}\n\n.landing-showcase-text h3 {\n  font-family: var(--font-display);\n  font-size: clamp(1.5rem, 2vw, 1.95rem);\n  font-weight: var(--font-weight-bold);\n  letter-spacing: -0.025em;\n  line-height: 1.1;\n  color: var(--text-primary);\n  margin: 0;\n}\n\n.landing-showcase-text p {\n  font-family: var(--font-display);\n  font-size: clamp(0.95rem, 1.1vw, 1.05rem);\n  line-height: 1.6;\n  color: var(--text-muted);\n  font-weight: 400;\n  margin: 0;\n}\n\n.landing-showcase-visual {\n  background: var(--bg-card);\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-2xl);\n  padding: var(--space-6);\n  backdrop-filter: var(--glass-blur);\n  overflow: hidden;\n}\n\n/* Showcase stagger */\n.landing-features .landing-showcase:nth-child(3) { transition-delay: 0.06s; }\n.landing-features .landing-showcase:nth-child(4) { transition-delay: 0.12s; }\n.landing-features .landing-showcase:nth-child(5) { transition-delay: 0.18s; }\n.landing-features .landing-showcase:nth-child(6) { transition-delay: 0.24s; }\n\n/* ---- Boss Map SVG ---- */\n.landing-map-svg {\n  width: 100%;\n  height: auto;\n  display: block;\n}\n\n/* ---- Mock Concept Card ---- */\n.landing-mock-concept {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-3);\n}\n\n.landing-mock-concept-header {\n  display: flex;\n  align-items: center;\n  gap: var(--space-2);\n  color: #0ea5e9;\n  font-size: var(--text-sm);\n  font-weight: var(--font-weight-semibold);\n}\n\n.landing-mock-concept-table {\n  width: 100%;\n  border-collapse: collapse;\n  font-size: var(--text-sm);\n}\n\n.landing-mock-concept-table th {\n  text-align: left;\n  padding: var(--space-2) var(--space-3);\n  font-size: 0.65rem;\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: var(--tracking-wider);\n  text-transform: uppercase;\n  color: var(--text-subtle);\n  border-bottom: 1px solid var(--border-subtle);\n}\n\n.landing-mock-concept-table td {\n  padding: var(--space-2) var(--space-3);\n  color: var(--text-muted);\n  border-bottom: 1px solid rgba(255,255,255,0.04);\n}\n\n.landing-mock-concept-table td:first-child {\n  font-weight: var(--font-weight-semibold);\n  color: var(--text-secondary);\n  font-size: 0.7rem;\n  letter-spacing: var(--tracking-wide);\n  text-transform: uppercase;\n}\n\n.landing-mock-concept-mastery {\n  display: flex;\n  align-items: center;\n  gap: var(--space-3);\n}\n\n.landing-mock-concept-bar {\n  flex: 1;\n  height: clamp(4px, 0.3vw, 6px);\n  background: rgba(255,255,255,0.06);\n  border-radius: 3px;\n  overflow: hidden;\n}\n\n.landing-mock-concept-fill {\n  width: 72%;\n  height: 100%;\n  background: linear-gradient(90deg, #0ea5e9, #8b5cf6);\n  border-radius: 3px;\n}\n\n.landing-mock-concept-mastery span {\n  font-size: 0.65rem;\n  font-weight: var(--font-weight-semibold);\n  color: var(--text-subtle);\n}\n\n/* ---- Mock Quiz ---- */\n.landing-mock-quiz {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-4);\n}\n\n.landing-mock-quiz-q {\n  font-size: var(--text-base);\n  font-weight: var(--font-weight-semibold);\n  color: var(--text-primary);\n  line-height: var(--leading-relaxed);\n}\n\n.landing-mock-quiz-options {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-2);\n}\n\n.landing-mock-quiz-opt {\n  display: flex;\n  align-items: center;\n  gap: var(--space-3);\n  padding: var(--space-2) var(--space-3);\n  border-radius: var(--radius-lg);\n  font-size: var(--text-sm);\n  color: var(--text-muted);\n  border: 1px solid var(--border-subtle);\n  background: transparent;\n}\n\n.landing-mock-quiz-opt-correct {\n  border-color: rgba(34,197,94,0.4);\n  background: rgba(34,197,94,0.06);\n  color: var(--text-primary);\n}\n\n.landing-mock-quiz-radio {\n  width: clamp(10px, 0.55vw, 14px);\n  height: clamp(10px, 0.55vw, 14px);\n  border-radius: 50%;\n  border: 1.5px solid var(--border-default);\n  flex-shrink: 0;\n}\n\n.landing-mock-quiz-radio-correct {\n  border-color: #22c55e;\n  background: radial-gradient(circle, #22c55e 40%, transparent 41%);\n}\n\n.landing-mock-quiz-opt svg {\n  margin-left: auto;\n}\n\n/* ============================================\n   LEVEL UP — Player system strip\n   ============================================ */\n\n.landing-levelup {\n  padding: var(--space-16) 0;\n  position: relative;\n  z-index: 1;\n}\n\n.landing-levelup-grid {\n  display: grid;\n  grid-template-columns: repeat(5, 1fr);\n  gap: 0;\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-2xl);\n  overflow: hidden;\n  background:\n    linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));\n  backdrop-filter: var(--glass-blur);\n}\n\n.landing-levelup-item {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  text-align: center;\n  gap: var(--space-3);\n  padding: var(--space-8) var(--space-4);\n  min-width: 0;\n  max-width: none;\n  position: relative;\n  transition: background var(--transition-normal);\n}\n\n.landing-levelup-item:not(:last-child)::after {\n  content: '';\n  position: absolute;\n  right: 0;\n  top: 12%;\n  bottom: 12%;\n  width: 1px;\n  background: linear-gradient(180deg, transparent, var(--border-subtle), transparent);\n}\n\n.landing-levelup-item:hover {\n  background: rgba(255,255,255,0.025);\n}\n\n.landing-levelup-icon {\n  width: clamp(46px, 2.6vw, 56px);\n  height: clamp(46px, 2.6vw, 56px);\n  border-radius: var(--radius-xl);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  margin-bottom: var(--space-2);\n  position: relative;\n}\n\n.landing-levelup-icon::after {\n  content: '';\n  position: absolute;\n  inset: -5px;\n  border-radius: 16px;\n  border: 1px dashed currentColor;\n  opacity: 0.22;\n}\n\n/* XP — gold */\n.landing-levelup-item:nth-child(1) .landing-levelup-icon {\n  background: rgba(251,191,36,0.12);\n  border: 1px solid rgba(251,191,36,0.3);\n  color: #fbbf24;\n}\n\n/* Progression — cyan */\n.landing-levelup-item:nth-child(2) .landing-levelup-icon {\n  background: rgba(56,189,248,0.12);\n  border: 1px solid rgba(56,189,248,0.3);\n  color: #38bdf8;\n}\n\n/* Equipment — purple */\n.landing-levelup-item:nth-child(3) .landing-levelup-icon {\n  background: rgba(167,139,250,0.12);\n  border: 1px solid rgba(167,139,250,0.3);\n  color: #a78bfa;\n}\n\n/* Boss Battles — red */\n.landing-levelup-item:nth-child(4) .landing-levelup-icon {\n  background: rgba(239,68,68,0.12);\n  border: 1px solid rgba(239,68,68,0.3);\n  color: #f87171;\n}\n\n/* Honor — amber */\n.landing-levelup-item:nth-child(5) .landing-levelup-icon {\n  background: rgba(245,158,11,0.12);\n  border: 1px solid rgba(245,158,11,0.3);\n  color: #f59e0b;\n}\n\n.landing-levelup-label {\n  font-family: var(--font-display);\n  font-size: 0.95rem;\n  font-weight: var(--font-weight-bold);\n  letter-spacing: -0.005em;\n  color: var(--text-primary);\n}\n\n.landing-levelup-desc {\n  font-family: var(--font-display);\n  font-size: 0.8rem;\n  color: var(--text-muted);\n  line-height: 1.5;\n  font-weight: 400;\n}\n\n/* Level-up stagger */\n.landing-levelup-grid .landing-animate:nth-child(2) { transition-delay: 0.06s; }\n.landing-levelup-grid .landing-animate:nth-child(3) { transition-delay: 0.12s; }\n.landing-levelup-grid .landing-animate:nth-child(4) { transition-delay: 0.18s; }\n.landing-levelup-grid .landing-animate:nth-child(5) { transition-delay: 0.24s; }\n\n/* ---- Motivational sentiments ---- */\n.landing-sentiment {\n  text-align: center;\n  font-family: var(--font-serif);\n  font-style: italic;\n  font-weight: 400;\n  font-size: clamp(1.5rem, 2.4vw, 2.4rem);\n  letter-spacing: -0.015em;\n  line-height: 1.25;\n  color: var(--text-secondary);\n  padding: var(--space-10) var(--space-6);\n  position: relative;\n  z-index: 1;\n  max-width: clamp(420px, 36vw, 760px);\n  margin: 0 auto;\n}\n\n.landing-sentiment::before,\n.landing-sentiment::after {\n  content: '';\n  display: block;\n  width: 24px;\n  height: 1px;\n  background: var(--accent-primary);\n  opacity: 0.5;\n  margin: var(--space-4) auto;\n}\n\n.landing-sentiment::before {\n  margin-bottom: var(--space-5);\n}\n\n.landing-sentiment::after {\n  margin-top: var(--space-5);\n}\n\n/* ---- Feature section lede ----\n   Replaced the Study Features / Gamification toggle: the two tabs described the\n   same feature list from two angles, so they were merged into one run of\n   showcases that each name their learning method. This line sets that up. */\n.landing-features-lede {\n  max-width: 58ch;\n  margin: calc(-1 * var(--space-6)) auto 0;\n  padding: 0 var(--space-6);\n  text-align: center;\n  font-family: var(--font-display);\n  font-size: clamp(0.95rem, 1.15vw, 1.1rem);\n  line-height: 1.6;\n  color: var(--text-muted);\n}\n\n/* ---- Method tag ----\n   The learning method a showcase implements; the h3 under it is the mechanic\n   that carries it. Uses the same uppercase micro-label grammar as the rest of\n   the page so it reads as an eyebrow, not a second heading. */\n.landing-method-tag {\n  align-self: flex-start;\n  display: inline-flex;\n  align-items: center;\n  gap: 0.4rem;\n  padding: 0.3rem 0.7rem;\n  border: 1px solid rgba(var(--axis, 239 68 68) / 0.34);\n  border-radius: var(--radius-full);\n  background: rgba(var(--axis, 239 68 68) / 0.09);\n  font-family: var(--font-display);\n  font-size: 0.66rem;\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: 0.14em;\n  text-transform: uppercase;\n  line-height: 1;\n  color: rgba(var(--axis, 248 113 113) / 0.95);\n  margin-bottom: calc(-1 * var(--space-2));\n}\n\n/* ---- Axis assignment ----\n   Each showcase declares WHICH WAY OF KNOWING its mechanic trains, and that one\n   attribute drives every coloured thing inside it. The mapping is the point of\n   the whole system, so it is stated once, here, rather than being smeared\n   across the markup as hardcoded colours:\n\n     understanding  the map, the builds, the tree — things whose meaning is in\n                    how the parts interact, so you cannot hold one without the rest\n     knowledge      the cards, the drills, the deck — plain recall, memorised\n                    and rehearsed on a schedule\n     intuition      PvP, live combat — answering faster than you can reason,\n                    which only works when the knowing has stopped being recall\n\n   An element with no data-axis keeps the old red. Nothing here is required.\n\n   Note there is deliberately NO per-axis rule in this file. Landing.jsx\n   resolves each tagged element's --axis from its own attribute, so the\n   stylesheet never holds a list of axis names that could fall out of step with\n   the markup. Adding an axis means declaring its colour token, and nothing\n   else, anywhere. */\n\n/* The element-scale echo of the page-scale cloud: the same hue, same softness,\n   bled in from the card's leading edge. Sits behind the content and never\n   touches a text box, so it tints without costing contrast. */\n.landing [data-axis] {\n  position: relative;\n}\n\n/* A gradient is clipped by its own box, so if it is still opaque when it\n   reaches an edge, that edge becomes a straight line — measured at 8.2\n   luminance units against a 4.4 noise floor on the first version, which is a\n   visible seam across the top of every card. The geometry is therefore solved\n   rather than tuned: centred vertically at 50% with a 44% radius, the fade\n   completes at 6% and 94% of the box height, INSIDE it, so there is no top or\n   bottom edge to see at any card height. Horizontally it still meets the box,\n   but at 32% ± 40% the alpha there is under 0.01 — below the noise floor, and\n   it lands off the content anyway. */\n.landing [data-axis]::before {\n  content: '';\n  position: absolute;\n  inset: -12% -6%;\n  z-index: -1;\n  pointer-events: none;\n  background: radial-gradient(40% 44% at 32% 50%,\n    rgba(var(--axis) / 0.085) 0%,\n    rgba(var(--axis) / 0.032) 46%,\n    rgba(var(--axis) / 0) 100%);\n}\n\n.landing .landing-showcase-reverse[data-axis]::before {\n  background: radial-gradient(40% 44% at 68% 50%,\n    rgba(var(--axis) / 0.085) 0%,\n    rgba(var(--axis) / 0.032) 46%,\n    rgba(var(--axis) / 0) 100%);\n}\n\n/* Edge: one hairline in the axis hue under each showcase. This is the \"clean\"\n   half of the brief — a 1px rule reads as precision where a glow reads as\n   decoration, and it gives the eye a real boundary between mechanics. */\n.landing [data-axis]::after {\n  content: '';\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: calc(-1 * var(--space-8));\n  height: 1px;\n  pointer-events: none;\n  background: linear-gradient(90deg,\n    rgba(var(--axis) / 0.30) 0%,\n    rgba(var(--axis) / 0.10) 34%,\n    rgba(255, 255, 255, 0.04) 62%,\n    transparent 100%);\n}\n\n.landing .landing-showcase-reverse[data-axis]::after {\n  background: linear-gradient(270deg,\n    rgba(var(--axis) / 0.30) 0%,\n    rgba(var(--axis) / 0.10) 34%,\n    rgba(255, 255, 255, 0.04) 62%,\n    transparent 100%);\n}\n\n.landing [data-axis]:last-child::after { display: none; }\n\n.landing-method-tag::before {\n  content: '';\n  width: 4px;\n  height: 4px;\n  border-radius: 50%;\n  background: currentColor;\n}\n\n/* Wide showcases stack their text in a plain <div>, so the tag needs its own\n   block spacing there rather than the flex-column gap. */\n.landing-showcase-wide-header .landing-method-tag {\n  margin-bottom: var(--space-2);\n}\n\n/* ---- Live skill-tree preview: the REAL SkillTreeViewport, read-only ----\n   Rendered in a FULL-WIDTH wide-showcase so the tree has horizontal room. */\n.landing-showcase-wide-visual.landing-showcase-visual--skilltree {\n  display: block;\n  width: 100%;\n  background: var(--bg-card);\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-2xl);\n  backdrop-filter: var(--glass-blur);\n  overflow: hidden;\n}\n.landing-skilltree-embed {\n  position: relative;\n  width: 100%;\n  /* Wider card → taller canvas so the branching tree spreads out, not cramped. */\n  height: clamp(360px, 40vw, 560px);\n  overflow: hidden;\n  border-radius: var(--radius-2xl);\n  /* Read-only preview — the interactive tree is the demo map above. Block\n     clicks/hover so the marketing card never mutates the anon loadout. */\n  pointer-events: none;\n}\n.landing-skilltree-embed .skill-tree-viewport,\n.landing-skilltree-embed .skill-tree-viewport__body {\n  width: 100%;\n  height: 100%;\n}\n\n/* ---- Mock PvP Combat (matching prod PvPHPBar) ---- */\n.landing-mock-pvp {\n  display: flex;\n  align-items: center;\n  gap: var(--space-3);\n  padding: var(--space-4) 0;\n}\n\n.landing-mock-pvp-side {\n  flex: 1;\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-1);\n  min-width: 0;\n}\n\n.landing-mock-pvp-header {\n  display: flex;\n  align-items: baseline;\n  justify-content: space-between;\n  gap: var(--space-2);\n}\n\n.landing-mock-pvp-name {\n  font-size: var(--text-xs);\n  font-weight: var(--font-weight-bold);\n  text-transform: uppercase;\n  letter-spacing: var(--tracking-wide);\n}\n\n.landing-mock-pvp-name-player { color: #22c55e; }\n.landing-mock-pvp-name-opponent { color: #f87171; }\n\n.landing-mock-pvp-hp-value {\n  font-size: var(--text-xs);\n  font-weight: var(--font-weight-bold);\n  color: var(--text-secondary);\n  font-variant-numeric: tabular-nums;\n}\n\n.landing-mock-pvp-track {\n  height: 8px;\n  border-radius: var(--radius-full);\n  overflow: hidden;\n  background: rgba(255,255,255,0.06);\n}\n\n.landing-mock-pvp-fill {\n  height: 100%;\n  border-radius: var(--radius-full);\n  transition: width 0.5s ease;\n}\n\n.landing-mock-pvp-fill-player { background: #22c55e; }\n.landing-mock-pvp-fill-opponent { background: #f87171; }\n\n.landing-mock-pvp-vs {\n  font-size: var(--text-xs);\n  font-weight: var(--font-weight-bold);\n  color: var(--text-muted);\n  letter-spacing: 0.1em;\n  flex-shrink: 0;\n  padding: 0 var(--space-1);\n}\n\n/* ---- Wide showcase (for Gold Card) ---- */\n/* `clip`, NOT `hidden`, and the difference is the whole reason these two blocks\n   used to carry a scrollbar. `overflow-x: hidden` with `overflow-y` left at\n   `visible` does not mean \"clip sideways\": the used value of `visible` becomes\n   `auto` whenever the other axis is neither `visible` nor `clip`, so the block\n   silently became a SCROLL CONTAINER on the vertical axis. Then `.landing\n   [data-axis]::before` — the axis glow, deliberately bled past the box at\n   `inset: -12% -6%` — landed in that container's scrollable overflow region and\n   gave it a scroll range. Measured at four widths: the range was 12.0% of the\n   block's height and 6.0% of its width EVERY time (153/1276, 87/726, 82/678,\n   69/576) — the glow's inset sets that extent exactly. (It was not the ONLY\n   thing in there, as this comment first claimed: suppressing the glow leaves 32px\n   on the Gold Card block, which is its `::after` hairline at `bottom: -32px`. So\n   a reader who dragged that inner bar down 32px would have reached a hairline\n   rather than nothing. The conclusion stands — it was a scrollbar onto\n   decoration — but \"nothing else\" was wrong.)\n\n   🔴 THE LAYOUT DOES MOVE, and this comment said it did not. A scroll container\n   with a real range RESERVES A GUTTER: under the old rule both blocks lose 10px\n   of content width (clientWidth 712 vs 722), which costs the Skill Tree block's\n   header paragraph a fourth line and takes that block 726 -> 749px and the\n   document 8776 -> 8799. So `clip` makes the page ~23px SHORTER and both blocks\n   10px WIDER — a real improvement, not a no-op.\n   The first A/B here reported zero deltas because it ran through the harness's\n   `cdp-pipe.mjs`, which launches chrome with `--hide-scrollbars` — that flag pins\n   the gutter, the one quantity that changes, at zero. Re-measure this with\n   `2026-08-05-landing-scroll/cdp-pipe-scrollbars.mjs`, never the default driver.\n   (In-run A/B is still mandatory for a different reason: the page renders with or\n   without its ~950px demo section depending on whether the anon token mint is\n   rate-limited, so a cross-LOAD diff is unattributable.)\n   ONE thing does come back with it: `.landing [data-axis]::after`, the 1px axis\n   hairline at `bottom: -32px`, which sat outside the old clip and never painted\n   on the Gold Card block while five other showcases drew theirs. Measured on\n   pixels — the row at that exact offset goes 17.78 -> 33.69 while every\n   neighbouring row moves under 0.11. The Skill Tree block still draws none, and\n   should not: it is `:last-child`, which `.landing [data-axis]:last-child::after`\n   sets to `display: none` on purpose. The glow's vertical bleed is NOT a visible\n   change either way — by the box edge its alpha is already ~0 by the geometry\n   the comment above this describes, and the pixels agree (under 0.11 per row).\n   The two declarations are a FALLBACK PAIR, not a leftover: a browser without\n   `overflow-x: clip` (pre-16 Safari) keeps the old behaviour rather than silently\n   dropping the clip. Verified in Chrome 144 and Firefox 153 — both resolve the\n   pair to `clip`, gutter 0, scroll refused. Do not delete the second line.\n   (An earlier draft called losing the horizontal clip \"the one thing worse than\n   the scrollbar\". That was unfounded: with `overflow-x: visible` on both blocks\n   nothing measurable changes — the 43px of bleed is entirely the glow, it lands\n   in a 354px page margin, and `.landing`'s own `overflow-x: hidden` absorbs\n   anything that escapes. The pair is cheap insurance, not a rescue.)\n\n   ⚠️ KNOWN AND NOT ADDRESSED: `clip` on ONE axis clips one axis. The vertical\n   edges of these blocks are now unbounded, where the old scroll container clipped\n   both. Nothing real is out there today (it is why the hairline can paint at all),\n   but `overflow: clip; overflow-clip-margin: 33px` was measured to give layout\n   numbers bit-identical to this rule — 722 / 1276 / 726 / 8776, gutter 0, scroll\n   refused — while bounding the escape at 33px, which is just enough for the\n   hairline's `bottom: -32px` + 1px. It is NOT shipped because the containment\n   half was never confirmed on an instrument of mine that could tell the two apart.\n   See the todo before adopting it, and note it couples this rule to `--space-8`. */\n.landing-showcase-wide {\n  margin-bottom: var(--space-16);\n  overflow-x: hidden; /* fallback for browsers without `clip` */\n  overflow-x: clip;\n}\n\n.landing-showcase-wide-header {\n  display: flex;\n  align-items: flex-start;\n  gap: var(--space-4);\n  margin-bottom: var(--space-6);\n}\n\n.landing-showcase-wide-header h3 {\n  font-size: var(--text-xl);\n  font-weight: var(--font-weight-semibold);\n  color: var(--text-primary);\n  margin-bottom: var(--space-2);\n}\n\n.landing-showcase-wide-header p {\n  font-size: var(--text-base);\n  line-height: var(--leading-relaxed);\n  color: var(--text-muted);\n}\n\n.landing-showcase-wide-visual {\n  display: flex;\n  justify-content: center;\n}\n\n/* ---- Build Rows (synergistic card loadouts) ---- */\n.landing-builds-list {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-6);\n  overflow-x: hidden;\n}\n\n.landing-build-row {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-3);\n}\n\n.landing-build-meta {\n  display: flex;\n  align-items: flex-start;\n  gap: var(--space-3);\n}\n\n/* The row's synergy copy is a LIST of authored lines (a keystone build carries\n   one per augmented card), so it stacks. Each line is its own registry string\n   and gets its own box rather than being run together into one sentence. */\n.landing-build-synergy {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-1);\n  min-width: 0;\n  font-size: var(--text-sm);\n  color: var(--text-muted);\n  font-style: italic;\n}\n\n.landing-build-line {\n  line-height: var(--leading-relaxed);\n}\n\n/* FONT FIDELITY. PillCard.css declares no font-family at all, so a mounted card\n   inherits the page's — and `.landing` sets var(--font-display) (Outfit) at the\n   top of this file, while the game draws every card in var(--font-primary) from\n   design-tokens.css:12. The restore goes HERE, on the smallest box that holds\n   only mounted game components: the cards then read exactly as they do in game,\n   while the landing's own chrome (row label, synergy lines) keeps the page's\n   display face. Same fix the compendium makes at Compendium.css:31, scoped instead of page\n   -wide. */\n.landing-build-cards {\n  display: flex;\n  gap: var(--space-2);\n  flex-wrap: wrap;\n  justify-content: center;\n  font-family: var(--font-primary);\n}\n\n/* The gap is 8 and not 12 for a MEASURED reason. This section's column is\n   770px wide with 24px of padding, so a build row has 722px to fill, and a\n   keystone build is a keystone card plus its augmented drugs: four 172px cards\n   = 688px. At a 12px gap that is 724px and the row broke 3 + 1, orphaning the\n   last card, for want of two pixels. At 8px it is 712px and the build reads as\n   one line. Rows still wrap (a five-card build would), which is the behaviour a\n   registry-driven list needs; this only stops a build the registry actually\n   holds from wrapping for nothing. */\n\n/* Build label. Tinted by the row's OWN accent, handed down as --build-accent by\n   Landing.jsx from BUILD_ROWS (the keystone's accent, or the lead card's\n   skill-tree node colour) — the same value the cards on that row paint their\n   stripe and icon ring with. There is deliberately no per-build colour class:\n   a build that arrives from the registry tomorrow cannot land here unstyled.\n   The 82/18 mix with white lifts the darkest accent (#ef4444) off a near-black\n   background at this size without inventing a second palette. */\n.landing-build-label {\n  font-size: 0.6rem;\n  font-weight: var(--font-weight-bold);\n  text-transform: uppercase;\n  letter-spacing: var(--tracking-widest);\n  padding: 0.15rem 0.5rem;\n  border-radius: var(--radius-full);\n  width: fit-content;\n  flex-shrink: 0;\n  color: color-mix(in srgb, var(--build-accent, var(--text-muted)) 82%, #fff);\n  background: color-mix(in srgb, var(--build-accent, transparent) 12%, transparent);\n  border: 1px solid color-mix(in srgb, var(--build-accent, transparent) 28%, transparent);\n}\n\n/* ---- Mock Deck Upkeep ---- */\n.landing-mock-upkeep {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-4);\n}\n\n.landing-mock-upkeep-card {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-2);\n}\n\n.landing-mock-upkeep-info {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n}\n\n.landing-mock-upkeep-name {\n  font-size: var(--text-sm);\n  font-weight: var(--font-weight-semibold);\n  color: var(--text-primary);\n}\n\n.landing-mock-upkeep-status {\n  font-size: 0.65rem;\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: var(--tracking-wide);\n  text-transform: uppercase;\n  padding: 0.15rem 0.5rem;\n  border-radius: var(--radius-full);\n}\n\n.landing-mock-upkeep-status-warn {\n  color: #f59e0b;\n  background: rgba(245,158,11,0.1);\n  border: 1px solid rgba(245,158,11,0.25);\n}\n\n.landing-mock-upkeep-status-good {\n  color: #22c55e;\n  background: rgba(34,197,94,0.1);\n  border: 1px solid rgba(34,197,94,0.25);\n}\n\n.landing-mock-upkeep-bar {\n  height: clamp(6px, 0.35vw, 8px);\n  background: rgba(255,255,255,0.06);\n  border-radius: 4px;\n  overflow: hidden;\n}\n\n.landing-mock-upkeep-fill {\n  height: 100%;\n  border-radius: 4px;\n  transition: width 0.6s ease;\n}\n\n.landing-mock-upkeep-fill-warn {\n  background: linear-gradient(90deg, #f59e0b, #fbbf24);\n}\n\n.landing-mock-upkeep-fill-good {\n  background: linear-gradient(90deg, #22c55e, #4ade80);\n}\n\n/* Decay timer inline */\n.landing-mock-upkeep-decay {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.25rem;\n  font-size: 0.65rem;\n  font-weight: var(--font-weight-semibold);\n  margin-top: var(--space-1);\n}\n\n.landing-mock-upkeep-decay-danger {\n  color: #f87171;\n}\n\n.landing-mock-upkeep-decay-safe {\n  color: var(--text-subtle);\n}\n\n/* ============================================\n   SOCIAL PROOF — Stats bar\n   ============================================ */\n\n.landing-proof {\n  padding: var(--space-12) 0;\n  position: relative;\n  z-index: 1;\n}\n\n/* Side-by-side two-panel grid */\n.landing-proof-grid {\n  display: grid;\n  grid-template-columns: 1fr;\n  gap: var(--space-4);\n}\n\n.landing-proof-panel {\n  background:\n    linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01));\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-2xl);\n  padding: var(--space-6) var(--space-6) var(--space-6);\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-5);\n  position: relative;\n  overflow: hidden;\n}\n\n.landing-proof-panel::before {\n  content: '';\n  position: absolute;\n  top: 0;\n  left: 24px;\n  right: 24px;\n  height: 1px;\n  background: linear-gradient(90deg, transparent, var(--border-default), transparent);\n}\n\n.landing-proof-panel-header {\n  display: flex;\n  align-items: center;\n  gap: var(--space-3);\n  padding-bottom: var(--space-3);\n  border-bottom: 1px dashed var(--border-subtle);\n}\n\n.landing-proof-panel-dot {\n  width: clamp(6px, 0.4vw, 8px);\n  height: clamp(6px, 0.4vw, 8px);\n  border-radius: 50%;\n  flex-shrink: 0;\n}\n\n.landing-proof-panel-dot-live {\n  background: #22c55e;\n  box-shadow: 0 0 8px rgba(34, 197, 94, 0.7);\n  animation: landing-pulse-dot 2s ease-in-out infinite;\n}\n\n@keyframes landing-pulse-dot {\n  0%, 100% { opacity: 1; }\n  50% { opacity: 0.4; }\n}\n\n.landing-proof-panel-title {\n  font-family: var(--font-mono);\n  font-size: 0.72rem;\n  font-weight: var(--font-weight-medium);\n  color: var(--text-secondary);\n  text-transform: uppercase;\n  letter-spacing: 0.18em;\n  flex: 1;\n}\n\n.landing-proof-version {\n  font-family: var(--font-mono);\n  font-size: 0.62rem;\n  font-weight: var(--font-weight-medium);\n  color: #60a5fa;\n  background: rgba(59, 130, 246, 0.1);\n  border: 1px solid rgba(59, 130, 246, 0.3);\n  padding: 0.15rem 0.4rem;\n  border-radius: 4px;\n  letter-spacing: 0.08em;\n}\n\n.landing-proof-stats {\n  display: flex;\n  align-items: flex-end;\n  justify-content: space-evenly;\n  gap: var(--space-4);\n  flex: 1;\n}\n\n.landing-proof-stat {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  gap: var(--space-2);\n  min-width: 0;\n}\n\n/* Live panel is now the sole (full-width) proof panel, so its 4 stats spread\n   across a single row. (Was a 2x2 grid when it sat in a half-width column next\n   to the since-removed Game Content panel.) Mobile drops back to 2x2 below. */\n.landing-proof-panel-live .landing-proof-stats {\n  display: grid;\n  grid-template-columns: repeat(4, 1fr);\n  gap: var(--space-3);\n  align-items: end;\n}\n\n.landing-proof-number {\n  font-family: var(--font-display);\n  font-size: clamp(1.65rem, 2.8vw, 2.4rem);\n  font-weight: var(--font-weight-bold);\n  color: var(--text-primary);\n  font-variant-numeric: tabular-nums;\n  line-height: 1;\n  letter-spacing: -0.025em;\n}\n\n.landing-proof-panel-live .landing-proof-number {\n  color: var(--text-primary);\n  background: linear-gradient(180deg, #ffffff 0%, #d1d5db 100%);\n  -webkit-background-clip: text;\n  background-clip: text;\n  -webkit-text-fill-color: transparent;\n}\n\n.landing-proof-label {\n  font-family: var(--font-mono);\n  font-size: 0.62rem;\n  font-weight: var(--font-weight-medium);\n  color: var(--text-subtle);\n  text-transform: uppercase;\n  letter-spacing: 0.14em;\n  white-space: nowrap;\n}\n\n/* ============================================\n   PRICING — Tier cards\n   ============================================ */\n\n.landing-pricing {\n  padding: var(--space-16) 0;\n  position: relative;\n  z-index: 1;\n}\n\n.landing-pricing-title {\n  text-align: center;\n  font-family: var(--font-display);\n  font-size: clamp(1.85rem, 3vw, 2.6rem);\n  font-weight: var(--font-weight-bold);\n  letter-spacing: -0.025em;\n  line-height: 1.1;\n  /* The support line below now carries the gap down to the cards. */\n  margin-bottom: var(--space-4);\n  color: var(--text-primary);\n  max-width: 540px;\n  margin-left: auto;\n  margin-right: auto;\n}\n\n/* The concrete claim under the rhetorical headline. Same 540px measure as the\n   title so the two share an edge; muted so the headline stays the loud one. */\n.landing-pricing-sub {\n  text-align: center;\n  font-family: var(--font-display);\n  font-size: clamp(1.05rem, 1.25vw, 1.25rem);\n  line-height: 1.55;\n  font-weight: 400;\n  color: var(--text-muted);\n  max-width: 540px;\n  margin: 0 auto var(--space-12);\n}\n\n.landing-pricing-sub strong {\n  font-weight: var(--font-weight-bold);\n  color: var(--text-primary);\n}\n\n.landing-pricing-title em {\n  font-family: var(--font-serif);\n  font-style: italic;\n  font-weight: 400;\n  color: var(--accent-primary);\n  font-size: 1.1em;\n}\n\n.landing-pricing-grid {\n  display: grid;\n  grid-template-columns: repeat(3, 1fr);\n  gap: var(--space-5);\n  max-width: clamp(680px, 44vw, 980px);\n  margin: 0 auto;\n}\n\n.landing-pricing-dlc-row {\n  display: flex;\n  justify-content: center;\n  gap: var(--space-5);\n  max-width: clamp(680px, 44vw, 980px);\n  margin: var(--space-5) auto 0;\n}\n\n.landing-pricing-dlc-row .landing-pricing-card {\n  flex: 0 1 calc(50% - var(--space-2));\n  max-width: clamp(301px, 19vw, 430px);\n}\n\n.landing-pricing-card {\n  background:\n    linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01));\n  border: 1px solid var(--border-subtle);\n  border-radius: var(--radius-2xl);\n  padding: var(--space-8) var(--space-6);\n  display: flex;\n  flex-direction: column;\n  align-items: stretch;\n  text-align: left;\n  position: relative;\n  transition: all var(--transition-normal);\n  backdrop-filter: var(--glass-blur);\n}\n\n.landing-pricing-card:hover {\n  border-color: var(--border-default);\n  transform: translateY(-4px);\n  box-shadow: 0 20px 40px rgba(0,0,0,0.35);\n}\n\n.landing-pricing-card-highlight {\n  border-color: rgba(168, 85, 247, 0.5);\n  background:\n    linear-gradient(180deg, rgba(168, 85, 247, 0.12) 0%, rgba(168, 85, 247, 0.02) 70%, rgba(255,255,255,0.01) 100%);\n  box-shadow:\n    0 0 0 1px rgba(168,85,247,0.15) inset,\n    0 0 32px rgba(168,85,247,0.18);\n  transform: scale(1.03);\n}\n\n.landing-pricing-card-highlight::before {\n  content: '';\n  position: absolute;\n  top: -1px;\n  left: 10%;\n  right: 10%;\n  height: 1px;\n  background: linear-gradient(90deg, transparent, rgba(168,85,247,0.9), transparent);\n}\n\n.landing-pricing-card-highlight:hover {\n  transform: scale(1.04) translateY(-4px);\n  border-color: rgba(168, 85, 247, 0.7);\n}\n\n.landing-pricing-card-naplex {\n  border-color: rgba(var(--dlc-naplex-rgb), 0.35);\n  background:\n    linear-gradient(180deg, rgba(var(--dlc-naplex-rgb), 0.08) 0%, rgba(255,255,255,0.01) 100%);\n}\n\n.landing-pricing-badge {\n  position: absolute;\n  top: -11px;\n  left: 50%;\n  transform: translateX(-50%);\n  z-index: 1;\n  background: linear-gradient(135deg, #2a1a3e, #4c1d95);\n  border: 1px solid rgba(168, 85, 247, 0.6);\n  color: #e9d5ff;\n  font-family: var(--font-mono);\n  font-size: 0.62rem;\n  font-weight: var(--font-weight-medium);\n  text-transform: uppercase;\n  letter-spacing: 0.18em;\n  padding: 0.3rem 0.75rem;\n  border-radius: var(--radius-full);\n  white-space: nowrap;\n  box-shadow: 0 4px 16px rgba(168,85,247,0.3);\n}\n\n.landing-pricing-badge-green {\n  background: #122b22;\n  border-color: rgba(var(--dlc-naplex-rgb), 0.5);\n  color: var(--dlc-naplex-hover);\n}\n\n.landing-pricing-name {\n  font-family: var(--font-mono);\n  font-size: 0.72rem;\n  font-weight: var(--font-weight-medium);\n  color: var(--text-muted);\n  text-transform: uppercase;\n  letter-spacing: 0.22em;\n  margin-bottom: var(--space-3);\n}\n\n.landing-pricing-price {\n  font-family: var(--font-display);\n  font-size: clamp(2.4rem, 4vw, 3.4rem);\n  font-weight: var(--font-weight-black);\n  letter-spacing: -0.04em;\n  color: var(--text-primary);\n  margin-bottom: var(--space-2);\n  line-height: 1;\n  font-variant-numeric: tabular-nums;\n}\n\n.landing-pricing-price span {\n  font-family: var(--font-display);\n  font-size: 0.95rem;\n  font-weight: var(--font-weight-medium);\n  color: var(--text-muted);\n  letter-spacing: 0;\n}\n\n.landing-pricing-price-was {\n  font-family: var(--font-display);\n  font-size: 1.1rem;\n  font-weight: var(--font-weight-medium);\n  color: var(--text-disabled);\n  text-decoration: line-through;\n  margin-right: 0.5rem;\n  letter-spacing: 0;\n}\n\n.landing-pricing-effective {\n  font-family: var(--font-display);\n  font-size: 0.78rem;\n  color: var(--text-subtle);\n  margin-bottom: var(--space-4);\n  font-style: italic;\n}\n\n.landing-pricing-features {\n  list-style: none;\n  padding: 0;\n  margin: var(--space-5) 0 var(--space-6);\n  flex: 1;\n  border-top: 1px dashed var(--border-subtle);\n  padding-top: var(--space-5);\n}\n\n.landing-pricing-features li {\n  font-family: var(--font-display);\n  font-size: 0.88rem;\n  color: var(--text-secondary);\n  padding: var(--space-2) 0;\n  line-height: 1.5;\n  position: relative;\n  padding-left: 1.25rem;\n  font-weight: 400;\n}\n\n.landing-pricing-features li::before {\n  content: '';\n  position: absolute;\n  left: 0;\n  top: 0.7rem;\n  width: 6px;\n  height: 6px;\n  border-radius: 50%;\n  background: var(--accent-primary);\n  opacity: 0.7;\n}\n\n.landing-pricing-card-highlight .landing-pricing-features li::before { background: #c084fc; }\n.landing-pricing-card-naplex .landing-pricing-features li::before { background: var(--dlc-naplex-hover); }\n.landing-pricing-card-ptcb .landing-pricing-features li::before { background: var(--dlc-ptcb); }\n.landing-pricing-card-mpje .landing-pricing-features li::before { background: var(--dlc-mpje-hover); }\n\n.landing-pricing-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 100%;\n  padding: 0.85rem 0;\n  font-family: var(--font-display);\n  font-size: 0.85rem;\n  font-weight: var(--font-weight-semibold);\n  letter-spacing: 0.06em;\n  text-transform: uppercase;\n  text-align: center;\n  text-decoration: none;\n  border-radius: var(--radius-full);\n  transition: all var(--transition-fast);\n}\n\n.landing-pricing-btn-free {\n  background: rgba(107, 114, 128, 0.15);\n  border: 1px solid rgba(107, 114, 128, 0.3);\n  color: var(--text-secondary);\n}\n\n.landing-pricing-btn-free:hover {\n  background: rgba(107, 114, 128, 0.3);\n  color: var(--text-primary);\n}\n\n.landing-pricing-btn-premium {\n  background: rgba(59, 130, 246, 0.15);\n  border: 1px solid rgba(59, 130, 246, 0.3);\n  color: #60a5fa;\n}\n\n.landing-pricing-btn-premium:hover {\n  background: rgba(59, 130, 246, 0.3);\n  color: #93c5fd;\n}\n\n.landing-pricing-btn-naplex {\n  background: rgba(var(--dlc-naplex-rgb), 0.15);\n  border: 1px solid rgba(var(--dlc-naplex-rgb), 0.3);\n  color: var(--dlc-naplex-hover);\n}\n\n.landing-pricing-btn-naplex:hover {\n  background: rgba(var(--dlc-naplex-rgb), 0.3);\n  color: #a7f3d0;\n}\n\n.landing-pricing-card-ptcb {\n  border-color: rgba(var(--dlc-ptcb-rgb), 0.4);\n  background: linear-gradient(180deg, rgba(var(--dlc-ptcb-rgb), 0.06) 0%, var(--bg-surface) 100%);\n}\n\n.landing-pricing-badge-ptcb {\n  background: rgba(var(--dlc-ptcb-rgb), 0.14);\n  border-color: rgba(var(--dlc-ptcb-rgb), 0.5);\n  color: var(--dlc-ptcb);\n}\n\n.landing-pricing-btn-ptcb {\n  background: rgba(var(--dlc-ptcb-rgb), 0.15);\n  border: 1px solid rgba(var(--dlc-ptcb-rgb), 0.3);\n  color: var(--dlc-ptcb);\n}\n\n.landing-pricing-btn-ptcb:hover {\n  background: rgba(var(--dlc-ptcb-rgb), 0.3);\n  color: var(--dlc-ptcb-hover);\n}\n\n.landing-pricing-card-mpje {\n  border-color: rgba(var(--dlc-mpje-rgb), 0.4);\n  background: linear-gradient(180deg, rgba(var(--dlc-mpje-rgb), 0.06) 0%, var(--bg-surface) 100%);\n}\n\n.landing-pricing-badge-mpje {\n  background: #0c1a33;\n  border-color: rgba(var(--dlc-mpje-rgb), 0.5);\n  color: var(--dlc-mpje-hover);\n}\n\n.landing-pricing-btn-mpje {\n  background: rgba(var(--dlc-mpje-rgb), 0.15);\n  border: 1px solid rgba(var(--dlc-mpje-rgb), 0.3);\n  color: var(--dlc-mpje-hover);\n}\n\n.landing-pricing-btn-mpje:hover {\n  background: rgba(var(--dlc-mpje-rgb), 0.3);\n  color: #93c5fd;\n}\n\n.landing-pricing-btn-purple {\n  background: rgba(168, 85, 247, 0.15);\n  border: 1px solid rgba(168, 85, 247, 0.3);\n  color: #c084fc;\n}\n\n.landing-pricing-btn-purple:hover {\n  background: rgba(168, 85, 247, 0.3);\n  color: #e0b3ff;\n}\n\n.landing-pricing-footnote {\n  text-align: center;\n  font-size: var(--text-sm);\n  color: var(--text-subtle);\n  margin-top: var(--space-8);\n  line-height: var(--leading-relaxed);\n}\n\n.landing-pricing-footnote a {\n  color: var(--accent-primary);\n  text-decoration: none;\n}\n\n.landing-pricing-footnote a:hover {\n  text-decoration: underline;\n}\n\n/* Pricing stagger */\n.landing-pricing-grid .landing-animate:nth-child(2) { transition-delay: 0.06s; }\n.landing-pricing-grid .landing-animate:nth-child(3) { transition-delay: 0.12s; }\n.landing-pricing-grid .landing-animate:nth-child(4) { transition-delay: 0.18s; }\n\n/* ---- Bottom CTA ---- */\n.landing-bottom-cta {\n  padding: var(--space-16) 0 calc(var(--space-16) * 1.5);\n  text-align: center;\n  position: relative;\n  z-index: 1;\n}\n\n.landing-bottom-cta::before {\n  content: '';\n  display: block;\n  width: 1px;\n  height: 60px;\n  background: linear-gradient(180deg, transparent, var(--border-default));\n  margin: 0 auto var(--space-12);\n}\n\n.landing-bottom-title {\n  font-family: var(--font-display);\n  font-size: clamp(2.4rem, 5vw, 4rem);\n  font-weight: var(--font-weight-black);\n  letter-spacing: -0.035em;\n  line-height: 0.98;\n  margin-bottom: var(--space-5);\n  color: var(--text-primary);\n  background: linear-gradient(180deg, #ffffff 0%, #d1d5db 100%);\n  -webkit-background-clip: text;\n  background-clip: text;\n  -webkit-text-fill-color: transparent;\n  max-width: 720px;\n  margin-left: auto;\n  margin-right: auto;\n}\n\n.landing-bottom-title em {\n  font-family: var(--font-serif);\n  font-style: italic;\n  font-weight: 400;\n  -webkit-text-fill-color: var(--accent-primary);\n}\n\n.landing-bottom-sub {\n  font-family: var(--font-display);\n  font-size: clamp(1.05rem, 1.3vw, 1.2rem);\n  color: var(--text-muted);\n  max-width: clamp(380px, 28vw, 580px);\n  margin: 0 auto var(--space-10);\n  line-height: 1.55;\n}\n\n.landing-bottom-cta .landing-btn-primary {\n  padding: 1.15rem 2.6rem;\n  font-size: 1.05rem;\n}\n\n/* ---- Footer ---- */\n.landing-footer {\n  position: relative;\n  z-index: 1;\n  border-top: 1px solid var(--border-subtle);\n  padding: var(--space-10) var(--space-6);\n  background: rgba(0,0,0,0.4);\n  backdrop-filter: blur(10px);\n}\n\n.landing-footer-inner {\n  max-width: clamp(770px, 49vw, 1100px);\n  margin: 0 auto;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: var(--space-6);\n  flex-wrap: wrap;\n}\n\n.landing-footer-text {\n  font-family: var(--font-display);\n  font-size: 0.85rem;\n  color: var(--text-subtle);\n  flex: 1;\n  min-width: 280px;\n  font-style: italic;\n}\n\n.landing-footer-origin-link {\n  color: var(--text-muted);\n  text-decoration: none;\n  border-bottom: 1px dotted rgba(148, 163, 184, 0.35);\n  padding-bottom: 1px;\n  transition: color var(--transition-fast), border-color var(--transition-fast);\n}\n\n.landing-footer-origin-link:hover {\n  color: var(--text-primary);\n  border-bottom-color: rgba(148, 163, 184, 0.7);\n}\n\n.landing-footer-links {\n  display: flex;\n  gap: var(--space-4);\n}\n\n.landing-footer-links a {\n  font-size: var(--text-sm);\n  color: var(--text-muted);\n  text-decoration: none;\n  transition: color var(--transition-fast);\n}\n\n.landing-footer-links a:hover {\n  color: var(--text-primary);\n}\n\n/* ============================================\n   RESPONSIVE\n   ============================================ */\n\n@media (max-width: 768px) {\n  /* Flow: vertical */\n  .landing-flow-grid {\n    flex-direction: column;\n    align-items: center;\n  }\n\n  .landing-flow-card {\n    max-width: 320px;\n    width: 100%;\n  }\n\n  .landing-flow-arrow {\n    transform: rotate(90deg);\n    padding-top: 0;\n  }\n\n  /* Showcases: single column */\n  .landing-showcase {\n    grid-template-columns: 1fr;\n    gap: var(--space-6);\n  }\n\n  .landing-showcase-reverse {\n    direction: ltr;\n  }\n\n  /* Showcase visuals: tighter padding on tablet */\n  .landing-showcase-visual {\n    padding: var(--space-4);\n  }\n\n  /* Wide showcase: full-width on mobile */\n  .landing-showcase-wide-header {\n    flex-direction: column;\n    gap: var(--space-3);\n  }\n\n  .landing-build-meta {\n    flex-direction: column;\n    align-items: flex-start;\n    gap: var(--space-1);\n  }\n\n  .landing-build-cards {\n    justify-content: center;\n  }\n\n  /* Level up: smaller gap */\n  .landing-levelup-grid {\n    gap: var(--space-4);\n  }\n\n  .landing-levelup-item {\n    min-width: 100px;\n  }\n\n  /* Social proof: stack on mobile */\n  .landing-proof-grid {\n    grid-template-columns: 1fr;\n  }\n\n  /* Full-width Live panel: drop to 2x2 so 4 stats stay legible on narrow widths. */\n  .landing-proof-panel-live .landing-proof-stats {\n    grid-template-columns: 1fr 1fr;\n    gap: var(--space-5) var(--space-3);\n    align-items: start;\n  }\n\n  /* Each stat must FILL its grid cell and center its content. Without this the\n     generic `.landing-proof-stat { width: calc(50% - space-2) }` flex-wrap rule\n     (from the <=480 block) constrains every stat to ~75px, so it left-anchors\n     inside the ~165px cell (ragged) and wide values like \"10,602\" overflow the\n     box. Higher specificity here wins over that generic rule at every width.\n     (Edward 2026-06-09 — mobile live-tracker alignment.) */\n  .landing-proof-panel-live .landing-proof-stat {\n    width: 100%;\n    align-items: center;\n    text-align: center;\n  }\n\n  .landing-proof-panel {\n    padding: var(--space-4);\n  }\n\n  .landing-proof-stats {\n    gap: var(--space-3);\n  }\n\n  /* Pricing: 2-col on tablet, 1-col handled below */\n  .landing-pricing-grid {\n    grid-template-columns: repeat(2, 1fr);\n    gap: var(--space-3);\n  }\n\n  .landing-pricing-dlc-row {\n    flex-direction: row;\n    gap: var(--space-3);\n  }\n\n  /* Footer */\n  .landing-footer-inner {\n    flex-direction: column;\n    gap: var(--space-4);\n    text-align: center;\n  }\n\n  /* Programs: smaller */\n  .landing-program-tag {\n    padding: 0.25rem 0.6rem;\n    font-size: 0.6rem;\n  }\n\n  /* Feature section lede */\n  .landing-features-lede {\n    max-width: 42ch;\n  }\n\n  /* PvP mock: tighter gap */\n  .landing-mock-pvp {\n    gap: var(--space-3);\n  }\n}\n\n@media (max-width: 480px) {\n  .landing-hero {\n    /* Longhands, NOT the padding shorthand. As a shorthand this rule is a later\n       declaration of equal specificity, so it replaced the fitted vertical\n       padding outright and pinned it at a flat 64px — silently un-solving the\n       fit for this whole band (measured: padding-top 64px at 480x800 where the\n       geometry had asked for 42.9px, i.e. the strip landing 21px lower than\n       solved, more than the entire clearance budget).\n\n       The phone's roomier 64px is kept and made to yield, the same shape as the\n       desktop rule: identical to what was authored here at fit 1, smaller only\n       when the screen cannot pay for it. */\n    padding-left: var(--space-4);\n    padding-right: var(--space-4);\n    padding-top: min(var(--space-16), calc(64 * var(--hero-u)));\n    padding-bottom: calc(64 * var(--hero-u));\n  }\n\n  .landing-hero-name {\n    font-size: 3.5rem;\n    letter-spacing: 0.2em;\n    margin-right: -0.2em;\n  }\n\n  .landing-hero-sub {\n    font-size: var(--text-base);\n  }\n\n  /* Section inner: tighter horizontal padding on phones */\n  .landing-section-inner {\n    padding: 0 var(--space-4);\n  }\n\n  /* Section label: less bottom margin */\n  .landing-section-label {\n    margin-bottom: var(--space-8);\n  }\n\n  /* Flow: full width */\n  .landing-flow-card {\n    max-width: 100%;\n  }\n\n  /* Showcase text: tighter spacing */\n  .landing-showcase-text h3 {\n    font-size: var(--text-lg);\n  }\n\n  .landing-showcase-text p {\n    font-size: var(--text-sm);\n  }\n\n  /* Showcase visuals: minimal padding on phones */\n  .landing-showcase-visual {\n    padding: var(--space-3);\n  }\n\n  /* Concept table: prevent overflow */\n  .landing-mock-concept-table {\n    font-size: var(--text-xs);\n  }\n\n  .landing-mock-concept-table th,\n  .landing-mock-concept-table td {\n    padding: var(--space-1) var(--space-2);\n  }\n\n  /* Quiz mock: tighter */\n  .landing-mock-quiz-q {\n    font-size: var(--text-sm);\n  }\n\n  .landing-mock-quiz-opt {\n    font-size: var(--text-xs);\n    padding: var(--space-2);\n    gap: var(--space-2);\n  }\n\n  /* PvP mock: stack vertically on very small screens */\n  .landing-mock-pvp {\n    flex-direction: column;\n    gap: var(--space-2);\n  }\n\n  .landing-mock-pvp-vs {\n    align-self: center;\n  }\n\n  /* Wide showcase header text */\n  .landing-showcase-wide-header h3 {\n    font-size: var(--text-lg);\n  }\n\n  .landing-showcase-wide-header p {\n    font-size: var(--text-sm);\n  }\n\n  /* Build cards (pill cards) need nothing here any more. The card's own width\n     is PillCard.css:11 (172px) and stays there: this file no longer reaches\n     into a mounted component's class to resize it, and the base rule's\n     var(--space-2) gap is already what this breakpoint used to re-declare. */\n\n  /* Build meta: stack synergy label */\n  .landing-build-label {\n    font-size: 0.55rem;\n  }\n\n  /* Mock upkeep cards: tighter */\n  .landing-mock-upkeep-name {\n    font-size: var(--text-xs);\n  }\n\n  .landing-mock-upkeep-status {\n    font-size: 0.6rem;\n  }\n\n  /* Level up: 2-col grid */\n  .landing-levelup-grid {\n    display: grid;\n    grid-template-columns: repeat(2, 1fr);\n    gap: var(--space-4);\n  }\n\n  /* Items are max-width-capped (~112px) but grid cells are 1fr (~half-viewport).\n     Without justify-self the items default-anchor to cell start, bunching all\n     four 2x2 cards into the left half of the viewport and leaving the right\n     half empty. Center them. */\n  .landing-levelup-item {\n    justify-self: center;\n  }\n\n  .landing-levelup-item:last-child:nth-child(odd) {\n    grid-column: 1 / -1;\n    justify-self: center;\n  }\n\n  /* Social proof: tighter padding */\n  .landing-proof-panel {\n    padding: var(--space-3);\n  }\n\n  .landing-proof-stats {\n    gap: var(--space-2);\n    flex-wrap: wrap;\n    justify-content: center;\n  }\n\n  .landing-proof-stat {\n    min-width: 0;\n    width: calc(50% - var(--space-2));\n  }\n\n  .landing-proof-label {\n    font-size: 0.6rem;\n    white-space: normal;\n  }\n\n  /* Pricing: single column on small screens */\n  .landing-pricing-grid {\n    grid-template-columns: 1fr;\n    max-width: 320px;\n    margin: 0 auto;\n  }\n\n  .landing-pricing-dlc-row {\n    flex-direction: column;\n    align-items: center;\n    max-width: 320px;\n  }\n\n  .landing-pricing-dlc-row .landing-pricing-card {\n    flex: none;\n    width: 100%;\n    max-width: 320px;\n  }\n\n  .landing-pricing-card {\n    padding: var(--space-5) var(--space-4);\n  }\n\n  /* Programs: tighter */\n  .landing-program-strip {\n    gap: 0.3rem;\n  }\n\n  .landing-program-tag {\n    padding: 0.2rem 0.5rem;\n    font-size: 0.55rem;\n  }\n\n  /* Method tags: smaller */\n  .landing-method-tag {\n    font-size: 0.6rem;\n    padding: 0.25rem 0.55rem;\n    letter-spacing: 0.12em;\n  }\n\n  /* Sentiment text: smaller on mobile */\n  .landing-sentiment {\n    font-size: var(--text-base);\n    padding: var(--space-6) var(--space-4);\n  }\n\n  /* Sections: reduce vertical padding */\n  .landing-features {\n    padding: var(--space-10) 0;\n  }\n\n  .landing-showcase {\n    margin-bottom: var(--space-10);\n  }\n\n  .landing-showcase-wide {\n    margin-bottom: var(--space-10);\n  }\n\n  /* Bottom CTA: tighter */\n  .landing-bottom-title {\n    font-size: clamp(1.25rem, 5vw, 1.75rem);\n  }\n\n  .landing-bottom-sub {\n    font-size: var(--text-base);\n  }\n\n  /* Footer: tighter */\n  .landing-footer {\n    padding: var(--space-6) var(--space-4);\n  }\n\n  .landing-footer-links {\n    gap: var(--space-3);\n  }\n}\n\n/* ============================================\n   ACCESSIBILITY\n   ============================================ */\n\n@media (prefers-reduced-motion: reduce) {\n  .landing-animate {\n    opacity: 1;\n    transform: none;\n    transition: none;\n  }\n\n  /* Skip the intro sequence entirely and land on its finished state. */\n  .landing-hero-logo .dbl-line,\n  .landing-hero-logo .dbl-sweep {\n    stroke-dashoffset: 0;\n    animation: none;\n  }\n\n  .landing-hero-logo .dbl-flood {\n    opacity: 1;\n    animation: none;\n  }\n\n  .landing-hero-script .dbs-ink {\n    stroke-dashoffset: 0;\n    animation: none;\n  }\n\n  .landing-hero-script .dbs-nib {\n    animation: none;\n    opacity: 0;\n  }\n\n\n\n  .landing-glow {\n    animation: none;\n  }\n\n  .landing-btn-primary:hover,\n  .landing-flow-card:hover {\n    transform: none;\n  }\n}\n\n/* ============================================\n   FOUNDERS DISCOUNT (revertible — see config/foundersDiscount.js)\n   ============================================ */\n\n.landing-pricing-card { position: relative; }\n\n.landing-founders-flag {\n  display: inline-flex;\n  align-items: center;\n  gap: 0.4rem;\n  margin: 0 0 0.6rem 0;\n  padding: 0.28rem 0.65rem 0.28rem 0.5rem;\n  border-radius: 999px;\n  font-size: 0.65rem;\n  font-weight: 700;\n  letter-spacing: 0.05em;\n  text-transform: uppercase;\n  border: 1px solid;\n  font-family: var(--font-primary, 'Inter', system-ui, sans-serif);\n  white-space: nowrap;\n  align-self: flex-start;\n}\n\n.landing-founders-flag-dot {\n  width: 5px; height: 5px; border-radius: 50%;\n  background: currentColor;\n  display: inline-block;\n  box-shadow: 0 0 6px currentColor;\n  animation: landing-founders-pulse 2.4s ease-in-out infinite;\n}\n\n@keyframes landing-founders-pulse {\n  0%, 100% { opacity: 0.55; transform: scale(0.85); }\n  50%      { opacity: 1;    transform: scale(1.1); }\n}\n\n.landing-founders-flag-naplex {\n  background: rgba(var(--dlc-naplex-rgb), 0.12);\n  border-color: rgba(var(--dlc-naplex-rgb), 0.4);\n  color: #4ade80;\n}\n\n.landing-founders-flag-mpje {\n  background: rgba(var(--dlc-mpje-rgb), 0.12);\n  border-color: rgba(var(--dlc-mpje-rgb), 0.4);\n  color: var(--dlc-mpje-hover);\n}\n\n.landing-pricing-price-was {\n  display: inline-block;\n  font-size: 0.55em;\n  font-weight: 600;\n  color: rgba(255, 255, 255, 0.45);\n  text-decoration: line-through;\n  text-decoration-color: rgba(239, 68, 68, 0.6);\n  text-decoration-thickness: 1.5px;\n  margin-right: 0.4rem;\n  vertical-align: 0.55em;\n  letter-spacing: -0.01em;\n}\n\n/* Deleted-account confirmation banner — shown when ?deleted=1 is in the URL */\n.landing-deleted-banner {\n  position: fixed;\n  top: 24px;\n  left: 50%;\n  transform: translateX(-50%);\n  z-index: 100;\n  display: flex;\n  align-items: center;\n  gap: 0.75rem;\n  padding: 0.7rem 0.85rem 0.7rem 1rem;\n  border-radius: 999px;\n  background: rgba(74, 222, 128, 0.12);\n  border: 1px solid rgba(74, 222, 128, 0.32);\n  backdrop-filter: blur(10px);\n  -webkit-backdrop-filter: blur(10px);\n  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4), 0 0 24px rgba(74, 222, 128, 0.18);\n  color: #d1fae5;\n  font-size: 13px;\n  font-weight: 500;\n  animation: landing-deleted-banner-in 240ms cubic-bezier(0.16, 1, 0.3, 1);\n}\n\n@keyframes landing-deleted-banner-in {\n  from { opacity: 0; transform: translate(-50%, -16px); }\n  to   { opacity: 1; transform: translate(-50%, 0); }\n}\n\n.landing-deleted-banner-icon {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 22px;\n  height: 22px;\n  border-radius: 50%;\n  background: rgba(74, 222, 128, 0.22);\n  color: #4ade80;\n  flex-shrink: 0;\n}\n\n.landing-deleted-banner-text {\n  white-space: nowrap;\n  letter-spacing: 0.01em;\n}\n\n.landing-deleted-banner-close {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: 22px;\n  height: 22px;\n  border-radius: 50%;\n  background: rgba(255, 255, 255, 0.06);\n  border: 1px solid rgba(255, 255, 255, 0.08);\n  color: rgba(255, 255, 255, 0.65);\n  cursor: pointer;\n  transition: background 150ms ease, color 150ms ease;\n  margin-left: 0.25rem;\n}\n\n.landing-deleted-banner-close:hover {\n  background: rgba(255, 255, 255, 0.12);\n  color: rgba(255, 255, 255, 0.9);\n}\n\n@media (max-width: 640px) {\n  .landing-deleted-banner-text {\n    white-space: normal;\n    max-width: 220px;\n    font-size: 12px;\n    line-height: 1.35;\n  }\n}\n\n/* ============ Playable warfarin Drug-Lab — no-signup combat preview ============ */\n.landing-demo-section {\n  position: relative;\n  min-height: 100vh;\n  width: 100%;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  gap: 14px;\n  padding: 48px 0;\n}\n/* Sits directly above the stage label; the [program] word is swapped by the hero\n   program picker, so the line is what re-frames the demo per audience. */\n.landing-druglab-stage-title {\n  margin: 0;\n  font-family: var(--font-display);\n  font-size: clamp(22px, 2.6vw, 34px);\n  font-weight: var(--font-weight-bold);\n  letter-spacing: -0.01em;\n  line-height: 1.15;\n  color: var(--text-primary);\n  text-align: center;\n}\n.landing-druglab-stage-label {\n  margin: 0;\n  font-size: 11px;\n  font-weight: 700;\n  letter-spacing: 0.18em;\n  text-transform: uppercase;\n  color: rgba(239, 68, 68, 0.85);\n  text-align: center;\n}\n/* EXACT logged-in map size: the dashboard map viewport computes to\n   (100vw − 280px sidebar) × (100vh − 196px chrome). The preview drops the\n   sidebar/bars but keeps the map at the same real size, centered. The element\n   ALSO carries `.deck-map-area` so BossMapModal's portal target lands here and\n   combat renders confined to the stage (not the <body> fallback). The compound\n   selector re-asserts the frame's own visual treatment so the dashboard\n   .deck-map-area base chrome can't leak in regardless of load order. */\n.landing-druglab-stage-frame,\n.landing-druglab-stage-frame.deck-map-area {\n  position: relative;\n  display: block;\n  width: calc(100vw - 280px);\n  height: calc(100vh - 196px);\n  min-height: 460px;\n  margin: 0 auto;\n  padding: 0;\n  border: 1px solid rgba(255, 255, 255, 0.08);\n  border-radius: 16px;\n  background:\n    radial-gradient(120% 90% at 50% 0%, rgba(239, 68, 68, 0.06), transparent 60%),\n    rgba(255, 255, 255, 0.02);\n  box-shadow: 0 30px 80px -30px rgba(0, 0, 0, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.04);\n  overflow: hidden;\n  overflow-clip-margin: 0;\n}\n.landing-demo-cta {\n  display: flex;\n  gap: 12px;\n  justify-content: center;\n  align-items: center;\n}\n@media (max-width: 768px) {\n  .landing-druglab-stage-frame,\n  .landing-druglab-stage-frame.deck-map-area {\n    width: calc(100vw - 32px);\n    height: calc(100vh - 160px);\n    min-height: 420px;\n  }\n}\n\n/* ===== Mobile corner-icon treatment (Edward 2026-06-12) =====\n   On phones, the centered icon tiles that sit ABOVE card headings burn a full\n   row of vertical space per card. At <=768px every such icon docks to the\n   card's top-right corner and the text reclaims the column. Desktop keeps the\n   centered tiles. Appended at end of file so these override the earlier base\n   and media rules by order. */\n@media (max-width: 768px) {\n  /* -- 3-step flow cards: icon mirrors the step-num chip (top-left) in the\n        top-right corner; the AUTO badge drops below the docked icon so the\n        right edge reads as one corner cluster. 56px top padding clears the\n        docked 40px icon; the heading no longer waits below a centered icon\n        row (text used to start ~116px down: 48 pad + 48 icon + 20 margin). */\n  .landing-flow-card {\n    padding-top: 56px;\n  }\n\n  .landing-flow-icon {\n    position: absolute;\n    top: 12px;\n    right: 12px;\n    width: 40px;\n    height: 40px;\n    margin: 0;\n  }\n\n  .landing-flow-icon::after {\n    inset: -4px;\n    border-radius: 14px;\n  }\n\n  .landing-flow-auto-badge {\n    top: 64px;\n    right: 14px;\n  }\n\n  /* -- Level-up grid items: icon floats into the top-right corner; the label\n        shares its row and the description wraps beneath it. Block flow (not\n        flex) so the float shortens line boxes naturally. */\n  .landing-levelup-item {\n    display: block;\n    text-align: left;\n  }\n\n  .landing-levelup-icon {\n    float: right;\n    width: 38px;\n    height: 38px;\n    margin: 0 0 var(--space-2) var(--space-3);\n  }\n\n  .landing-levelup-icon::after {\n    inset: -4px;\n    border-radius: 13px;\n  }\n\n  .landing-levelup-icon svg {\n    width: 17px;\n    height: 17px;\n  }\n\n  .landing-levelup-label {\n    display: flex;\n    align-items: center;\n    min-height: 38px;\n    margin-bottom: var(--space-2);\n  }\n\n  .landing-levelup-desc {\n    display: block;\n  }\n\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
